// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Permission keys for RBAC enforcement.
 * See: docs/security/permission-matrix.md
 *
 * Sprint 1.0.0 + 1.1.0 + 1.2.0 permissions.
 */
export const Permission = {
    // Case Operations (Group B)
    CASE_CREATE: 'case:create',
    CASE_READ: 'case:read',
    CASE_UPDATE: 'case:update',
    CASE_DELETE: 'case:delete',
    CASE_ASSIGN: 'case:assign',
    CASE_REASSIGN: 'case:reassign',
    CASE_COMMENT: 'case:comment',
    CASE_ATTACH: 'case:attach',
    CASE_ATTACH_DOWNLOAD: 'case:attach:download',
    // Task Operations
    TASK_CREATE: 'task:create',
    TASK_READ: 'task:read',
    TASK_UPDATE: 'task:update',
    TASK_COMPLETE: 'task:complete',
    // Template Operations
    TEMPLATE_READ: 'template:read',
    TEMPLATE_WRITE: 'template:write',
    // Rights Requests Specific (Group C)
    DSR_INTAKE_CONFIGURE: 'dsr:intake:configure',
    DSR_IDENTITY_VERIFY: 'dsr:identity:verify',
    DSR_RESPONSE_DRAFT: 'dsr:response:draft',
    DSR_RESPONSE_SEND: 'dsr:response:send',
    DSR_SLA_CONFIGURE: 'dsr:sla:configure',
    DSR_SLA_OVERRIDE: 'dsr:sla:override',
    // SLA Config (Sprint 1.1.0 - Epic A)
    SLA_CONFIG_READ: 'sla:config:read',
    SLA_CONFIG_WRITE: 'sla:config:write',
    DSR_DATA_COLLECTION_REQUEST: 'dsr:data_collection:request',
    DSR_DELETION_REQUEST: 'dsr:deletion:request',
    DSR_DELETION_CONFIRM: 'dsr:deletion:confirm',
    // Sprint 1.1.0 additions
    CASE_ATTACH_DELETE: 'case:attach:delete',
    CASE_CHECKLIST_UPDATE: 'case:checklist:update',
    CASE_REFERENCE_ADD: 'case:reference:add',
    CASE_CLOSURE_SUBMIT: 'case:closure:submit',
    CASE_CLOSURE_APPROVE: 'case:closure:approve',
    CASE_EXPORT_PDF: 'case:export:pdf',
    CASE_EXPORT_ZIP: 'case:export:zip',
    // Audit (Group I)
    AUDIT_EVENTS_READ: 'audit:events:read',
    AUDIT_EVENTS_SEARCH: 'audit:events:search',
    AUDIT_EVENTS_EXPORT: 'audit:events:export',
    AUDIT_DASHBOARD_READ: 'audit:dashboard:read',
    /**
     * Sprint 63.25.0 (OQ-2, spec F2) — record a reviewer's decision (verified or
     * returned) on one version of an audit evidence pack. Not the export grant:
     * the person who assembles a pack is not the person who verifies it, and the
     * service refuses a reviewer who assembled that version
     * (`REVIEWER_IS_ASSEMBLER`). The two T1 roles hold it by derivation; the
     * auditor is given it explicitly (work file §3.6) because no suffix rule
     * reaches `:review`.
     */
    AUDIT_EVIDENCE_PACKS_REVIEW: 'audit:evidence_packs:review',
    // Platform (Group A)
    PLATFORM_SETTINGS_READ: 'platform:settings:read',
    PLATFORM_SETTINGS_WRITE: 'platform:settings:write',
    PLATFORM_SECURITY_READ: 'platform:security:read',
    PLATFORM_SECURITY_WRITE: 'platform:security:write',
    PLATFORM_USERS_READ: 'platform:users:read',
    /**
     * Sprint 63.14.0 (owner determination OD12) — resolve a user id to a name.
     *
     * Gates `GET /users/directory` alone, which returns `{_id, name, email}` for
     * the caller's own organisation and nothing else. It exists because a label
     * map on an ordinary register page used to need `PLATFORM_USERS_READ` — a
     * read of the whole user document — to show who a record is assigned to.
     * Granted to every INTERNAL system role, as its own permission so that a
     * custom role can withhold it. The two external seeded roles
     * (`partner_external_counsel`, `processor_liaison`) are withheld it by owner
     * determination OD12c: the directory lists every member of the organisation,
     * and a baseline grant cannot be withdrawn from a system role afterwards. The route also accepts `PLATFORM_USERS_READ`, whose
     * holders can already read a strict superset of these fields.
     */
    PLATFORM_USERS_LOOKUP: 'platform:users:lookup',
    PLATFORM_USERS_WRITE: 'platform:users:write',
    PLATFORM_USERS_ROLES_ASSIGN: 'platform:users:roles:assign',
    PLATFORM_SERVICE_ACCOUNTS_READ: 'platform:service_accounts:read',
    PLATFORM_SERVICE_ACCOUNTS_WRITE: 'platform:service_accounts:write',
    PLATFORM_INTEGRATIONS_READ: 'platform:integrations:read',
    PLATFORM_INTEGRATIONS_WRITE: 'platform:integrations:write',
    PLATFORM_JOBS_READ: 'platform:jobs:read',
    PLATFORM_JOBS_MANAGE: 'platform:jobs:manage',
    // Sprint 3.1.0: MFA & SSO
    /*
     * ## Sprint 63.7.0 — what these two no longer gate, and why
     *
     * `platform:mfa:read` and `platform:mfa:write` used to gate the six
     * self-scoped MFA routes: enrol, confirm, verify, status, disable and
     * regenerate backup codes. Every one of them acts on `req.session.user_id`
     * and no request field can move that subject.
     *
     * They were measured against the seeded roles. `allPermissions` — which is
     * the whole permission set of exactly two of the seven seeded roles,
     * `platform_admin` and `compliance_owner` — is the only place they appear.
     * `case_manager`, `approver`, `partner_external_counsel` and
     * `processor_liaison` hold neither; `auditor` reaches `:read` through
     * `reachesAuditorBySuffix` and not `:write`. **So five of seven seeded roles
     * received 403 on `POST /api/v1/mfa/enroll`.**
     *
     * That was survivable while MFA was purely opt-in — a user who cannot enrol
     * simply has no second factor. It stops being survivable the moment an
     * organisation can REQUIRE one: `all_required` on those gates would have put
     * every case manager, auditor, approver, external counsel and processor
     * liaison into a state they could not leave, on a tenant that thought it was
     * strengthening its own sign-in.
     *
     * There is no authorization question in "may I strengthen my own sign-in",
     * so the six self routes now carry no `requirePermission` at all. They remain
     * behind `authenticate` and `enforceOrgScope`, and they remain self-scoped.
     *
     * The two constants are kept, not deleted: they are still seeded, still in
     * the published permission matrix, and deleting a permission a tenant's
     * custom role may already hold is a compatibility decision this sprint does
     * not take. What they gate now is the frontend's read of whether to show the
     * MFA card, which is an R-34 hide-not-disable affordance rather than a
     * security boundary.
     */
    PLATFORM_MFA_READ: 'platform:mfa:read',
    PLATFORM_MFA_WRITE: 'platform:mfa:write',
    /**
     * Sprint 63.7.0 — acting on ANOTHER user's MFA.
     *
     * Deliberately its own permission rather than a widening of
     * `platform:mfa:write`. The two are different acts: one strengthens the
     * caller's own sign-in and needs no authorization, the other weakens somebody
     * else's and is the only way one person can do that. Keeping them apart is
     * what lets the self routes lose their gate without handing anybody a route
     * to another account.
     *
     * It grants reset, disable and backup-code revocation on a target in the
     * caller's own organisation. It grants **no read of a secret**: no route
     * anywhere returns another user's secret, otpauth URI, QR data URL or backup
     * codes, and that absence is derived over the whole route tree rather than
     * spot-checked.
     */
    PLATFORM_MFA_ADMIN: 'platform:mfa:admin',
    PLATFORM_SSO_READ: 'platform:sso:read',
    PLATFORM_SSO_WRITE: 'platform:sso:write',
    // Sprint 3.2.0: API Keys, Delegations, Break-Glass
    PLATFORM_API_KEYS_READ: 'platform:api-keys:read',
    PLATFORM_API_KEYS_WRITE: 'platform:api-keys:write',
    PLATFORM_DELEGATIONS_READ: 'platform:delegations:read',
    PLATFORM_DELEGATIONS_WRITE: 'platform:delegations:write',
    PLATFORM_BREAKGLASS_REQUEST: 'platform:breakglass:request',
    PLATFORM_BREAKGLASS_APPROVE: 'platform:breakglass:approve',
    // Sprint 3.3.0: Access Review, Attestation, SoD
    PLATFORM_ACCESS_REVIEW_READ: 'platform:access-review:read',
    PLATFORM_ACCESS_REVIEW_WRITE: 'platform:access-review:write',
    PLATFORM_ACCESS_REVIEW_DECIDE: 'platform:access-review:decide',
    PLATFORM_ATTESTATION_READ: 'platform:attestation:read',
    PLATFORM_ATTESTATION_WRITE: 'platform:attestation:write',
    PLATFORM_SOD_READ: 'platform:sod:read',
    PLATFORM_SOD_WRITE: 'platform:sod:write',
    // Sprint 41.4.0 F1 — Platform → Files Configuration & Browser
    // 10 new permissions backing the FilesConfigPage, FilesBrowserPage,
    // PlatformFileDetailModal, FilesDashboardPage. See sprint-41.4.0.md
    // §"Epic F1 — Permissions" for the role-tier mapping rationale.
    PLATFORM_FILES_CONFIG_READ: 'platform:files:config:read',
    PLATFORM_FILES_CONFIG_WRITE: 'platform:files:config:write',
    PLATFORM_FILES_BROWSE: 'platform:files:browse',
    PLATFORM_FILES_DETAIL_READ: 'platform:files:detail:read',
    PLATFORM_FILES_SOFT_DELETE: 'platform:files:soft_delete',
    PLATFORM_FILES_RESTORE: 'platform:files:restore',
    PLATFORM_FILES_HARD_DELETE: 'platform:files:hard_delete',
    PLATFORM_FILES_AUDIT_READ: 'platform:files:audit:read',
    PLATFORM_FILES_METRICS_READ: 'platform:files:metrics:read',
    PLATFORM_FILES_LEGAL_HOLD_OVERRIDE: 'platform:files:legal_hold:override',
    // Data Mapping (Sprint 1.2.0)
    DM_SYSTEM_READ: 'dm:system:read',
    DM_SYSTEM_WRITE: 'dm:system:write',
    DM_SYSTEM_ARCHIVE: 'dm:system:archive',
    DM_CATEGORY_READ: 'dm:category:read',
    DM_CATEGORY_WRITE: 'dm:category:write',
    DM_PURPOSE_READ: 'dm:purpose:read',
    DM_PURPOSE_WRITE: 'dm:purpose:write',
    DM_RETENTION_READ: 'dm:retention:read',
    DM_RETENTION_WRITE: 'dm:retention:write',
    DM_FLOW_READ: 'dm:flow:read',
    DM_FLOW_WRITE: 'dm:flow:write',
    DM_FLOW_ARCHIVE: 'dm:flow:archive',
    DM_ACTIVITY_READ: 'dm:activity:read',
    DM_ACTIVITY_WRITE: 'dm:activity:write',
    DM_ACTIVITY_ARCHIVE: 'dm:activity:archive',
    DM_ROPA_READ: 'dm:ropa:read',
    DM_ROPA_GENERATE: 'dm:ropa:generate',
    DM_EVIDENCE_READ: 'dm:evidence:read',
    DM_EVIDENCE_WRITE: 'dm:evidence:write',
    DM_EVIDENCE_DELETE: 'dm:evidence:delete',
    DM_EVIDENCE_LINK: 'dm:evidence:link',
    DM_EVIDENCE_UNLINK: 'dm:evidence:unlink',
    DM_EXPORT_CSV: 'dm:export:csv',
    DM_EXPORT_PDF: 'dm:export:pdf',
    DM_AUDIT_READ: 'dm:audit:read',
    // Sprint 45.4.0 — Data Registry (Light-tier sub-module of Data Mapping)
    DM_REGISTRY_READ: 'dm:registry:read',
    DM_REGISTRY_WRITE: 'dm:registry:write',
    DM_REGISTRY_ARCHIVE: 'dm:registry:archive',
    DM_REGISTRY_EXPORT: 'dm:registry:export',
    DM_REGISTRY_IMPORT_FORM: 'dm:registry:import_form',
    // Sprint 44.3.1 Epic C — DM_DEFAULTS_MANAGE deleted (dead perm; no
    // consumers in code). Migration script at 44.3.1-perm-cleanup removes
    // 'dm:defaults:manage' from any persisted custom-role permissions[].
    // Data Mapping Attestation (Sprint 20.5.0)
    DM_ATTESTATION_READ: 'dm:attestation:read',
    DM_ATTESTATION_WRITE: 'dm:attestation:write',
    DM_OWNERSHIP_READ: 'dm:ownership:read',
    DM_OWNERSHIP_WRITE: 'dm:ownership:write',
    DM_RISK_ASSESSMENT_READ: 'dm:risk-assessment:read',
    DM_RISK_ASSESSMENT_WRITE: 'dm:risk-assessment:write',
    DM_RISK_ASSESSMENT_APPROVE: 'dm:risk-assessment:approve',
    DM_ATTESTATION_EXPORT: 'dm:attestation:export',
    // Data Mapping Discovery Workflow (Sprint 27.8.1)
    DM_CATEGORY_APPROVE: 'dm:category:approve',
    DM_REVIEW_READ: 'dm:review:read',
    DM_REVIEW_WRITE: 'dm:review:write',
    DM_INVENTORY_READ: 'dm:inventory:read',
    // Gate E12.5 pass 1 (fix sprint #456) — `dm:exception:read` / `dm:exception:write` were RETIRED
    // here. No route ever checked them: the classification-exception routes check
    // `classification_exception:read` / `:write`, so a role holding only the `dm:` pair saw the
    // Exceptions page and then got a 403 on its data. The page now gates on the route permission.
    // `migrations/fix-456-dm-exception-permission-retire.ts` strips the two codes from stored roles
    // (role-service rejects an unknown code, so a stale one would make the role unsaveable).
    DM_HISTORY_READ: 'dm:history:read',
    // Governance (Sprint 1.3.0)
    GOV_VENDOR_READ: 'gov:vendor:read',
    GOV_VENDOR_WRITE: 'gov:vendor:write',
    GOV_VENDOR_APPROVE: 'gov:vendor:approve',
    GOV_VENDOR_OFFBOARD: 'gov:vendor:offboard',
    GOV_SUBPROCESSOR_READ: 'gov:subprocessor:read',
    GOV_SUBPROCESSOR_WRITE: 'gov:subprocessor:write',
    GOV_TRANSFER_READ: 'gov:transfer:read',
    GOV_TRANSFER_WRITE: 'gov:transfer:write',
    GOV_CROSSBORDER_READ: 'gov:crossborder:read',
    GOV_CROSSBORDER_EXPORT: 'gov:crossborder:export',
    // Sprint 41.5.0 G1 — Cross-Border Filing Lifecycle. Each permission
    // gates a distinct verb on grc_cross_border_filings so the DPO,
    // Cross-Border Lead, Internal Auditor, and CEO personas all map to
    // distinct ACL slices per UC-VN-07.
    GOV_CROSS_BORDER_FILING_READ: 'gov:crossborder:filing:read',
    GOV_CROSS_BORDER_FILING_WRITE: 'gov:crossborder:filing:write',
    GOV_CROSS_BORDER_FILING_SUBMIT: 'gov:crossborder:filing:submit',
    GOV_CROSS_BORDER_FILING_ARCHIVE: 'gov:crossborder:filing:archive',
    GOV_DRIFT_READ: 'gov:drift:read',
    GOV_DRIFT_MANAGE: 'gov:drift:manage',
    GOV_DRIFT_ACCEPT_RISK: 'gov:drift:accept_risk',
    GOV_SCORE_READ: 'gov:score:read',
    GOV_SCORE_WRITE: 'gov:score:write',
    GOV_PLAYBOOK_READ: 'gov:playbook:read',
    GOV_PLAYBOOK_WRITE: 'gov:playbook:write',
    GOV_PLAYBOOK_DELETE: 'gov:playbook:delete',
    GOV_EXECUTION_READ: 'gov:execution:read',
    GOV_EXECUTION_INITIATE: 'gov:execution:initiate',
    GOV_EXECUTION_STEP: 'gov:execution:step',
    GOV_APPROVAL_READ: 'gov:approval:read',
    GOV_APPROVAL_DECIDE: 'gov:approval:decide',
    GOV_EVIDENCE_READ: 'gov:evidence:read',
    GOV_EVIDENCE_WRITE: 'gov:evidence:write',
    GOV_EVIDENCE_DELETE: 'gov:evidence:delete',
    GOV_EVIDENCE_LINK: 'gov:evidence:link',
    GOV_AUDIT_READ: 'gov:audit:read',
    GOV_EXPORT_CSV: 'gov:export:csv',
    GOV_EXPORT_PDF: 'gov:export:pdf',
    GOV_EXPORT_EXECUTE: 'gov:export:execute',
    // Vendor Governance Maturity (Sprint 20.9.0)
    GOV_ONBOARDING_READ: 'gov:onboarding:read',
    GOV_ONBOARDING_WRITE: 'gov:onboarding:write',
    GOV_DPA_REVIEW_READ: 'gov:dpa_review:read',
    GOV_DPA_REVIEW_WRITE: 'gov:dpa_review:write',
    GOV_PERIODIC_REVIEW_READ: 'gov:periodic_review:read',
    GOV_PERIODIC_REVIEW_WRITE: 'gov:periodic_review:write',
    GOV_OFFBOARDING_READ: 'gov:offboarding:read',
    GOV_OFFBOARDING_WRITE: 'gov:offboarding:write',
    // Consent Governance (Sprint 1.4.0)
    CONSENT_POLICY_READ: 'consent_policy:read',
    CONSENT_POLICY_WRITE: 'consent_policy:write',
    CONSENT_POLICY_APPROVE: 'consent_policy:approve',
    CONSENT_POLICY_DELETE: 'consent_policy:delete',
    CONSENT_WORKFLOW_READ: 'consent_workflow:read',
    CONSENT_WORKFLOW_WRITE: 'consent_workflow:write',
    CONSENT_CMP_SNAPSHOT_READ: 'consent_cmp_snapshot:read',
    CONSENT_CMP_SNAPSHOT_WRITE: 'consent_cmp_snapshot:write',
    CONSENT_EXPORT_EXECUTE: 'consent_export:execute',
    // Consent Governance Maturity (Sprint 20.6.0)
    CONSENT_PROPAGATION_READ: 'consent_propagation:read',
    CONSENT_PROPAGATION_WRITE: 'consent_propagation:write',
    CONSENT_RECONCILIATION_READ: 'consent_reconciliation:read',
    CONSENT_RECONCILIATION_WRITE: 'consent_reconciliation:write',
    CONSENT_SYNC_READ: 'consent_sync:read',
    CONSENT_SYNC_WRITE: 'consent_sync:write',
    // Assessments (Sprint 1.5.0)
    DPIA_READ: 'dpia:read',
    DPIA_WRITE: 'dpia:write',
    DPIA_APPROVE: 'dpia:approve',
    DPIA_DELETE: 'dpia:delete',
    /**
     * PDPL evidence-pack authority submit (fix-sprint-20260619-0053-followup-2 #10).
     * Gates the irreversible /send + authority-response + hard-copy-followup routes
     * that transmit the assembled 8-part ZIP to MoPS A05. Split out from DPIA_WRITE
     * so that drafting, editing, and sent-proof upload (still gated by DPIA_WRITE)
     * can be delegated broadly while the actual transmission remains restricted
     * to org_admin / DPO by default.
     */
    PDPL_EVIDENCE_PACK_SEND: 'pdpl_evidence_pack:send',
    /**
     * Sprint 64.35.0 — the retrieval sibling of `PDPL_EVIDENCE_PACK_SEND`.
     *
     * The seven PDPL filing-pack owner types (the regulator-facing DPIA/OTIA pack
     * ZIPs, manual send proofs, authority responses, hard-copy receipts and both
     * cover-letter variants) are gated on `DPIA_READ` today. That is the exact
     * exposure Sprint 64.0.0 closed for the framework evidence pack and left open
     * for the platform's *other* filing-pack family: a role denied the right to
     * mint a filing-grade archive can still download one.
     *
     * ## This permission ends in `:read` and MUST NOT reach the auditor
     *
     * Those two facts are in direct conflict, and the conflict is the reason this
     * comment is long. `seed-roles.ts`'s `reachesAuditorBySuffix` grants the
     * auditor **every** permission ending `:read` — so simply declaring this one
     * hands it to the population the sprint exists to exclude, silently, and
     * AC1's "an auditor-shaped session receives 403" would fail while every part
     * of the change looked correct.
     *
     * The name is kept (it is the sibling of `:send` and renaming it would make
     * the family incoherent) and `reachesAuditorBySuffix` carries an explicit
     * exception instead, joined to this constant rather than to a string literal.
     * See that function, and the test that pins the exclusion.
     */
    PDPL_EVIDENCE_PACK_READ: 'pdpl_evidence_pack:read',
    TRANSFER_ASSESSMENT_READ: 'transfer_assessment:read',
    TRANSFER_ASSESSMENT_WRITE: 'transfer_assessment:write',
    TRANSFER_ASSESSMENT_APPROVE: 'transfer_assessment:approve',
    TRANSFER_ASSESSMENT_DELETE: 'transfer_assessment:delete',
    // Sprint 44.3.1 Epic C — ASSESSMENT_EXPORT_EXECUTE deleted (dead perm;
    // no consumers). Migration removes 'assessment_export:execute' from
    // custom roles.
    // Sprint 40.1.0: Assessment Trigger Configuration
    ASSESSMENT_TRIGGER_CONFIGURE: 'assessment:trigger:configure',
    // Classification & Lifecycle (Sprint 1.6.0)
    TAXONOMY_READ: 'taxonomy:read',
    TAXONOMY_WRITE: 'taxonomy:write',
    TAXONOMY_DELETE: 'taxonomy:delete',
    CLASSIFICATION_READ: 'classification:read',
    CLASSIFICATION_WRITE: 'classification:write',
    CLASSIFICATION_REVIEW: 'classification:review',
    LIFECYCLE_READ: 'lifecycle:read',
    LIFECYCLE_WRITE: 'lifecycle:write',
    LIFECYCLE_HOLD: 'lifecycle:hold',
    GAP_REPORT_READ: 'gap_report:read',
    GAP_REPORT_MANAGE: 'gap_report:manage',
    CLASSIFICATION_EXPORT_EXECUTE: 'classification_export:execute',
    // Monitoring & Evidence Program (Sprint 1.7.0)
    PROGRAM_READ: 'program:read',
    PROGRAM_WRITE: 'program:write',
    PROGRAM_MANAGE: 'program:manage',
    REVIEW_TASK_READ: 'review_task:read',
    REVIEW_TASK_WRITE: 'review_task:write',
    REVIEW_TASK_ASSIGN: 'review_task:assign',
    TASK_TEMPLATE_READ: 'task_template:read',
    TASK_TEMPLATE_WRITE: 'task_template:write',
    KPI_READ: 'kpi:read',
    KPI_WRITE: 'kpi:write',
    RIC_READ: 'ric:read',
    RIC_WRITE: 'ric:write',
    // Sprint 44.3.1 Epic C — PROGRAM_EXPORT_EXECUTE deleted (dead perm;
    // superseded by monitoring_export:execute). Migration removes
    // 'program_export:execute' from custom roles.
    // Monitoring Programs Maturity (Sprint 21.3.0)
    MONITORING_FRAMEWORK_LINK_READ: 'monitoring_framework_link:read',
    MONITORING_FRAMEWORK_LINK_WRITE: 'monitoring_framework_link:write',
    MONITORING_AUTHORITY_READINESS_READ: 'monitoring_authority_readiness:read',
    MONITORING_AUTHORITY_READINESS_WRITE: 'monitoring_authority_readiness:write',
    MONITORING_CHANGE_REVIEW_READ: 'monitoring_change_review:read',
    MONITORING_CHANGE_REVIEW_WRITE: 'monitoring_change_review:write',
    // Monitoring Operational Completeness (Sprint 27.7.1)
    MONITORING_EXPORT: 'monitoring_export:execute',
    MONITORING_RACI_READ: 'monitoring_raci:read',
    MONITORING_RACI_WRITE: 'monitoring_raci:write',
    // Incident & Breach Response (Sprint 1.8.0)
    INCIDENT_READ: 'incident:read',
    INCIDENT_WRITE: 'incident:write',
    INCIDENT_TRIAGE: 'incident:triage',
    INCIDENT_ASSESS: 'incident:assess',
    INCIDENT_CLOSE: 'incident:close',
    INCIDENT_TASK_READ: 'incident_task:read',
    INCIDENT_TASK_WRITE: 'incident_task:write',
    INCIDENT_TASK_ASSIGN: 'incident_task:assign',
    INCIDENT_DECISION_WRITE: 'incident_decision:write',
    INCIDENT_NOTIFICATION_MANAGE: 'incident_notification:manage',
    INCIDENT_EXPORT_EXECUTE: 'incident_export:execute',
    // Onboarding & Enablement (Sprint 1.9.0)
    ONBOARDING_WIZARD_READ: 'onboarding_wizard:read',
    ONBOARDING_WIZARD_WRITE: 'onboarding_wizard:write',
    ONBOARDING_TOUR_READ: 'onboarding_tour:read',
    ONBOARDING_TOUR_WRITE: 'onboarding_tour:write',
    SAMPLE_DATA_READ: 'sample_data:read',
    SAMPLE_DATA_LOAD: 'sample_data:load',
    SAMPLE_DATA_CLEAR: 'sample_data:clear',
    HELP_ARTICLE_READ: 'help_article:read',
    HELP_ARTICLE_WRITE: 'help_article:write',
    HELP_ARTICLE_DELETE: 'help_article:delete',
    // 44.1.0 — FAQ Q&A editing on Help Center articles. Full admin surface
    // lands in a later phase; permission + stub routes scaffolded here.
    HELP_FAQ_EDIT: 'help_faq:edit',
    // Connectors Foundation (Sprint 2.0.0)
    CONNECTOR_READ: 'connector:read',
    CONNECTOR_WRITE: 'connector:write',
    CONNECTOR_MANAGE: 'connector:manage',
    // Sprint 64.145.0 — split out of `connector:manage`, which gated eight
    // routes. Seven are tenant-scoped operations over `grc_connector_instances`
    // (`{ org_id }` on every filter). The eighth pair — create and update a
    // connector DEFINITION — writes `grc_connector_definitions`, which carries no
    // tenant column and which `listDefinitions` reads with `{}`. So one grant
    // covered "configure my organisation's connectors" and "rewrite the
    // `config_schema` and `capabilities` every other tenant's connectors are
    // built from". This is PLATFORM_ONLY: see PLATFORM_ONLY_PERMISSIONS below.
    CONNECTOR_DEFINITION_WRITE: 'connector_definition:write',
    CONNECTOR_TEST: 'connector:test',
    CREDENTIAL_READ: 'credential:read',
    CREDENTIAL_WRITE: 'credential:write',
    EVENT_BUS_READ: 'event_bus:read',
    EVENT_BUS_MANAGE: 'event_bus:manage',
    IDENTITY_SSO_CONFIGURE: 'identity_sso:configure',
    STORAGE_CONNECTOR_CONFIGURE: 'storage_connector:configure',
    // Vendor Risk Scoring & Procurement Governance (Sprint 2.1.0)
    VENDOR_RISK_CONFIG_READ: 'vendor_risk_config:read',
    VENDOR_RISK_CONFIG_WRITE: 'vendor_risk_config:write',
    VENDOR_RISK_SCORE_READ: 'vendor_risk_score:read',
    VENDOR_RISK_SCORE_WRITE: 'vendor_risk_score:write',
    VENDOR_QUESTIONNAIRE_TEMPLATE_READ: 'vendor_questionnaire_template:read',
    VENDOR_QUESTIONNAIRE_TEMPLATE_WRITE: 'vendor_questionnaire_template:write',
    VENDOR_QUESTIONNAIRE_READ: 'vendor_questionnaire:read',
    VENDOR_QUESTIONNAIRE_WRITE: 'vendor_questionnaire:write',
    VENDOR_QUESTIONNAIRE_REVIEW: 'vendor_questionnaire:review',
    VENDOR_CONTRACT_READ: 'vendor_contract:read',
    VENDOR_CONTRACT_WRITE: 'vendor_contract:write',
    // Sprint 44.3.1 Epic D (P1-04) — added so auditor (and other read-scope
    // roles) can view gate evaluations without carrying the write EVALUATE
    // perm. Previously GateEvaluationsPage gated view on EVALUATE which
    // blocked auditors by design.
    VENDOR_GATE_READ: 'vendor_gate:read',
    VENDOR_GATE_EVALUATE: 'vendor_gate:evaluate',
    VENDOR_GATE_OVERRIDE: 'vendor_gate:override',
    VENDOR_REVIEW_READ: 'vendor_review:read',
    VENDOR_REVIEW_WRITE: 'vendor_review:write',
    VENDOR_RISK_EXPORT_EXECUTE: 'vendor_risk_export:execute',
    // Vendor Risk Maturity (Sprint 21.0.0)
    VENDOR_RISK_SIGNAL_READ: 'vendor_risk_signal:read',
    VENDOR_RISK_SIGNAL_WRITE: 'vendor_risk_signal:write',
    VENDOR_RISK_REASSESSMENT_READ: 'vendor_risk_reassessment:read',
    VENDOR_RISK_REASSESSMENT_WRITE: 'vendor_risk_reassessment:write',
    VENDOR_RISK_ACCEPTANCE_READ: 'vendor_risk_acceptance:read',
    VENDOR_RISK_ACCEPTANCE_WRITE: 'vendor_risk_acceptance:write',
    VENDOR_RISK_PORTFOLIO_READ: 'vendor_risk_portfolio:read',
    VENDOR_RISK_REVIEW_ROUTING_READ: 'vendor_risk_review_routing:read',
    VENDOR_RISK_REVIEW_ROUTING_WRITE: 'vendor_risk_review_routing:write',
    // Vendor Onboarding Lifecycle (Sprint 28.3.1)
    VENDOR_ONBOARDING_CASE_READ: 'vendor_onboarding_case:read',
    VENDOR_ONBOARDING_CASE_WRITE: 'vendor_onboarding_case:write',
    VENDOR_ONBOARDING_CASE_EXPORT: 'vendor_onboarding_case:export',
    VENDOR_ONBOARDING_APPROVAL_READ: 'vendor_onboarding_approval:read',
    VENDOR_ONBOARDING_APPROVAL_WRITE: 'vendor_onboarding_approval:write',
    // Deletion Orchestration (Sprint 2.2.0)
    DELETION_ORCHESTRATION_READ: 'deletion_orchestration:read',
    DELETION_ORCHESTRATION_WRITE: 'deletion_orchestration:write',
    DELETION_ORCHESTRATION_EXECUTE: 'deletion_orchestration:execute',
    DELETION_ORCHESTRATION_VERIFY: 'deletion_orchestration:verify',
    DELETION_ORCHESTRATION_CANCEL: 'deletion_orchestration:cancel',
    DELETION_TEMPLATE_READ: 'deletion_template:read',
    DELETION_TEMPLATE_WRITE: 'deletion_template:write',
    DELETION_VENDOR_REQUEST_READ: 'deletion_vendor_request:read',
    DELETION_VENDOR_REQUEST_WRITE: 'deletion_vendor_request:write',
    DELETION_VENDOR_REQUEST_ESCALATE: 'deletion_vendor_request:escalate',
    DELETION_PROOF_READ: 'deletion_proof:read',
    DELETION_PROOF_CONFIGURE: 'deletion_proof:configure',
    DELETION_REPORT_GENERATE: 'deletion_report:generate',
    // Data Discovery & Classification Automation (Sprint 2.3.0)
    DISCOVERY_READ: 'discovery:read',
    DISCOVERY_WRITE: 'discovery:write',
    DISCOVERY_PROMOTE: 'discovery:promote',
    DISCOVERY_CONFIGURE: 'discovery:configure',
    CLASSIFICATION_SUGGESTION_READ: 'classification_suggestion:read',
    CLASSIFICATION_SUGGESTION_REVIEW: 'classification_suggestion:review',
    CLASSIFICATION_RULE_READ: 'classification_rule:read',
    CLASSIFICATION_RULE_WRITE: 'classification_rule:write',
    DISCOVERY_EXPORT_EXECUTE: 'discovery_export:execute',
    // Classification Maturity (Sprint 20.8.0)
    CLASSIFICATION_REVIEW_READ: 'classification_review:read',
    CLASSIFICATION_REVIEW_WRITE: 'classification_review:write',
    CLASSIFICATION_EXCEPTION_READ: 'classification_exception:read',
    CLASSIFICATION_EXCEPTION_WRITE: 'classification_exception:write',
    CLASSIFICATION_DISCOVERY_READ: 'classification_discovery:read',
    CLASSIFICATION_DISCOVERY_WRITE: 'classification_discovery:write',
    // Incident Operations Level 2 (Sprint 2.4.0)
    //
    // @deprecated — Sprint 44.3.0 B3 — This INCIDENT_* family is the
    // legacy permission namespace. The canonical family is INCIDENT_OPS_*
    // (Sprint 21.2.0, below at lines ~422+). Do NOT add new references
    // to the INCIDENT_* family — consume INCIDENT_OPS_* instead.
    //
    // Migration mapping (clean 1:1):
    //   INCIDENT_PLAYBOOK_READ          -> INCIDENT_OPS_PLAYBOOK_LINK_READ
    //   INCIDENT_PLAYBOOK_WRITE         -> INCIDENT_OPS_PLAYBOOK_LINK_WRITE
    //   INCIDENT_SLA_READ               -> INCIDENT_OPS_REGULATORY_SLA_READ
    //   INCIDENT_SLA_WRITE              -> INCIDENT_OPS_REGULATORY_SLA_WRITE
    //   INCIDENT_REGULATOR_EXPORT_READ  -> INCIDENT_OPS_COORDINATION_READ
    //   INCIDENT_VENDOR_COORD_READ      -> INCIDENT_OPS_COORDINATION_READ
    //   INCIDENT_VENDOR_COORD_WRITE     -> INCIDENT_OPS_COORDINATION_WRITE
    //
    // No INCIDENT_OPS_* equivalent exists yet for:
    //   INCIDENT_PLAYBOOK_APPROVE, INCIDENT_PLAYBOOK_ACTIVATE,
    //   INCIDENT_REGULATOR_EXPORT_EXECUTE, INCIDENT_REGULATOR_EXPORT_SUBMIT,
    //   INCIDENT_METRICS_READ, INCIDENT_METRICS_EXPORT.
    //
    // Full route/page migration deferred to Sprint 44.3.1 (needs new
    // INCIDENT_OPS_*_APPROVE / ACTIVATE / EXECUTE / SUBMIT / METRICS_* perms).
    /** @deprecated Use INCIDENT_OPS_PLAYBOOK_LINK_READ instead. Sprint 44.3.0 B3. */
    INCIDENT_PLAYBOOK_READ: 'incident_playbook:read',
    /** @deprecated Use INCIDENT_OPS_PLAYBOOK_LINK_WRITE instead. Sprint 44.3.0 B3. */
    INCIDENT_PLAYBOOK_WRITE: 'incident_playbook:write',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_APPROVE equivalent. */
    INCIDENT_PLAYBOOK_APPROVE: 'incident_playbook:approve',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_ACTIVATE equivalent. */
    INCIDENT_PLAYBOOK_ACTIVATE: 'incident_playbook:activate',
    /** @deprecated Use INCIDENT_OPS_REGULATORY_SLA_READ instead. Sprint 44.3.0 B3. */
    INCIDENT_SLA_READ: 'incident_sla:read',
    /** @deprecated Use INCIDENT_OPS_REGULATORY_SLA_WRITE instead. Sprint 44.3.0 B3. */
    INCIDENT_SLA_WRITE: 'incident_sla:write',
    /** @deprecated Use INCIDENT_OPS_COORDINATION_READ instead. Sprint 44.3.0 B3. */
    INCIDENT_REGULATOR_EXPORT_READ: 'incident_regulator_export:read',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_EXECUTE equivalent. */
    INCIDENT_REGULATOR_EXPORT_EXECUTE: 'incident_regulator_export:execute',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_SUBMIT equivalent. */
    INCIDENT_REGULATOR_EXPORT_SUBMIT: 'incident_regulator_export:submit',
    /** @deprecated Use INCIDENT_OPS_COORDINATION_READ instead. Sprint 44.3.0 B3. */
    INCIDENT_VENDOR_COORD_READ: 'incident_vendor_coord:read',
    /** @deprecated Use INCIDENT_OPS_COORDINATION_WRITE instead. Sprint 44.3.0 B3. */
    INCIDENT_VENDOR_COORD_WRITE: 'incident_vendor_coord:write',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_METRICS_READ equivalent. */
    INCIDENT_METRICS_READ: 'incident_metrics:read',
    /** @deprecated Sprint 44.3.0 B3 — awaiting INCIDENT_OPS_*_METRICS_EXPORT equivalent. */
    INCIDENT_METRICS_EXPORT: 'incident_metrics:export',
    // Incident Response Maturity (Sprint 21.1.0)
    INCIDENT_BREACH_NOTIFICATION_READ: 'incident_breach_notification:read',
    INCIDENT_BREACH_NOTIFICATION_WRITE: 'incident_breach_notification:write',
    INCIDENT_SUPPLEMENT_READ: 'incident_supplement:read',
    INCIDENT_SUPPLEMENT_WRITE: 'incident_supplement:write',
    INCIDENT_REMEDIATION_READ: 'incident_remediation:read',
    INCIDENT_REMEDIATION_WRITE: 'incident_remediation:write',
    INCIDENT_AUTHORITY_REQUEST_READ: 'incident_authority_request:read',
    INCIDENT_AUTHORITY_REQUEST_WRITE: 'incident_authority_request:write',
    INCIDENT_DISCLOSURE_READ: 'incident_disclosure:read',
    INCIDENT_DISCLOSURE_WRITE: 'incident_disclosure:write',
    // Incident Operations Maturity (Sprint 21.2.0)
    INCIDENT_OPS_PLAYBOOK_LINK_READ: 'incident_ops_playbook_link:read',
    INCIDENT_OPS_PLAYBOOK_LINK_WRITE: 'incident_ops_playbook_link:write',
    INCIDENT_OPS_REGULATORY_SLA_READ: 'incident_ops_regulatory_sla:read',
    INCIDENT_OPS_REGULATORY_SLA_WRITE: 'incident_ops_regulatory_sla:write',
    INCIDENT_OPS_COORDINATION_READ: 'incident_ops_coordination:read',
    INCIDENT_OPS_COORDINATION_WRITE: 'incident_ops_coordination:write',
    INCIDENT_OPS_EVIDENCE_CHAIN_READ: 'incident_ops_evidence_chain:read',
    INCIDENT_OPS_EVIDENCE_CHAIN_WRITE: 'incident_ops_evidence_chain:write',
    INCIDENT_OPS_POST_REVIEW_READ: 'incident_ops_post_review:read',
    INCIDENT_OPS_POST_REVIEW_WRITE: 'incident_ops_post_review:write',
    // Sprint 44.3.1 Epic A — gap-filler perms that unblock the INCIDENT_*
    // legacy family route migration (B3). Each maps to one of the 5
    // legacy perms that had no INCIDENT_OPS_* equivalent.
    INCIDENT_OPS_PLAYBOOK_LINK_APPROVE: 'incident_ops_playbook_link:approve',
    INCIDENT_OPS_PLAYBOOK_LINK_ACTIVATE: 'incident_ops_playbook_link:activate',
    INCIDENT_OPS_COORDINATION_EXECUTE: 'incident_ops_coordination:execute',
    INCIDENT_OPS_COORDINATION_SUBMIT: 'incident_ops_coordination:submit',
    INCIDENT_OPS_METRICS_READ: 'incident_ops_metrics:read',
    INCIDENT_OPS_METRICS_EXPORT: 'incident_ops_metrics:export',
    // Program Governance (Sprint 2.5.0)
    MATURITY_MODEL_READ: 'maturity_model:read',
    MATURITY_MODEL_WRITE: 'maturity_model:write',
    MATURITY_ASSESSMENT_READ: 'maturity_assessment:read',
    MATURITY_ASSESSMENT_WRITE: 'maturity_assessment:write',
    MATURITY_ASSESSMENT_APPROVE: 'maturity_assessment:approve',
    TREND_DASHBOARD_READ: 'trend_dashboard:read',
    // Sprint 43.7.1 Wave 2 — renamed from TREND_DASHBOARD_CONFIGURE. The
    // only consumer left after 43.7.0 removed DashboardConfigPage is the
    // /trends and /trends/batch POST routes, which write trend metrics,
    // not dashboard config. Naming now matches the actual resource.
    TREND_METRIC_WRITE: 'trend_metric:write',
    EXECUTIVE_REPORT_READ: 'executive_report:read',
    EXECUTIVE_REPORT_WRITE: 'executive_report:write',
    EXECUTIVE_REPORT_PUBLISH: 'executive_report:publish',
    AUDIT_READINESS_READ: 'audit_readiness:read',
    AUDIT_READINESS_WRITE: 'audit_readiness:write',
    // Sprint 41.5.0 G5 — Program Governance permission split.
    // The 2026-04-11 module finalization review found that
    // governance-documents, obligation-mappings, raci-assignments,
    // and acknowledgment routes all gated on the blanket
    // MATURITY_MODEL_* permissions. This collapsed the ACL for
    // P-VN-01 (DPO), P-VN-05 (Data Governance Lead), P-VN-10
    // (Internal Audit), P-VN-11 (HR), P-VN-12 (Subsidiary), and
    // P-VN-14 (External Legal) into the same access pattern. The
    // 14 permissions below restore the semantic distinction so
    // each persona can be gated correctly per UC-VN-17 (Governance
    // Model Issuance) and downstream document/acknowledgment flows.
    GOV_DOC_READ: 'gov_doc:read',
    GOV_DOC_WRITE: 'gov_doc:write',
    GOV_DOC_ISSUE: 'gov_doc:issue',
    GOV_DOC_ARCHIVE: 'gov_doc:archive',
    OBLIGATION_MAPPING_READ: 'obligation_mapping:read',
    OBLIGATION_MAPPING_WRITE: 'obligation_mapping:write',
    MATURITY_READ: 'maturity:read',
    MATURITY_WRITE: 'maturity:write',
    MATURITY_ASSESS: 'maturity:assess',
    ACKNOWLEDGMENT_READ: 'acknowledgment:read',
    ACKNOWLEDGMENT_MANAGE: 'acknowledgment:manage',
    CHANGE_LOG_READ: 'change_log:read',
    REVIEW_CADENCE_READ: 'review_cadence:read',
    REVIEW_CADENCE_MANAGE: 'review_cadence:manage',
    GOVERNANCE_EXPORT_EXECUTE: 'governance_export:execute',
    // Access Accountability (Sprint 2.6.0)
    ACCESS_LOG_READ: 'access_accountability:log:read',
    ACCESS_LOG_WRITE: 'access_accountability:log:write',
    ACCESS_LOG_EXPORT: 'access_accountability:log:export',
    /**
     * Sprint 63.29.0 (#483, owner ruling 2026-10-18) — sign off one stage of an
     * access evidence package (`POST /evidence-packages/:id/sign-off`). It was gated
     * on `:log:export`, so any export holder could approve or return any stage; it is
     * a decision, not an export. Held by the auditor so the `internal_audit` stage can
     * still be signed; closing a package stays on `:log:write`.
     */
    ACCESS_EVIDENCE_PACKAGE_SIGN_OFF: 'access_accountability:evidence_package:sign_off',
    // Sprint 63.1.0 — running the access-log retention sweep. Separate from
    // `:config:write` (which sets the window) and from `:log:write` (which adds
    // rows), because this is the only permission in the module that *destroys*
    // material the record exists to preserve. The read-only register that answers
    // what a sweep would do is gated on `:log:read` alone.
    ACCESS_LOG_RETENTION_SWEEP: 'access_accountability:log:retention_sweep',
    ACCESS_JUSTIFICATION_READ: 'access_accountability:justification:read',
    // Creating a justification is a write into the append-only accountability
    // record, so it is separated from the READ permission every dashboard viewer
    // holds — the same line `access_accountability:log:write` draws on the sibling
    // write in the same router. Added in fix-sprint-20260808: POST
    // /justifications was gated on `:read`, and the module declared no write
    // permission at all, so the seeded read-only auditor could file rows into the
    // record it audits.
    ACCESS_JUSTIFICATION_WRITE: 'access_accountability:justification:write',
    ACCESS_JUSTIFICATION_REVIEW: 'access_accountability:justification:review',
    ACCESS_INVESTIGATION_READ: 'access_accountability:investigation:read',
    ACCESS_INVESTIGATION_WRITE: 'access_accountability:investigation:write',
    ACCESS_INVESTIGATION_CLOSE: 'access_accountability:investigation:close',
    ACCESS_INVESTIGATION_EXPORT: 'access_accountability:investigation:export',
    ACCESS_CONFIG_READ: 'access_accountability:config:read',
    ACCESS_CONFIG_WRITE: 'access_accountability:config:write',
    ACCESS_ANCHORING_CONFIGURE: 'access_accountability:anchoring:configure',
    ACCESS_ANCHORING_VERIFY: 'access_accountability:anchoring:verify',
    ACCESS_SESSION_READ: 'access_accountability:session:read',
    ACCESS_SESSION_WRITE: 'access_accountability:session:write',
    ACCESS_SESSION_REVOKE: 'access_accountability:session:revoke',
    // Consent Properties / Multi-Property Governance (Sprint 2.7.0)
    CONSENT_PROPERTY_READ: 'consent_property:read',
    CONSENT_PROPERTY_WRITE: 'consent_property:write',
    CONSENT_PROPERTY_DECOMMISSION: 'consent_property:decommission',
    CONSENT_BINDING_READ: 'consent_binding:read',
    CONSENT_BINDING_WRITE: 'consent_binding:write',
    CONSENT_COMPARISON_READ: 'consent_comparison:read',
    CONSENT_COMPARISON_EXECUTE: 'consent_comparison:execute',
    CONSENT_CHANGE_REQUEST_READ: 'consent_change_request:read',
    CONSENT_CHANGE_REQUEST_WRITE: 'consent_change_request:write',
    CONSENT_CHANGE_REQUEST_APPROVE: 'consent_change_request:approve',
    CONSENT_CHANGE_REQUEST_DEPLOY: 'consent_change_request:deploy',
    CONSENT_SNAPSHOT_SCHEDULE_READ: 'consent_snapshot_schedule:read',
    CONSENT_SNAPSHOT_SCHEDULE_WRITE: 'consent_snapshot_schedule:write',
    CONSENT_GOLDEN_TEMPLATE_READ: 'consent_golden_template:read',
    CONSENT_GOLDEN_TEMPLATE_WRITE: 'consent_golden_template:write',
    CONSENT_CROSS_PROPERTY_EXPORT: 'consent_cross_property_export:execute',
    CONSENT_PROPERTY_ANALYTICS_READ: 'consent_property_analytics:read',
    // Consent Properties Operational Completeness (Sprint 28.2.1)
    CONSENT_SENSITIVE_DATA_REVIEW: 'consent_sensitive_data:review',
    // International Packaging and Localization (Sprint 2.8.0)
    MARKET_PACK_READ: 'market_pack:read',
    MARKET_PACK_WRITE: 'market_pack:write',
    MARKET_PACK_MANAGE: 'market_pack:manage',
    LANGUAGE_PACK_READ: 'language_pack:read',
    LANGUAGE_PACK_WRITE: 'language_pack:write',
    LANGUAGE_PACK_MANAGE: 'language_pack:manage',
    // Help-Content Pack Management (Sprint 45.8.0)
    HELP_CONTENT_PACK_MANAGE: 'help_content_pack:manage',
    REGULATORY_FRAMEWORK_READ: 'regulatory_framework:read',
    REGULATORY_FRAMEWORK_WRITE: 'regulatory_framework:write',
    REGULATORY_MAPPING_WRITE: 'regulatory_mapping:write',
    PACK_SUBMISSION_READ: 'pack_submission:read',
    PACK_SUBMISSION_WRITE: 'pack_submission:write',
    PACK_SUBMISSION_REVIEW: 'pack_submission:review',
    LOCALIZATION_EXPORT_EXECUTE: 'localization_export:execute',
    // Partner Marketplace (Sprint 2.9.0)
    PARTNER_ORG_READ: 'partner_org:read',
    PARTNER_ORG_WRITE: 'partner_org:write',
    PARTNER_TENANT_READ: 'partner_tenant:read',
    PARTNER_TENANT_PROVISION: 'partner_tenant:provision',
    PARTNER_TENANT_LIFECYCLE: 'partner_tenant:lifecycle',
    SERVICE_TIER_READ: 'service_tier:read',
    SERVICE_TIER_WRITE: 'service_tier:write',
    DELIVERABLE_READ: 'deliverable:read',
    DELIVERABLE_WRITE: 'deliverable:write',
    DELIVERABLE_TEMPLATE_WRITE: 'deliverable_template:write',
    PARTNER_REPORT_READ: 'partner_report:read',
    PARTNER_REPORT_GENERATE: 'partner_report:generate',
    PARTNER_BRAND_READ: 'partner_brand:read',
    PARTNER_BRAND_WRITE: 'partner_brand:write',
    PARTNER_TICKET_READ: 'partner_ticket:read',
    PARTNER_TICKET_WRITE: 'partner_ticket:write',
    PARTNER_TICKET_ESCALATE: 'partner_ticket:escalate',
    PARTNER_WORKFLOW_READ: 'partner_workflow:read',
    PARTNER_WORKFLOW_WRITE: 'partner_workflow:write',
    PARTNER_WORKFLOW_TEMPLATE_WRITE: 'partner_workflow_template:write',
    PARTNER_AUDIT_READ: 'partner_audit:read',
    PARTNER_EXPORT_EXECUTE: 'partner_export:execute',
    // Sprint 3.6.0: File Storage, Email & External Integrations
    ATTACHMENTS_UPLOAD: 'attachments:upload',
    /**
     * Sprint 64.32.0 — persisting a client-built export, split OUT of
     * `attachments:upload`.
     *
     * `POST /attachments/client-csv-export` renders a file the browser already
     * holds and stores it in the File Component so it appears in Files Browser.
     * It required `attachments:upload`, which is also the key to
     * `POST /attachments` — the route that accepts **any** owner
     * types, `incident_evidence` and `dpia_record` among them.
     *
     * Sprint 43.5.7 granted the auditor `attachments:upload` for one reason: the
     * Governance Audit Trail CSV/XLSX export 403'd without it, because this
     * pass-through persists. That grant was correct about the symptom and, as
     * Sprint 64.0.0's gate E12.5 recorded (compliance F3), it handed a
     * deliberately read-only role an ungated creation key for every
     * evidence-bearing owner type in the platform.
     *
     * Two capabilities were sharing one permission because one of them needed
     * persistence. They no longer do. The auditor holds this and not
     * `attachments:upload`.
     */
    ATTACHMENTS_CLIENT_EXPORT_PERSIST: 'attachments:client_export:persist',
    ATTACHMENTS_DOWNLOAD: 'attachments:download',
    ATTACHMENTS_DELETE: 'attachments:delete',
    /**
     * Sprint 63.31.0 (#410, owner rulings Q4 (a), OD-C) — releasing a file an
     * evidence entry rests on at the end of its retention period, or for an
     * erasure request that reaches it (`POST /attachments/:fileId/release-at-retention`).
     * A destruction of evidence, so it is not `attachments:delete` (which every
     * delete path refuses for a depended-on file) and not a read-shaped grant.
     * Held by the roles that hold retention write (`dm:retention:write`) today.
     */
    ATTACHMENT_RETENTION_RELEASE: 'attachments:retention:release',
    PLATFORM_WEBHOOKS_READ: 'platform:webhooks:read',
    PLATFORM_WEBHOOKS_WRITE: 'platform:webhooks:write',
    PLATFORM_DOMAINS_READ: 'platform:domains:read',
    PLATFORM_DOMAINS_WRITE: 'platform:domains:write',
    PLATFORM_EXPORT_SCHEDULES_READ: 'platform:export-schedules:read',
    PLATFORM_EXPORT_SCHEDULES_WRITE: 'platform:export-schedules:write',
    // Privacy Scanner (Sprint 4.1.0)
    PRIVACY_SCANNER_READ: 'privacy_scanner:read',
    PRIVACY_SCANNER_WRITE: 'privacy_scanner:write',
    PRIVACY_SCANNER_EXPORT: 'privacy_scanner:export',
    // Module Configuration (Sprint 4.4.0)
    PLATFORM_MODULE_CONFIG_READ: 'platform:module-config:read',
    PLATFORM_MODULE_CONFIG_WRITE: 'platform:module-config:write',
    // Form Engine (Sprint 7.6.0 — Phase 7)
    FORMS_TEMPLATE_READ: 'forms:template:read',
    FORMS_TEMPLATE_WRITE: 'forms:template:write',
    FORMS_INSTANCE_READ: 'forms:instance:read',
    FORMS_INSTANCE_WRITE: 'forms:instance:write',
    FORMS_INSTANCE_APPROVE: 'forms:instance:approve',
    FORMS_INSTANCE_EXPORT: 'forms:instance:export',
    FORMS_AUDIT_READ: 'forms:audit:read',
    // Sprint 11.5.0: Corporate Group Management (Phase 9)
    GROUP_MANAGE: 'group:manage',
    GROUP_READ: 'group:read',
    GROUP_SHARED_DATA_MANAGE: 'group:shared_data:manage',
    GROUP_SHARED_DATA_READ: 'group:shared_data:read',
    GROUP_AUDIT_READ: 'group:audit:read',
    GROUP_DASHBOARD_READ: 'group:dashboard:read',
    GROUP_USERS_MANAGE: 'group:users:manage',
    GROUP_REPORTING_READ: 'group:reporting:read',
    // Generating a group compliance report consumes work, and deleting one is a
    // hard `deleteOne` with no tombstone. Both used to be gated on
    // `GROUP_REPORTING_READ`, which the seeded `auditor` role holds — a role
    // described as "read-only access to audit trails and compliance reports"
    // could permanently destroy any of them. Reads stay on `:read`; generation
    // takes this; deletion takes `GROUP_MANAGE`.
    GROUP_REPORTING_WRITE: 'group:reporting:write',
    GROUP_SETTINGS_MANAGE: 'group:settings:manage',
    GROUP_ORG_SWITCH: 'group:org:switch',
    // Phase 9 Extension: Organization Management & Public Intake Config
    ORG_MANAGE_READ: 'org:manage:read',
    ORG_MANAGE_WRITE: 'org:manage:write',
    ORG_MANAGE_DEACTIVATE: 'org:manage:deactivate',
    INTAKE_CONFIG_READ: 'intake_config:read',
    INTAKE_CONFIG_WRITE: 'intake_config:write',
    // Task Management (Sprint 17.0.0 — Phase 15)
    TM_TASK_READ: 'tm:task:read',
    TM_TASK_CREATE: 'tm:task:create',
    TM_TASK_UPDATE: 'tm:task:update',
    TM_TASK_DELETE: 'tm:task:delete',
    TM_TASK_ASSIGN: 'tm:task:assign',
    TM_TASK_COMPLETE: 'tm:task:complete',
    TM_TASK_ESCALATE: 'tm:task:escalate',
    TM_COMMENT_READ: 'tm:comment:read',
    TM_COMMENT_WRITE: 'tm:comment:write',
    TM_CHECKLIST_UPDATE: 'tm:checklist:update',
    TM_CALENDAR_READ: 'tm:calendar:read',
    TM_EXPORT: 'tm:export',
    // Workflow Engine (Sprint 17.1.0 — Phase 15)
    WF_DEFINITION_READ: 'wf:definition:read',
    WF_DEFINITION_WRITE: 'wf:definition:write',
    WF_DEFINITION_DELETE: 'wf:definition:delete',
    WF_DEFINITION_ACTIVATE: 'wf:definition:activate',
    WF_INSTANCE_READ: 'wf:instance:read',
    WF_INSTANCE_TRANSITION: 'wf:instance:transition',
    WF_TEMPLATE_READ: 'wf:template:read',
    WF_TEMPLATE_WRITE: 'wf:template:write',
    // Notifications (Sprint 17.3.0 — Phase 15)
    PLATFORM_NOTIFICATIONS_READ: 'platform:notifications:read',
    PLATFORM_NOTIFICATIONS_MANAGE: 'platform:notifications:manage',
    PLATFORM_NOTIFICATIONS_PREFERENCES: 'platform:notifications:preferences',
    PLATFORM_NOTIFICATIONS_ADMIN: 'platform:notifications:admin',
    // Dashboard (Sprint 29.2.0 — Phase 22)
    DASHBOARD_READ: 'dashboard:read',
    /**
     * Sprint 63.17.0 (Q6, #310) — save the caller's OWN dashboard widget layout
     * (`PUT /dashboard/preferences`). The route was gated on `DASHBOARD_READ`, so a
     * role built to be read-only could change what the dashboard stored for it.
     * The effect is narrow and personal, which is why it is its own grant and not
     * a module write: held by `platform_admin`, `compliance_owner` (through
     * `allPermissions`) and `auditor` (explicitly — the auditor reaches the page
     * and has always saved its layout).
     */
    DASHBOARD_PREFERENCES_WRITE: 'dashboard:preferences:write',
    /**
     * Sprint 63.13.0 (AC8) — capture the current period's point of a stored
     * metric series on demand (`POST /dashboard/metrics/:metric_key/capture`),
     * rather than waiting for the hourly scheduler. The point is computed like
     * any other and stamped with the capturing user's id. The capture is
     * audited only when its write applies (Gate E12.5 pass 3): a capture that
     * finds its month already closed, or loses an insert race, is answered
     * `applied: false` with the other writer's row and writes no audit row.
     *
     * Held by the two T1 roles (`platform_admin`, `compliance_owner`) through
     * `allPermissions` and by nobody else out of the box: it writes a figure the
     * whole organisation's dashboard shows, and `DASHBOARD_READ` — which every
     * role holds — must not be the key to it. Its suffix reaches no suffix
     * filter (`reachesAuditorBySuffix`) and its prefix no prefix filter.
     */
    DASHBOARD_METRICS_CAPTURE: 'dashboard:metrics:capture',
    // UI Assist (Sprint 17.8.0 — Phase 15)
    UI_ASSIST_EXECUTE: 'ui_assist:execute',
    UI_ASSIST_VIEW: 'ui_assist:view',
    // Agent Lobby — Multi-Session Coordination (UI Assist extension)
    AGENT_LOBBY_VIEW: 'agent_lobby:view',
    AGENT_LOBBY_MANAGE: 'agent_lobby:manage',
    AGENT_LOBBY_DECIDE: 'agent_lobby:decide',
    // Sprint 18.0.0: License & Package Management
    LICENSE_READ: 'license:read',
    LICENSE_ACTIVATE: 'license:activate',
    LICENSE_DEACTIVATE: 'license:deactivate',
    PACKAGE_READ: 'package:read',
    PACKAGE_UPGRADE: 'package:upgrade',
    PACKAGE_DOWNGRADE: 'package:downgrade',
    // Sprint 40.0.0: Bulk Import
    CONSENT_WORKFLOW_IMPORT: 'consent:workflow:import',
    CASE_IMPORT: 'case:import',
    // Sprint 28.7.1: Multi-Department Report Consolidation (UC-VN-11)
    CONSOLIDATION_CASE_CREATE: 'consolidation:case:create',
    CONSOLIDATION_CASE_READ: 'consolidation:case:read',
    CONSOLIDATION_CASE_UPDATE: 'consolidation:case:update',
    CONSOLIDATION_CASE_DELETE: 'consolidation:case:delete',
    CONSOLIDATION_SECTION_SUBMIT: 'consolidation:section:submit',
    CONSOLIDATION_SECTION_REVIEW: 'consolidation:section:review',
    CONSOLIDATION_APPROVAL_DECIDE: 'consolidation:approval:decide',
    CONSOLIDATION_EXPORT_GENERATE: 'consolidation:export:generate',
    CONSOLIDATION_FLAG_RESOLVE: 'consolidation:flag:resolve',
    // Sprint 40.2.0: Data Subject Profile
    DATA_SUBJECT_CREATE: 'data_subject:create',
    DATA_SUBJECT_READ: 'data_subject:read',
    DATA_SUBJECT_UPDATE: 'data_subject:update',
    DATA_SUBJECT_DELETE: 'data_subject:delete',
    DATA_SUBJECT_LINK: 'data_subject:link',
    // Sprint 43.2.0 batch 1 — Rights Request admin & retention & breach
    // (E1 template admin, E2 dept admin, E3 bulk export, E5 breach flag).
    // Role-tier mapping (per sprint-43.2.0.md):
    //   CASE_TEMPLATE_ADMIN  → PLATFORM_ADMIN + COMPLIANCE_OWNER (DPO)
    //   ORG_DEPARTMENT_ADMIN → PLATFORM_ADMIN only
    //   EXPORT_BULK          → PLATFORM_ADMIN + COMPLIANCE_OWNER + CASE_MANAGER
    //   BREACH_FLAG          → COMPLIANCE_OWNER (DPO) only
    CASE_TEMPLATE_ADMIN: 'case:template_admin',
    ORG_DEPARTMENT_ADMIN: 'org:department_admin',
    EXPORT_BULK: 'export:bulk',
    BREACH_FLAG: 'breach:flag',
    // Sprint 47.4.0 — Immutable Audit Trails Foundation
    // Four permission keys that gate the new Platform → Audit & Proof
    // sub-module. All four are eligible across Light, Standard, and
    // Enterprise tiers per the platform-tier decision recorded in the
    // 47.4.x series; commercial gating is via credit-package purchase,
    // not tier (architecture doc §13). Role-tier mapping (per arch §13):
    //   IMMUTABLE_AUDIT_TRAILS_READ      → PLATFORM_ADMIN + COMPLIANCE_OWNER + AUDITOR
    //   IMMUTABLE_AUDIT_TRAILS_CONFIGURE → PLATFORM_ADMIN + COMPLIANCE_OWNER
    //   IMMUTABLE_AUDIT_TRAILS_EXPORT    → PLATFORM_ADMIN + COMPLIANCE_OWNER + AUDITOR
    //   IMMUTABLE_AUDIT_TRAILS_BILLING   → PLATFORM_ADMIN + COMPLIANCE_OWNER
    //                                      (COMPLIANCE_OWNER holds every permission that is not
    //                                      custom-only or platform-only, and this is neither;
    //                                      the Auditor does not hold it)
    IMMUTABLE_AUDIT_TRAILS_READ: 'platform:immutable_audit_trails:read',
    /**
     * Sprint 63.17.0 (Q6) — start an integrity verification run
     * (`POST /integrity-center/runs`): it records a run row and dispatches a
     * worker, so it is not a read. The auditor's core activity, therefore held by
     * the same three roles as `IMMUTABLE_AUDIT_TRAILS_READ`.
     */
    IMMUTABLE_AUDIT_TRAILS_VERIFY: 'platform:immutable_audit_trails:verify',
    IMMUTABLE_AUDIT_TRAILS_CONFIGURE: 'platform:immutable_audit_trails:configure',
    IMMUTABLE_AUDIT_TRAILS_EXPORT: 'platform:immutable_audit_trails:export',
    IMMUTABLE_AUDIT_TRAILS_BILLING: 'platform:immutable_audit_trails:billing',
    // Sprint 47.5.0 — Forseti AI Legal Advisor (foundation)
    // Per family plan §9.1 Q2: ALL permissions are available on ALL TIERS
    // (Light/Standard/Enterprise). Tiers differentiate only on the monthly
    // token bucket (`grc_ai_quota_counters`), never on which permissions
    // exist. Role-level gating still applies (e.g. VIEWER gets only :read).
    // ai_advisor (4 + 3 memory permissions added in Sprint 48.0.0c)
    AI_ADVISOR_CHAT: 'ai_advisor:chat',
    AI_ADVISOR_CHAT_SHARE: 'ai_advisor:chat:share',
    AI_ADVISOR_CHAT_EXPORT: 'ai_advisor:chat:export',
    AI_ADVISOR_ADMIN: 'ai_advisor:admin',
    // Sprint 48.0.0c — memory browse + curate. `:read` reads org memories;
    // `:write` edits / deletes auto-inferred + user-explicit rows; `:curate`
    // is admin-only and gates the "promote to admin_curated" action that
    // locks a memory against retention GC.
    AI_ADVISOR_MEMORY_READ: 'ai_advisor:memory:read',
    AI_ADVISOR_MEMORY_WRITE: 'ai_advisor:memory:write',
    AI_ADVISOR_MEMORY_CURATE: 'ai_advisor:memory:curate',
    // ai_projects (5)
    AI_PROJECTS_READ: 'ai_projects:read',
    AI_PROJECTS_WRITE: 'ai_projects:write',
    AI_PROJECTS_SHARE: 'ai_projects:share',
    AI_PROJECTS_DELETE: 'ai_projects:delete',
    AI_PROJECTS_ADMIN: 'ai_projects:admin',
    // ai_workflows (8)
    AI_WORKFLOWS_READ: 'ai_workflows:read',
    AI_WORKFLOWS_WRITE: 'ai_workflows:write',
    AI_WORKFLOWS_RUN: 'ai_workflows:run',
    AI_WORKFLOWS_SHARE: 'ai_workflows:share',
    AI_WORKFLOWS_DELETE: 'ai_workflows:delete',
    AI_WORKFLOWS_ADMIN: 'ai_workflows:admin',
    AI_WORKFLOWS_SYSTEM_PACK_READ: 'ai_workflows:system_pack:read',
    AI_WORKFLOWS_SYSTEM_PACK_HIDE: 'ai_workflows:system_pack:hide',
    // ai_tabular_reviews (6)
    AI_TABULAR_REVIEWS_READ: 'ai_tabular_reviews:read',
    AI_TABULAR_REVIEWS_WRITE: 'ai_tabular_reviews:write',
    AI_TABULAR_REVIEWS_RUN: 'ai_tabular_reviews:run',
    AI_TABULAR_REVIEWS_SHARE: 'ai_tabular_reviews:share',
    AI_TABULAR_REVIEWS_DELETE: 'ai_tabular_reviews:delete',
    AI_TABULAR_REVIEWS_ADMIN: 'ai_tabular_reviews:admin',
    // ai_document_review (5)
    AI_DOCUMENT_REVIEW_PROPOSE_EDIT: 'ai_document_review:propose_edit',
    AI_DOCUMENT_REVIEW_ACCEPT_EDIT: 'ai_document_review:accept_edit',
    AI_DOCUMENT_REVIEW_REJECT_EDIT: 'ai_document_review:reject_edit',
    AI_DOCUMENT_REVIEW_VERSION_PROMOTE: 'ai_document_review:version_promote',
    AI_DOCUMENT_REVIEW_ADMIN: 'ai_document_review:admin',
    // ai_provider (4)
    AI_PROVIDER_CONFIGURE: 'ai_provider:configure',
    AI_PROVIDER_VIEW_USAGE: 'ai_provider:view_usage',
    AI_PROVIDER_VIEW_AUDIT: 'ai_provider:view_audit',
    // Quota override exists in the schema on every tier per family plan §4;
    // Org Administrator role is auto-assigned only on Enterprise. Standard
    // and Light orgs can grant manually if desired.
    AI_PROVIDER_QUOTA_OVERRIDE: 'ai_provider:quota:override',
    // Sprint 48.5.x — self-service "Buy More" billing. Gates both the Stripe
    // checkout-intent proxy (Flow A) and the invoice redeem that credits the
    // token bucket (Flow B). Distinct from `:quota:override` (which lets a
    // user bypass an exhausted bucket without paying); billing lets a user
    // legitimately purchase + redeem additional units. Available on every tier.
    AI_PROVIDER_BILLING: 'ai_provider:billing',
    // Sprint 47.9.7 — MCP connector KEY administration: issuance, rotation and
    // revocation of the API keys that external Claude clients use to call
    // Forseti tools, plus the read of that key inventory.
    //
    // Sprint 64.172.0 narrowed this family to the credential surface it names.
    // Until then `:create` also gated `POST /pending-writes/:id/approve` and
    // `/decline`, and `:list` also gated `GET /pending-writes` — so whoever
    // could mint an MCP key could approve the writes made with it, and an
    // organisation that wanted a reviewer who is not a key issuer had no
    // permission to give them. The queue now has its own two grants below.
    //
    // Rotation stays on `:create` by decision, not by omission: rotating a key
    // hands the caller a working plaintext secret for an existing key, which is
    // the same capability as minting one. A separate `:rotate` grant would name
    // a distinction that confers no different power.
    AI_PROVIDER_MCP_CREATE: 'ai_provider:mcp:create',
    AI_PROVIDER_MCP_REVOKE: 'ai_provider:mcp:revoke',
    AI_PROVIDER_MCP_LIST: 'ai_provider:mcp:list',
    /**
     * Sprint 64.172.0 — the MCP pending-write queue, as its own resource.
     *
     * `:pending:list` reads the queue; `:pending:decide` approves **or** declines
     * a row. One grant for both directions follows `GOV_APPROVAL_DECIDE`
     * (`governance/routes.ts:990,1007`) and `AGENT_LOBBY_DECIDE`: refusing a
     * proposal and accepting it are the same office, and splitting them would
     * let a role decline everything it cannot approve.
     *
     * The read is deliberately separate from `:decide`, and separate from
     * `:list`. A reviewer who may judge a queued write must be able to see the
     * queue without also seeing the credential inventory — otherwise the
     * separation this pair exists to make expressible still is not.
     *
     * `:pending:list` rather than `:pending:read`: the seed's auditor role takes
     * every permission ending in `:read` (`permissions-contract.test.ts:21`), and
     * the queue's rows carry a consequential preview whose prior/proposed record
     * values are NOT passed through the form-visibility filter
     * (`consequential-preview.ts:57`). Widening a known-unfiltered surface to a
     * new role is not something a suffix should decide silently. `:list` matches
     * this module's own vocabulary and reaches exactly who `:list` reaches today.
     *
     * Neither grant relaxes 64.123.0: a single admin holding both may still
     * approve their own proposal, and it is still recorded and audited. What
     * changes is that an organisation with staff to spread the roles across can
     * now express that — separation is available, not mandatory.
     */
    AI_PROVIDER_MCP_PENDING_LIST: 'ai_provider:mcp:pending:list',
    AI_PROVIDER_MCP_PENDING_DECIDE: 'ai_provider:mcp:pending:decide',
    /**
     * Sprint 63.23.0 — the MCP OAuth authorization server (decision 13).
     *
     * OAuth clients are credentials in the same sense API keys are, so they get
     * the same three-way split: `:list` reads the register, `:create` registers a
     * client, rotates its secret and switches self-registration (DCR) on or off
     * for the organisation, `:revoke` withdraws a client and every grant made to
     * it. Rotating a secret is on `:create` for the reason key rotation is: it
     * hands the caller a working secret, which is minting one.
     *
     * Grants — one user's consent to one client — are read and revoked on their
     * own two grants, so an organisation can give a reviewer the connected-apps
     * register without the power to register new clients. A user may always list
     * and revoke THEIR OWN grants without either permission.
     *
     * None of the five confers approval of a queued write: the 64.172.0
     * separation (approving writes is not minting credentials) holds.
     */
    AI_PROVIDER_MCP_OAUTH_CLIENT_LIST: 'ai_provider:mcp:oauth_client:list',
    AI_PROVIDER_MCP_OAUTH_CLIENT_CREATE: 'ai_provider:mcp:oauth_client:create',
    AI_PROVIDER_MCP_OAUTH_CLIENT_REVOKE: 'ai_provider:mcp:oauth_client:revoke',
    AI_PROVIDER_MCP_GRANT_LIST: 'ai_provider:mcp:grant:list',
    AI_PROVIDER_MCP_GRANT_REVOKE: 'ai_provider:mcp:grant:revoke',
    // F19 (fix-sprint-20260503-2128) — evidence review/approve permissions
    // backing `evidence-routes.ts`. Previously the routes used hardcoded
    // string sets (`'evidence:review'`, `'evidence:approve'`) that the
    // permission registry never saw; codifying them on the enum lets the
    // route layer use `requirePermission(...)` middleware and lets the
    // permission-coverage tooling include them in audits.
    EVIDENCE_REVIEW: 'evidence:review',
    EVIDENCE_APPROVE: 'evidence:approve',
    /**
     * Sprint 62.2.1 — cross-framework evidence packs.
     *
     * Genuinely new rather than a reuse of the PDPL family: verified at kickoff,
     * `PDPL_EVIDENCE_PACK_SEND` is that family's only member, and it gates
     * transmission of one specific regulator-facing artefact to MoPS A05. A
     * framework-agnostic read is not a narrower case of that.
     *
     * Only the read permission is declared here, because only the read path
     * ships in this sprint. Create, approve and send arrive in 62.2.2 and 62.2.4
     * alongside the transitions that enforce them — a permission that gates
     * nothing is a permission nobody can audit.
     */
    FRAMEWORK_EVIDENCE_PACK_READ: 'framework_evidence_pack:read',
    /**
     * Sprint 62.2.2 — assembling a version and exporting an archive.
     *
     * Separate from `:read` because assembly is a write that mints an immutable,
     * hash-bearing artefact, and export puts that artefact on disk where it can
     * be forwarded. A reviewer who may inspect the posture is not automatically
     * someone who may produce a filing-grade copy of it.
     */
    FRAMEWORK_EVIDENCE_PACK_CREATE: 'framework_evidence_pack:create',
    /**
     * Sprint 62.2.4 — approving a specific pack version for dispatch.
     *
     * Separate from `:create` because approval is the human judgement the parent
     * sprint's locked decision makes mandatory: *autonomous authority filing is
     * out of scope; human approval remains mandatory.* Whoever assembles a pack
     * is not automatically whoever may certify it fit to send.
     */
    FRAMEWORK_EVIDENCE_PACK_APPROVE: 'framework_evidence_pack:approve',
    /**
     * Sprint 62.2.4 — transmitting an approved pack to an authority, and
     * recording a hard-copy dispatch.
     *
     * The narrowest permission in the family. This is the point at which
     * ComplianceOne communicates with a regulator, which is a different risk
     * class from anything before it. Mirrors `PDPL_EVIDENCE_PACK_SEND`, which is
     * org_admin/DPO-scoped for the same reason.
     */
    FRAMEWORK_EVIDENCE_PACK_SEND: 'framework_evidence_pack:send',
    /**
     * Sprint 62.3.0 — the AI system register.
     *
     * Reading the register means reading which AI systems an organisation runs,
     * what they decide, and how they were classified under Decision 33. That is
     * the auditor's subject matter, so `:read` is deliberate — `seed-roles.ts`
     * grants the auditor role every permission whose value ends in `:read`.
     */
    AI_GOVERNANCE_READ: 'ai_governance:read',
    /**
     * Sprint 62.3.0 — creating and maintaining AI system profiles, and starting a
     * classification run.
     *
     * Running a screening is a write rather than a read: it mints an immutable
     * run bound to a pack version and enters the review queue. Separate from
     * `:review` because *proposing* a tier and *deciding* one are the two halves
     * the locked architecture decision requires be distinguishable — a screening
     * that its own author could approve would make "mandatory human approval"
     * self-attestation.
     */
    AI_GOVERNANCE_WRITE: 'ai_governance:write',
    /**
     * Sprint 62.3.0 — approving or rejecting a proposed risk tier.
     *
     * The narrowest permission in the family, and the one that carries legal
     * weight: Decree 142 obligations are instantiated from the tier a reviewer
     * approves, not from the tier the platform proposed.
     */
    AI_GOVERNANCE_REVIEW: 'ai_governance:review',
    /**
     * Sprint 62.48.0 (Wave 0A) — approving a release, and go-live of an AI system.
     *
     * Separate from `:review` because a tier decision and a decision to put a
     * system into production are different acts with different evidence. Granted
     * to the roles that hold `:review`, never to a role that holds only `:write`:
     * a person who can edit a system's profile must not also be the person who
     * releases it. The server refuses a release by a listed developer of the
     * version (AC7); the permission only says who may ask.
     */
    AI_GOVERNANCE_RELEASE: 'ai_governance:release',
    /**
     * Sprint 62.48.0 (Wave 0A) — validation sign-off on an AI system.
     *
     * Independent model validation (Điều 16) is recorded by a holder of this
     * permission, who must not be a listed developer of the system (AC9). Granted
     * alongside `:review` and never to a `:write`-only role, for the same reason
     * as `:release`.
     */
    AI_GOVERNANCE_VALIDATE: 'ai_governance:validate',
    /**
     * Sprint 62.48.0 (Wave 0A) — override, pause and stop of a human-supervised AI
     * channel (Điều 3 khoản 5; Điều 5 khoản 2–3).
     *
     * Granted to the roles that hold `:write`. It is a separate permission so an
     * organisation can give an operations supervisor the power to stop a system
     * without giving them the power to change its profile.
     */
    AI_GOVERNANCE_INTERVENE: 'ai_governance:intervene',
    /**
     * Sprint 62.48.0 (Wave 0A) — the payment-authority record for an automated
     * payment decision (Điều 5 khoản 1 g(i)–(iii), h).
     *
     * Granted to the roles that hold `:write`. Recording an authority is a
     * statement about what a system may do with money, so it is kept apart from
     * `:review`, which decides risk tiers.
     */
    AI_GOVERNANCE_AUTHORITY: 'ai_governance:authority',
    /**
     * Sprint 62.48.0 (Wave 0A) — routing and human re-review of AI-influenced
     * complaints (Điều 20 khoản 4).
     *
     * Granted to the roles that hold `:write`. Re-review is a decision about one
     * customer's outcome; it sits apart from the register's `:write` so the people
     * who run the complaint queue are not automatically the people who maintain
     * the system register.
     */
    AI_GOVERNANCE_COMPLAINT: 'ai_governance:complaint',
    /**
     * Sprint 62.48.0 (Wave 0A) — the three-lines attestation (Điều 12; Điều 14).
     *
     * Each line of defence attests its own part of the responsibility map. Granted
     * alongside `:review`, never to a `:write`-only role, so that the attesting
     * line is a named person with a decision to make rather than an editor.
     */
    AI_GOVERNANCE_ATTEST: 'ai_governance:attest',
    /**
     * Sprint 62.48.0 (cluster D, G22) — recording an AI customer or internal-user
     * disclosure (Điều 11; Điều 20 khoản 2–3).
     *
     * Split from `:write` so the person who records what a customer hears is not, by
     * the same grant, the person who can change `autonomy_level` (UC-VN-61 criterion 3).
     * Granted to the roles that hold `:write` in the seed, and never reached by the
     * auditor (no `:read` suffix). A custom role that records disclosures must be given
     * this permission; `:write` alone no longer reaches the disclosure route.
     */
    AI_GOVERNANCE_DISCLOSE: 'ai_governance:disclose',
    /**
     * Sprint 65.19.0 -- legal matters (LEG-01).
     *
     * Three. `:close` is separate from `:write` because resolving a matter is a
     * conclusion somebody reaches, not an edit: it writes what a named person
     * concluded and it is the transition the adopted fact watches. Recording that
     * a complaint arrived and deciding how it ends are different acts and can sit
     * in different hands.
     *
     * There is deliberately no `:advise`. The platform derives no legal position,
     * so there is no grant that would let one be recorded as the platform's.
     */
    LEGAL_MATTERS_READ: 'legal_matters:read',
    LEGAL_MATTERS_WRITE: 'legal_matters:write',
    LEGAL_MATTERS_CLOSE: 'legal_matters:close',
    /**
     * Sprint 65.21.0 — FINA-01 credit-information uses.
     *
     * Two grants, not three. There is deliberately no `:approve`: Law 91
     * Điều 27(1)(b) is a prohibition, so a use for a named purpose either rests
     * on covering consent or is refused by the server. A grant that let a person
     * approve one anyway would be a way to record the prohibited act as done.
     */
    CREDIT_INFORMATION_READ: 'credit_information:read',
    CREDIT_INFORMATION_WRITE: 'credit_information:write',
    /**
     * Sprint 65.18.0 -- continuity plans and exercises (IRP-01).
     *
     * Two, not three. Unlike de-identification there is no separate approval:
     * completing an exercise IS the record of what happened, and a second person
     * attesting to it would be attesting to someone else's observation rather
     * than to a judgement of their own.
     */
    CONTINUITY_READ: 'continuity:read',
    CONTINUITY_WRITE: 'continuity:write',
    /**
     * Sprint 65.17.0 -- de-identification runs (ANON-01).
     *
     * `:approve` is separate from `:write` because recording that a
     * transformation happened and attesting that what remains is acceptable are
     * different acts, and an organisation must be able to put them in different
     * hands. The approval is the only place a residual re-identification risk is
     * accepted, so it is the one worth fencing.
     */
    DEIDENTIFICATION_READ: 'deidentification:read',
    DEIDENTIFICATION_WRITE: 'deidentification:write',
    DEIDENTIFICATION_APPROVE: 'deidentification:approve',
    /**
     * Sprint 62.4.0 — the regulatory classification register.
     *
     * Reading it means reading which data assets and information systems an
     * organisation holds, what statutory tier each carries, and the reasoning
     * behind every tier. That is exactly the auditor's subject matter, so `:read`
     * is deliberate — `seed-roles.ts` grants the auditor role every permission
     * whose value ends in `:read`.
     *
     * Named for the *activity* rather than an instrument. The module classifies
     * against whatever scheme an installed framework declares, so a permission
     * named after any one jurisdiction's list would be wrong for every tenant
     * that installed a different one.
     */
    REGULATORY_CLASSIFICATION_READ: 'regulatory_classification:read',
    /**
     * Sprint 62.4.0 — maintaining subject profiles and running an assessment.
     *
     * An assessment is a write, not a read: it mints an immutable record bound to
     * a scheme version and enters the review queue. Separate from `:review`
     * because *proposing* a tier and *deciding* one are different acts, and the
     * split is what lets an organisation put them in different hands.
     *
     * **It does not put them in different hands by default, and this docblock used
     * to claim otherwise.** Both system roles that hold `:review` also hold
     * `:write` (`permissions.ts` role tables), so out of the box the same person
     * screens and approves — while the text here called that "self-attestation",
     * as though something prevented it. Gate E12.5 F12.
     *
     * Enforcing separation was considered and declined: it would lock every tenant
     * with a single compliance officer out of its own register on day one, which
     * is most of them. 62.2.4's precedent applies instead — the condition is made
     * **visible** rather than pretended away. `ClassificationReview.self_reviewed`
     * records whether the reviewer is the screening's own author, the detail page
     * says so on the decision, and the audit payload carries it. Whether to
     * require two people is a tenant policy question the record now supports
     * answering.
     */
    REGULATORY_CLASSIFICATION_WRITE: 'regulatory_classification:write',
    /**
     * Sprint 62.4.0 — approving or rejecting a proposed tier.
     *
     * The narrowest permission in the family, and the one that carries legal
     * weight. An approval writes the tier in force onto the profile, instantiates
     * the protective measures that follow from it, and — where the approved tier
     * declares a `platform_data_tier` — writes the legacy `data_tier` field on
     * the canonical record. (This said "211 existing call sites". Sprint 64.73.0
     * re-measured with the TypeScript language service while retiring the
     * `DataTier` union: 14 type-level references in 6 files. The 211 was never
     * re-derived after Sprint 62.4.0 moved the readers onto the profile.)
     */
    REGULATORY_CLASSIFICATION_REVIEW: 'regulatory_classification:review',
    /**
     * Sprint 62.20.0 AC5 — reading the regulatory placeholder register.
     *
     * The `:read` suffix is load-bearing: the auditor role is derived by
     * `endsWith(':read')`, and "what has the organisation been served, and what
     * has it written down about a mechanism the law has not defined yet" is
     * exactly the auditor's question. Named for the activity, never an
     * instrument — the kinds are declared by whichever pack an org installed.
     */
    REGULATORY_PLACEHOLDERS_READ: 'regulatory_placeholders:read',
    /**
     * Sprint 62.20.0 AC5 — recording and amending a placeholder intake.
     *
     * A write, and deliberately not a decision token. The register triggers
     * nothing, so there is no third `:approve`-shaped permission: nothing here is
     * approved, lodged, filed or declared, and a token implying otherwise would
     * be the register reading as an obligation.
     */
    REGULATORY_PLACEHOLDERS_WRITE: 'regulatory_placeholders:write',
    /**
     * Sprint 62.13.0 — reading penalty-exposure figures and the fine-area
     * readiness map.
     *
     * Named for the activity, not an instrument (the module computes whatever
     * schedule an installed framework declares). The `:read` suffix is
     * load-bearing: the auditor role is derived by `endsWith(':read')`, and an
     * exposure figure is exactly the kind of reading an auditor is for.
     */
    PENALTY_EXPOSURE_READ: 'penalty_exposure:read',
    /**
     * Sprint 62.13.0 — declaring the org's exposure facts: entity basis, metric
     * values (revenue, subject counts — vocabulary the packs declare), and
     * per-area applicability marks. A write, and a consequential one: the
     * figures every reader sees are computed over these declarations, which is
     * why recording them is separated from reading them.
     */
    PENALTY_EXPOSURE_MANAGE: 'penalty_exposure:manage',
    /**
     * Sprint 62.13.0 — reading the enforcement-case register: intake records,
     * lifecycle stages, corrective-measure evidence, the limitation clock.
     */
    ENFORCEMENT_CASE_READ: 'enforcement_case:read',
    /**
     * Sprint 62.13.0 — creating and driving an enforcement case: intake,
     * stage transitions, recording the penalty decision and its payment or
     * complaint path, corrective-measure entries (evidence mandatory), closure.
     * Deciding, admitting or classifying a violation is a named human action
     * behind this permission; the platform only validates the lifecycle.
     */
    ENFORCEMENT_CASE_MANAGE: 'enforcement_case:manage',
    /**
     * Sprint 62.14.0 — reading the authority-direction register: intake records,
     * statutory clock state, response evidence, closure outcomes.
     */
    AUTHORITY_DIRECTION_READ: 'authority_direction:read',
    /**
     * Sprint 62.14.0 — recording and driving a direction: intake with receipt
     * evidence, response entries, links to incident-scoped authority requests,
     * closure with outcome. The clock is resolved by the platform from installed
     * content; this permission never sets a deadline by hand.
     */
    AUTHORITY_DIRECTION_MANAGE: 'authority_direction:manage',
    /**
     * Sprint 62.23.0 — consequential release decision. Kept separate from
     * routine direction record management so a recorder cannot release
     * protected data merely because they can maintain the case file.
     */
    AUTHORITY_DIRECTION_DATA_RELEASE: 'authority_direction:data_release',
    /**
     * Sprint 62.14.0 — reading the legal-hold register.
     */
    LEGAL_HOLD_READ: 'legal_hold:read',
    /**
     * Sprint 62.14.0 — placing a hold. A decision token, not record-keeping
     * (the `logistics_claim:decide` reasoning): an active hold blocks all THREE
     * destruction paths server-side — platform-file destruction, retention
     * deletion and DSR erasure — so placing one changes what the platform will
     * refuse to destroy. The file gate is the strongest of the three: a register
     * hold refuses a hard delete even where the per-owner-type blanket hold
     * could be overridden. Separate from releasing, because the person who
     * freezes material and the person who un-freezes it should be separable.
     */
    LEGAL_HOLD_PLACE: 'legal_hold:place',
    /**
     * Sprint 62.14.0 — releasing a hold, with release evidence. The second half
     * of the separation above: release re-enables destruction on the next run,
     * which is exactly the consequence a placing officer should not be able to
     * unwind alone by default.
     */
    LEGAL_HOLD_RELEASE: 'legal_hold:release',
    /**
     * Sprint 62.14.0 — reading the external regulated-account register and its
     * lifecycle event trails.
     */
    REGULATED_ACCOUNT_READ: 'regulated_account:read',
    /**
     * Sprint 62.14.0 — creating and editing account records and credentials
     * (the mutable half; the event trail is append-only under its own token).
     */
    REGULATED_ACCOUNT_MANAGE: 'regulated_account:manage',
    /**
     * Sprint 62.14.0 — appending lifecycle events (registration, locks,
     * restoration, compromise notification) and corrections. Corrections are new
     * events with a mandatory reason; there is no update or delete of an event
     * anywhere (the parcel-event contract).
     */
    REGULATED_ACCOUNT_EVENT_APPEND: 'regulated_account_event:append',
    /**
     * Sprint 62.14.0 — recording a revocation event. A decision token separated
     * from `:append`: a revocation is the terminal statement that an account's
     * authorization has ended, with grounds — a position the organisation
     * records against itself, not routine event-keeping.
     */
    REGULATED_ACCOUNT_REVOKE: 'regulated_account:revoke',
    /**
     * Sprint 62.19.0 — reading the sector licence register: licences and
     * authorisations as dated objects, the pack-discovered licence kinds, and
     * the obligations computed against each licence's accountable owner.
     *
     * Named for the activity, not an instrument or a sector (the register holds
     * whatever kinds installed packs declare — R-46). The `:read` suffix is
     * load-bearing: the auditor role is derived by `endsWith(':read')`, and
     * "which licences do you hold, in what scope, and when do they lapse" is
     * exactly an auditor's question.
     */
    SECTOR_LICENCE_READ: 'sector_licence:read',
    /**
     * Sprint 62.19.0 — recording and editing licences: kind, authority,
     * reference, scope, dates, status, evidence. A consequential write: the
     * marketing-claim gate decides over these records, so editing a scope or
     * an expiry changes what the gate will refuse. Determining that a licence
     * is REQUIRED is never a platform act behind this or any permission.
     */
    SECTOR_LICENCE_MANAGE: 'sector_licence:manage',
    /**
     * Sprint 62.54.0 — recording the authority's DECISION on a licence,
     * certificate or appraisal application (pending, issued, refused; date,
     * reference, document). Its own token, separated from `:manage` (the
     * `marketing_approval:approve` reasoning): the person who records fee lines
     * and the person who records the outcome are separable, and no fee-line
     * write can reach the decision (AC6). The platform records the decision a
     * person read from the authority; it never derives one from a payment.
     * Not `:read`-suffixed, so the auditor role never receives it by shape.
     */
    SECTOR_LICENCE_DECISION_WRITE: 'sector_licence_decision:write',
    /**
     * Sprint 62.55.0 (UC-VN-109 F-1, F-3; 2026-10-26) — reading fee and charge
     * lines ACROSS the two registers that hold them (licence applications on the
     * Sector Licences register, sender-name certificates on Marketing
     * Communications): the period view, the fee-line exports (per application or
     * certificate and per paid-on month), their download, and the application
     * and certificate pages the period view links to.
     *
     * Its own read rather than `sector_licence:read` or
     * `marketing_communications:read` because the finance function (P-VN-52)
     * needs the lines of both registers and nothing else of either: the marketing
     * read also opens the consent and suppression registers, which hold personal
     * data a person recording payments has no use for. The fee-line and
     * certificate reads still answer to the two register reads as before, so no
     * existing holder loses anything. Named for the record, not an instrument
     * (R-46). `:read` reaches the auditor by suffix — "show me what was paid for
     * each application, and the decision kept apart" is an inspection question.
     * An export changes nothing, so it needs no grant of its own (the 62.50.1
     * register-export precedent). In no `MODULE_TIER_PERMS` bucket, like the
     * `sector_licence:*` tokens: the registers are reached through the pack that
     * declares the fee schedule.
     */
    REGULATORY_FEE_LINE_READ: 'regulatory_fee_line:read',
    /**
     * Sprint 62.55.0 (UC-VN-109 F-2; owner decision 2026-10-26) — recording,
     * editing and deleting fee lines on a licence application and charge lines
     * on a sender-name certificate, and uploading or removing a line's receipt,
     * WITHOUT `sector_licence:manage` (which also edits applications and
     * licences) or `marketing_communications:manage` (which also writes the
     * consent and suppression registers). The two manage grants keep recording
     * lines exactly as before: each line route accepts either its register's
     * manage grant or this one. It reaches no application, certificate or
     * decision field — those stay with `:manage` and
     * `sector_licence_decision:write` — so the person who records a payment
     * cannot record the outcome. The platform records what was paid against the
     * installed schedule; it never prices, decides or files.
     */
    REGULATORY_FEE_LINE_WRITE: 'regulatory_fee_line:write',
    /**
     * Sprint 62.24.0 — reading the credit-information supply register and the
     * consent-evidence entries it rests on: which consent covered which supply
     * to which class of recipient, and whether the consent was reproduced.
     * Named for the activity, not an instrument (R-46); `:read` reaches the
     * auditor by suffix, and "show me the supply and its cover" is an
     * auditor's question. Sits in no tier bucket — the register is reached
     * through the pack that declares its kinds, as `sector_licence:read` is.
     */
    CREDIT_INFORMATION_SUPPLY_READ: 'credit_information_supply:read',
    /**
     * Sprint 62.24.0 — recording supplies on the credit-information supply
     * register. A consequential write: a supply row states whether cover
     * existed. Deciding that a consent is VALID is never a platform act behind
     * this or any permission.
     *
     * It guards the SUPPLY register only. The consent-evidence entry a supply
     * rests on — creating it, amending it, demonstrating its reproducibility —
     * is written on the consent module under `consent_workflow:write`, which is
     * what `consent/routes.ts` checks on every `/evidence*` write (gate E12.5
     * pass 1, F10: this docblock used to say the opposite). Nor does it read a
     * restricted custody handle back in clear; that is `CONSENT_EVIDENCE_UNMASK`.
     */
    CREDIT_INFORMATION_SUPPLY_MANAGE: 'credit_information_supply:manage',
    /**
     * Sprint 62.24.0, gate E12.5 pass 1 F1 — reading a consent-evidence entry's
     * RESTRICTED custody reference unredacted. The 62.7.0 precedent
     * (`PAYMENTS_EVIDENCE_UNMASK`) applied without variation: positive
     * authorization to read a restricted handle must not arrive with a role,
     * so this is the second `CUSTOM_ONLY_PERMISSIONS` entry — held by no seeded
     * role, `platform_admin` included, and absent from `allPermissions`. A
     * tenant that needs it creates a role carrying it and assigns that role to
     * a named person.
     *
     * Until this token existed the grant was `credit_information_supply:manage`,
     * which both T1 roles hold unfiltered — the 62.7.0 property inverted, and a
     * consent-module handle unmasked by a supply-register token while the
     * entry's own recorder (`consent_workflow:write`) could not read it back.
     * `isCustodyUnmaskAuthorized` in `consent-evidence-service.ts` now tests
     * this token and nothing else.
     */
    CONSENT_EVIDENCE_UNMASK: 'consent_evidence:unmask',
    /**
     * Sprint 62.24.0 — reading provider records: notifications, periodic and
     * ad-hoc reports to a pack-declared authority, and the computed window and
     * report states. `:read` reaches the auditor by suffix.
     */
    DATA_SERVICE_PROVIDER_READ: 'data_service_provider:read',
    /**
     * Sprint 62.24.0 — creating provider records and recording their
     * notification and report entries. Deciding that an offering is IN SCOPE is
     * recorded on the offering (`REGULATORY_CLASSIFICATION_WRITE`) as a
     * person's decision, never computed here.
     */
    DATA_SERVICE_PROVIDER_MANAGE: 'data_service_provider:manage',
    // Controlled-testing cases (Sprint 63.24.0, Slice A)
    /**
     * Sprint 63.24.0 — reading a controlled-testing case: its recorded steps, the
     * clocks the installed procedure computes, and its transitional tag. `:read`
     * reaches the auditor by suffix. No permission here decides anything: the
     * authority decides, and the platform records.
     */
    CONTROLLED_TESTING_CASE_READ: 'controlled_testing_case:read',
    /**
     * Sprint 63.24.0 — opening a case, correcting its applicant fields and
     * recording an authority step. Deliberately one grant: there is no approve or
     * determine permission, because the platform never determines.
     */
    CONTROLLED_TESTING_CASE_MANAGE: 'controlled_testing_case:manage',
    // Supervision-information records (Sprint 62.50.0)
    /**
     * Sprint 62.50.0 — reading the supervision-information registers an
     * installed overlay declares through its templates' `register_surface`:
     * shared information received, branch requirements with the publicity and
     * information recorded against them, depositor enquiries, and the reporting
     * calendar, incident, correction, System-start and connection-readiness
     * records. `:read` reaches the auditor by suffix. Named for the activity,
     * never for an instrument (R-46).
     *
     * The six `pcf_*` grants are seeded in `allPermissions`, so the two T1 roles
     * (platform_admin, compliance_owner) hold all six and the auditor holds this
     * one by its `:read` suffix (`pcf-role-mapping-62.50.0.test.ts`). Like the
     * `sector_licence:*` and `data_service_provider:*` peers they sit in no
     * `MODULE_TIER_PERMS` bucket: the registers are reached through the pack that
     * declares their surfaces, so a tier grant would hand them to organisations
     * whose installed content declares nothing.
     */
    PCF_SUPERVISION_READ: 'pcf_supervision:read',
    /**
     * Sprint 62.50.0 — recording what a recipient received through sharing: the
     * scope limb, the purpose, and how a personal-data provision is met, in a
     * person's own words. The platform derives no mechanism and no default.
     */
    PCF_SHARED_INFORMATION_WRITE: 'pcf_shared_information:write',
    /**
     * Sprint 62.50.0 — recording publicity carried out on a branch requirement,
     * channel by channel, and binding its evidence files. Also writes the branch
     * requirement header (any-of with the information-request grant).
     */
    PCF_PUBLICITY_WRITE: 'pcf_publicity:write',
    /**
     * Sprint 62.50.0 — recording information provided on a branch request and
     * binding its evidence files. Also writes the branch requirement header.
     */
    PCF_INFORMATION_REQUEST_WRITE: 'pcf_information_request:write',
    /**
     * Sprint 62.50.0 — logging a depositor enquiry routed to the branch. There is
     * no lookup, result, verification or closing grant: the platform does none
     * of them.
     */
    PCF_DEPOSITOR_ENQUIRY_WRITE: 'pcf_depositor_enquiry:write',
    /**
     * Sprint 62.50.0 — recording the reporting calendar, incidents and
     * resumption, error and query notices with their resend and unlock request,
     * the dates the organisation was told the System operates, and connection
     * readiness. Nothing behind it sends, connects or reports to anyone.
     */
    PCF_SUPERVISION_SYSTEM_WRITE: 'pcf_supervision_system:write',
    // Data-exploitation records (Sprint 62.51.0)
    /**
     * Sprint 62.51.0 — reading the data-exploitation registers an installed pack
     * declares through its templates' `register_surface`: the route
     * determinations, the requests with the authority's grant or refusal, the
     * controlled-testing records, the incident and violation notices, the
     * end-of-purpose dispositions, the incubator records and the
     * foreign-cooperation risk assessments. `:read` reaches the auditor by
     * suffix. Named for the activity, never for an instrument (R-46).
     *
     * Like the `pcf_supervision:*` peers it sits in no `MODULE_TIER_PERMS`
     * bucket: the registers are reached through the pack that declares their
     * surfaces, so a tier grant would hand them to organisations whose installed
     * content declares nothing. Seeded in `allPermissions`, so the two T1 roles
     * hold it (`modules/data-exploitation/__tests__/de-writer-census-62.51.0.test.ts`).
     */
    DATA_EXPLOITATION_READ: 'data_exploitation:read',
    /**
     * Sprint 62.51.0 — recording on every data-exploitation register and binding
     * its evidence files. One grant, deliberately: nothing behind it decides,
     * approves, certifies, submits or connects to anything — a person records
     * what the organisation did and what the authority's document says.
     */
    DATA_EXPLOITATION_MANAGE: 'data_exploitation:manage',
    // Cybersecurity assurance index self-assessment records (Sprint 62.52.0)
    /**
     * Sprint 62.52.0 — opening the cybersecurity assurance index registers an
     * installed pack declares through its templates' `register_surface`: the
     * context, the periods list (the periods the caller is NAMED on) and each
     * register read. A period's own records pass a second gate, membership or
     * `:restricted_read` (owner answer OQ-2). No tier bucket: the registers are
     * reached through the pack that declares their surfaces. Seeded in
     * `allPermissions`, so the two T1 roles hold it and the auditor by suffix.
     */
    CYBERSECURITY_ASSURANCE_INDEX_READ: 'cybersecurity_assurance_index:read',
    /**
     * Sprint 62.52.0 — recording on the registers: opening a period, its systems
     * and members, each item's Mức with its explanation and evidence, the
     * statistics and the supplier requests, and binding the period's evidence.
     * Nothing behind it computes, submits, reports, ranks or certifies anything.
     * A period's records are still reached only by a member or a holder of
     * `:restricted_read`.
     */
    CYBERSECURITY_ASSURANCE_INDEX_WRITE: 'cybersecurity_assurance_index:write',
    /**
     * Sprint 62.52.0 (OQ-2 (a)) — reading EVERY period of the organisation,
     * whether or not the holder is named on it, and downloading its evidence. In
     * `CUSTOM_ONLY_PERMISSIONS`: no seeded role holds it, and it does not end in
     * `:read`, so the auditor's suffix rule never reaches it
     * (`csai-writer-census-62.52.0.test.ts`).
     */
    CYBERSECURITY_ASSURANCE_INDEX_RESTRICTED_READ: 'cybersecurity_assurance_index:restricted_read',
    // Food traceability records (Sprint 62.49.0)
    /**
     * Sprint 62.49.0 — reading the food traceability registers an installed pack
     * declares through its templates' `register_surface`: the establishment
     * record, the certificate register with its versions and equivalence
     * judgements, lot test results with the testing-body statement, inspections,
     * warnings, notices with their quantity reports, violation decisions with
     * their events, correction requests, supplier corrections, the retention
     * statement, connection routes with their receipts, grants and reviews,
     * complaints, and the records-request clock view. `:read` reaches the auditor
     * by suffix. Named for the activity, never for an instrument (R-46).
     *
     * Like the `data_exploitation:*` peers it sits in no `MODULE_TIER_PERMS`
     * bucket: the registers are reached through the pack that declares their
     * surfaces, so a tier grant would hand them to organisations whose installed
     * content declares nothing. Seeded in `allPermissions`, so the two T1 roles
     * hold all four. The food roles of the owner's OD-13 are Sprint 62.49.1.
     */
    FOOD_TRACEABILITY_READ: 'food_traceability:read',
    /**
     * Sprint 62.49.0 — recording on the food traceability registers and binding
     * their evidence files: facts as entered, results as recorded, decisions as
     * the authority stated them, a proposed correction, a supplier's own
     * correction, a receipt as returned. Nothing behind it verifies a
     * registration, judges a result, computes a sanction, submits, notifies an
     * authority or connects to any database: every state a register shows is
     * what a person recorded.
     */
    FOOD_TRACEABILITY_WRITE: 'food_traceability:write',
    /**
     * Sprint 62.49.0 — the second person's approval on a food record: an
     * equivalence judgement, a testing-body statement, an inspection response, a
     * correction request (which then applies the correction under the approver),
     * a food grant and a grant review's decision. Approving a food grant lends;
     * it reassigns nothing (follow-up 2026-10-28, AC23): the approver lends the
     * granted `food_traceability:read` / `:write` — never `:approve` or `:grant`,
     * which `DELEGATION_EXCLUDED_PERMISSIONS` holds back — to the user through a
     * `specific` delegation that runs from approval to the grant's end date, and
     * only permissions the approver holds in their own right. As a delegation it
     * stays in force only while the approver still holds them and is in good
     * standing. The server refuses an approval by the person who entered the
     * record (`FOOD_TRACEABILITY_SELF_APPROVAL`). Not `:read`-suffixed, so the
     * auditor never receives it by shape.
     */
    FOOD_TRACEABILITY_APPROVE: 'food_traceability:approve',
    /**
     * Sprint 62.49.0 (E0 D10, owner answer K-2) — requesting and removing food
     * grants and opening a grant review. A grant names a user, the food
     * permissions it gives (`:read` / `:write` only), an end date and a reason; it
     * takes effect only on a second person's `:approve`, as a delegation from that
     * approver (see `FOOD_TRACEABILITY_APPROVE`), and removing it revokes the
     * delegation (follow-up 2026-10-28, AC23). The tenant's segregation rule pairs this permission
     * with `food_traceability:approve` (configured through `sod-service`, AC24),
     * so one person holding both is flagged. Not `:read`-suffixed: the auditor
     * never receives it by shape.
     */
    FOOD_TRACEABILITY_GRANT: 'food_traceability:grant',
    // Customs tax-administration records (Sprint 62.55.0)
    /**
     * Sprint 62.55.0 — reading the customs tax-administration registers an
     * installed overlay declares through its templates' `register_surface`: the
     * organisation's dossiers and the documents received, payments and anchor
     * events with their due-date readings, duty-free lists with their deductions
     * and usage schedule, refund, debt-instrument and examination records,
     * project transfers, and the per-record transition readings. `:read`
     * reaches the auditor by suffix. Named for the activity, never for an
     * instrument (R-46).
     *
     * Like the `pcf_*` peers (62.50.0), the eight `customs_tax_*` grants are
     * seeded in `allPermissions`, so the two T1 roles hold all eight; the auditor
     * holds this one and the export grant by suffix and no write grant; none sits
     * in a `MODULE_TIER_PERMS` bucket — the registers are reached through the pack
     * that declares their surfaces, and the leaves sit in the existing logistics
     * module (G-110: tier counts do not move). A ninth, `:review` (owner decision
     * O7, 2026-10-26), is seeded the same way and given to the auditor explicitly.
     */
    CUSTOMS_TAX_ADMINISTRATION_READ: 'customs_tax_administration:read',
    /**
     * Sprint 62.55.0 — recording the organisation's own dossiers (procedure,
     * template, customs records, declared channel, sign-off, lodgement date,
     * confirmation answer), the documents the authority sent back with their
     * stated figures and stated deadlines, refund records and debt instruments.
     * Nothing behind it files, lodges or transmits: the channel is one the
     * framework declares and a person records having used.
     */
    CUSTOMS_TAX_DOSSIER_WRITE: 'customs_tax_dossier:write',
    /**
     * Sprint 62.55.0 — recording a payment (a stated amount, the payer, the date
     * on the pack's basis, the receipt), an anchor event against a customs record,
     * and a person's closure of a difference between two stated figures. Nothing
     * behind it pays, calculates or corrects a figure.
     */
    CUSTOMS_TAX_PAYMENT_WRITE: 'customs_tax_payment:write',
    /**
     * Gap A-2 (owner decision, 2026-10-26: narrow grants) — recording a document
     * the customs authority sent (an assessment decision, a payment, debt or
     * enforcement notice, an acknowledgement, a request or a decision on a
     * dossier) with its stated figures and stated deadlines, without
     * `customs_tax_dossier:write`, which opens and edits dossiers and still
     * records received documents too. For the finance function (P-VN-52), which
     * records the notice it pays and holds no dossier write. Seeded in
     * `allPermissions` (the two T1 roles) like its peers; not `:read`-suffixed,
     * so the auditor never receives it by shape; in no `MODULE_TIER_PERMS` bucket.
     */
    CUSTOMS_TAX_RECEIVED_DOCUMENT_WRITE: 'customs_tax_received_document:write',
    /**
     * Sprint 62.55.0 — recording a duty-free list's acknowledgement, parties,
     * amendments and stop-use events, the deductions as customs wrote them, and
     * the usage notifications made. The schedule they close is derived on read.
     */
    CUSTOMS_TAX_DUTY_FREE_LIST_WRITE: 'customs_tax_duty_free_list:write',
    /**
     * Sprint 62.55.0 — recording an examination at the premises and its steps
     * (the organisation's explanation, a refusal to sign, a suspension it asked
     * for), and linking an examination at the customs office to its Authority
     * Request. Every examination clock is the authority's; none is the
     * organisation's item.
     */
    CUSTOMS_TAX_EXAMINATION_WRITE: 'customs_tax_examination:write',
    /**
     * Sprint 62.55.0 — recording a project transfer, assigning the transferee
     * duty and closing it with the new declaration's customs record and date.
     */
    CUSTOMS_TAX_TRANSITION_WRITE: 'customs_tax_transition:write',
    /**
     * Sprint 62.55.0 (AC-E1) — recording a person's determination of the rules
     * that govern a dossier or examination whose date is missing or disputed,
     * with a reason, and an authority's statement beside it. A decision token
     * separated from `:write` (the `marketing_approval:approve` reasoning): the
     * determination sits beside the derived reading, never over it, and the
     * auditor role never receives it by shape.
     */
    CUSTOMS_TAX_TRANSITION_DETERMINE: 'customs_tax_transition:determine',
    /**
     * Sprint 62.55.0 (AC-F2) — exporting the five registers and the transition
     * statement through `UnifiedExportService` (R-33). Counted by
     * `census:read-gated-writes` as an export grant; reaches the auditor by
     * suffix.
     */
    CUSTOMS_TAX_ADMINISTRATION_EXPORT: 'customs_tax_administration:export',
    /**
     * Owner decision O7 (2026-10-26) — the optional review step: approving a
     * dossier, a person's determination of the governing rules or an
     * examination's conclusion as a second person, and withdrawing one's own
     * approval. Nothing requires an approval and nothing waits on one. The
     * service refuses the person who recorded the record, made its latest change
     * or (a determination) determined it, with 403 `CTA_REVIEW_SELF_APPROVAL` —
     * the two-person rule of the AI register's disclosures and interventions.
     *
     * A decision token, not a write: it changes no field of the record, and an
     * edit afterwards leaves the approval standing against the version it was
     * given for. Seeded in `allPermissions` (the two T1 roles) and given to the
     * auditor explicitly — P-VN-10 approves what is handed to the inspection
     * team (UC-VN-96 steps 12–13) — because no suffix filter reaches `:review`.
     * In no `MODULE_TIER_PERMS` bucket, like its eight peers.
     */
    CUSTOMS_TAX_ADMINISTRATION_REVIEW: 'customs_tax_administration:review',
    // Biometric capture components, chain of custody, readiness (Sprint 62.56.0)
    /**
     * Sprint 62.56.0 — reading the biometric capture-component register, the
     * chain-of-custody records and the readiness reviews an installed overlay
     * declares through its templates' `register_surface`. `:read` reaches the
     * auditor by suffix. Named for the activity, never for an instrument (R-46).
     *
     * Like the `pcf_*` peers, the six `biometric_capture*` grants are seeded in
     * `allPermissions` (the two T1 roles hold all six) and sit in no
     * `MODULE_TIER_PERMS` bucket: the registers are reached through the pack that
     * declares their surfaces, so a tier grant would hand them to organisations
     * whose installed content declares nothing.
     */
    BIOMETRIC_CAPTURE_READ: 'biometric_capture:read',
    /**
     * Sprint 62.56.0 — recording a component, its security check, its capture
     * software's conformity check, declaration and registration as a person
     * enters them, its evidence areas and lifecycle, its links to existing
     * records, and a readiness review. Nothing behind it tests, certifies,
     * inspects or registers anything.
     */
    BIOMETRIC_CAPTURE_COMPONENT_WRITE: 'biometric_capture_component:write',
    /**
     * Sprint 62.56.0 — recording a person's scope determination on a component
     * (append-only). A grant of its own: deciding whether a component is in scope
     * is a person's act, separable from recording the component.
     */
    BIOMETRIC_CAPTURE_SCOPE_WRITE: 'biometric_capture_scope:write',
    /**
     * Sprint 62.56.0 — recording a biometric data transfer's sender half and,
     * by a different person, its receiver half.
     */
    BIOMETRIC_CAPTURE_CUSTODY_WRITE: 'biometric_capture_custody:write',
    /**
     * Sprint 62.56.0 round 2 (PU-4) — recording a supervision, guidance,
     * monitoring or inspection act an organisation issued or received, its
     * follow-up, its links to components and its evidence. Nothing behind it
     * states that an act was complied with. In no tier bucket, like the other
     * four writes.
     */
    BIOMETRIC_CAPTURE_SUPERVISION_WRITE: 'biometric_capture_supervision:write',
    /**
     * Sprint 62.56.0 — producing the inspection evidence pack (PDF, DOCX, XLSX)
     * through `UnifiedExportService`. Reaches the auditor through the
     * platform's `:export` suffix rule (`reachesAuditorBySuffix`): an inspection
     * pack is an auditor's artefact before it is anyone else's.
     */
    BIOMETRIC_CAPTURE_EXPORT: 'biometric_capture:export',
    // Marketing-Communications Consent and Suppression (Sprint 62.28.0)
    /**
     * Sprint 62.28.0 — reading the per-channel marketing consent register, the
     * suppression register and the suppression-enforcement checks. Named for the
     * activity, not an instrument (R-46); `:read` reaches the auditor by suffix,
     * and "show me that this person was suppressed and that the suppression was
     * enforced before the send" is an auditor's question before it is anyone
     * else's.
     */
    MARKETING_COMMUNICATIONS_READ: 'marketing_communications:read',
    /**
     * Sprint 62.28.0 — recording a marketing consent entry, a suppression entry
     * and its confirmation, and a suppression-enforcement check.
     *
     * It does NOT lift a suppression: lifting resumes sending to a person who
     * asked it to stop, so it carries its own token below. It does not read a
     * restricted custody handle back in clear either; that is
     * `MARKETING_CONSENT_UNMASK`.
     */
    MARKETING_COMMUNICATIONS_MANAGE: 'marketing_communications:manage',
    /**
     * Sprint 62.28.0 — lifting a standing suppression.
     *
     * Separated from `:manage` on the `logistics_claim:decide` reasoning, and for
     * a sharper reason here: every other write on these registers moves toward
     * not contacting someone, and this one alone moves the other way. A lift is
     * never a delete — the row keeps its history and gains a named lifter, an
     * instant and a reason — but it is still the act that makes a person
     * contactable again, so the person who records a refusal and the person who
     * reverses it are separable, and the auditor role never receives it by shape.
     */
    MARKETING_SUPPRESSION_LIFT: 'marketing_suppression:lift',
    /**
     * Sprint 62.28.0 — reading a marketing consent entry's RESTRICTED custody
     * reference unredacted. The 62.7.0 precedent (`PAYMENTS_EVIDENCE_UNMASK`)
     * and its 62.24.0 repeat (`CONSENT_EVIDENCE_UNMASK`) applied without
     * variation: positive authorization to read a restricted handle must not
     * arrive with a role, so this is a `CUSTOM_ONLY_PERMISSIONS` entry — held by
     * no seeded role, `platform_admin` included, and absent from
     * `allPermissions`. A tenant that needs it creates a role carrying it and
     * assigns that role to a named person.
     */
    MARKETING_CONSENT_UNMASK: 'marketing_consent:unmask',
    /**
     * Sprint 62.19.0 — reading marketing-claim approvals and their gate
     * attempts, refusals included.
     */
    MARKETING_APPROVAL_READ: 'marketing_approval:read',
    /**
     * Sprint 62.19.0 — creating and withdrawing a marketing-claim approval
     * record (the material, the service it claims, the licence kind it needs).
     */
    MARKETING_APPROVAL_MANAGE: 'marketing_approval:manage',
    /**
     * Sprint 62.19.0 — attempting the gate. A decision token separated from
     * `:manage` (the `logistics_claim:decide` reasoning): an approval attaches
     * a licence reference to a public claim, and the register refuses it
     * server-side when no active licence covers the service. Separate so the
     * person who drafts the material and the person who signs it off are
     * separable, and so the auditor role never receives it by shape.
     */
    MARKETING_APPROVAL_APPROVE: 'marketing_approval:approve',
    /**
     * Sprint 62.19.0 — reading investor-verification evidence records: method,
     * outcome, verifier, timestamp, evidence references. Evidence references and
     * outcomes only — no identity values are stored, so reading the register
     * discloses that a verification happened, never who the investor is.
     */
    INVESTOR_VERIFICATION_READ: 'investor_verification:read',
    /**
     * Sprint 62.19.0 — recording and editing verification evidence per account
     * opening. The platform records that a verification was performed; it never
     * performs one and never treats a mechanism as satisfying the duty.
     */
    INVESTOR_VERIFICATION_MANAGE: 'investor_verification:manage',
    /**
     * Sprint 62.19.0 — reading account-data access, export and disclosure
     * justifications. Every read of this register is itself audit-logged — the
     * duty being evidenced is that every access is on the trail.
     */
    ACCOUNT_DATA_DISCLOSURE_READ: 'account_data_disclosure:read',
    /**
     * Sprint 62.19.0 — recording a justification (legal basis, justification
     * text, decision) for an access, export or disclosure of account data.
     */
    ACCOUNT_DATA_DISCLOSURE_MANAGE: 'account_data_disclosure:manage',
    /**
     * Sprint 62.19.0 — reading regulatory and investor disclosures and the
     * offering prospectus: versions, approver, publication evidence.
     */
    REGULATORY_DISCLOSURE_READ: 'regulatory_disclosure:read',
    /**
     * Sprint 62.19.0 — creating a disclosure, adding versions, and publishing
     * an approved one with publication evidence. Publishing is `:manage` rather
     * than a decision token because the decision is the approval below; a
     * publish without an approver is refused server-side regardless of who
     * holds this.
     */
    REGULATORY_DISCLOSURE_MANAGE: 'regulatory_disclosure:manage',
    /**
     * Sprint 62.19.0 — approving a disclosure version as its named approver. A
     * decision token: the approver's name is what the duty asks the record to
     * carry, and a new version clears it. Separate from `:manage` so the person
     * who drafts a disclosure and the person whose name stands behind it are
     * separable; the auditor role never receives it by shape.
     */
    REGULATORY_DISCLOSURE_APPROVE: 'regulatory_disclosure:approve',
    /**
     * Sprint 62.18.0 — reading the issued-record register: the pack-discovered
     * record kinds with their windows and retention statement, the scope
     * decision, records and their issued versions, consents, sharing events and
     * refusals, and the evidence recorded against the record set.
     *
     * Named for the activity, not an instrument or a sector: the register holds
     * whatever kinds installed packs declare (R-46). The `:read` suffix is
     * load-bearing — the auditor role is derived by `endsWith(':read')`, and
     * "which versions were issued, who signed them, and with whom were they
     * shared on what basis" is exactly an auditor's question. Every read of a
     * record or version is itself audit-logged.
     */
    ISSUED_RECORD_READ: 'issued_record:read',
    /**
     * Sprint 62.18.0 — the register's housekeeping writes: the scope decision
     * that precedes use, record creation, consent records and their withdrawal,
     * and evidence entries against the record set. Deliberately NOT the act of
     * issuing a version or sharing a record — those are separate tokens below,
     * so the person who keeps the register and the person whose name stands
     * behind an issuance or a disclosure are separable.
     */
    ISSUED_RECORD_MANAGE: 'issued_record:manage',
    /**
     * Sprint 62.18.0 — issuing, reissuing or correcting a version. A
     * consequential write in the `logistics_claim:decide` tradition: a version
     * is append-only by construction, a reissuance or correction supersedes the
     * prior version with a mandatory reason, and none of it can be undone. The
     * auditor role never receives it by shape.
     */
    ISSUED_RECORD_ISSUE: 'issued_record:issue',
    /**
     * Sprint 62.18.0 — recording a sharing event, which the server refuses
     * unless it cites a pack-named basis or a valid consent (with the
     * representative where the subject is a minor). Separate from `:manage`
     * because sharing discloses the record outside the organisation; the
     * refusal is recorded regardless of who attempted it.
     */
    ISSUED_RECORD_SHARE: 'issued_record:share',
    /**
     * Sprint 62.18.0 — producing a subject's access history or sharing history
     * through the unified export service. `:export` reaches the auditor role by
     * suffix, as every other `:export` does: producing the histories the duty
     * requires is what an inspection asks for.
     *
     * Gate E12.5 pass 1 (RECORD 19) records the DECISION these five share: they
     * are declared here and seeded in `allPermissions`, and they appear in no
     * `MODULE_TIER_PERMS` bucket. That is deliberate and matches the 62.19.0
     * `sector_licence:*` sibling — the register is reached through the pack that
     * declares its kinds, not through a tier bundle, so a tier grant would hand
     * it to organisations whose installed content declares nothing. It is a
     * decision, not an omission; a tier bucket is what a later sprint adds if the
     * register becomes part of a packaged tier.
     */
    ISSUED_RECORD_EXPORT: 'issued_record:export',
    /**
     * Sprint 62.17.0 — reading the publication register: publications with their
     * rights and provenance evidence and their applied categories, the
     * pack-discovered release-gate kinds and windows, the legal-deposit
     * submissions recorded against them, and the release-gate attempt trail with
     * its refusals.
     *
     * Named for the activity, not an instrument or a sector: the register holds
     * whatever gate kinds installed packs declare (R-46). The `:read` suffix is
     * load-bearing — the auditor role is derived by `endsWith(':read')`, and
     * "what did you deposit, when, and on what date did you become entitled to
     * release it" is exactly an auditor's question.
     */
    PUBLICATION_READ: 'publication:read',
    /**
     * Sprint 62.17.0 — the register's writes short of the gate: creating and
     * editing a publication, recording rights and provenance evidence against it,
     * applying or removing a category with the reason the person gives, and
     * recording a legal-deposit submission and its receipt. Deliberately NOT the
     * act of marking a publication releasable — that is the token below, so the
     * person who keeps the register and the person who releases are separable.
     */
    PUBLICATION_MANAGE: 'publication:manage',
    /**
     * Sprint 62.17.0 — attempting the release gate. A decision token in the
     * `logistics_claim:decide` / `marketing_approval:approve` tradition: the
     * server refuses the attempt when the pack-declared window has not run, and
     * records the refusal with who attempted it. It does NOT end in `:read`, so
     * the auditor role never receives it by shape — an auditor who could release
     * would be producing the evidence they audit.
     *
     * It is the gate's token, not an approval step: once the window has run the
     * attempt succeeds with no approver and no second permission, exactly as the
     * instrument states (AC2).
     */
    PUBLICATION_RELEASE: 'publication:release',
    /**
     * Sprint 62.17.0 — reading the appointed-representative register: every
     * entry, superseded ones included, because the superseded entries ARE the
     * register's evidence that the appointment history is intact.
     */
    LEGAL_REPRESENTATIVE_READ: 'legal_representative:read',
    /**
     * Sprint 62.17.0 — appending an entry to the appointed-representative
     * register. Append-only by construction: a change is a new entry with a
     * mandatory reason that supersedes its predecessor by compare-and-set, and
     * nothing behind this token edits or deletes what is already recorded.
     */
    LEGAL_REPRESENTATIVE_MANAGE: 'legal_representative:manage',
    /**
     * Sprint 62.17.0 — reading AI-assisted publication assessments: the AI
     * systems linked from the AI system register, the content source, the
     * editorial responsibility, the output traceability and the named approver.
     */
    PUBLICATION_ASSESSMENT_READ: 'publication_assessment:read',
    /**
     * Sprint 62.17.0 — recording and editing an assessment while it is a draft:
     * linking AI systems, naming the editor responsible, citing the publication's
     * rights evidence. Completing it is the token below.
     */
    PUBLICATION_ASSESSMENT_MANAGE: 'publication_assessment:manage',
    /**
     * Sprint 62.17.0 — completing an assessment as its named human approver. A
     * decision token: the approver's name is what the duty asks the record to
     * carry, and the server refuses a completion without one. Separate from
     * `:manage` so the person who records the assessment and the person whose
     * name stands behind it are separable; the auditor role never receives it by
     * shape.
     *
     * Approving records that a human approved the AI-assisted content. It is
     * never a ruling on lawfulness — the assessment carries no verdict field for
     * one to be stored in (AC7, AC9).
     *
     * The DECISION these eight 62.17.0 tokens share, in the 62.18.0 / 62.19.0
     * tradition: they are declared here and seeded in `allPermissions`, and they
     * appear in no `MODULE_TIER_PERMS` bucket. That is deliberate — the register
     * is reached through the pack that declares its gate kinds, not through a
     * tier bundle, so a tier grant would hand it to organisations whose installed
     * content declares nothing. It is a decision, not an omission.
     */
    PUBLICATION_ASSESSMENT_APPROVE: 'publication_assessment:approve',
    /**
     * Sprint 62.5.0 — the identity, trust service and electronic transaction
     * evidence register.
     *
     * Reading it means reading which regulated identities an organisation holds
     * evidence about, which trust service providers and credentials it relies on,
     * and what it has filed electronically to an authority. That is the auditor's
     * subject matter, so `:read` is deliberate — `seed-roles.ts` grants the
     * auditor role every permission whose value ends in `:read`.
     *
     * Named for the *activity*, not for an instrument or a jurisdiction. The
     * module records evidence under whichever trust regime an installed framework
     * declares (R-46).
     */
    TRUST_EVIDENCE_READ: 'trust_evidence:read',
    /**
     * Sprint 62.5.0 — maintaining identity profiles, attributes, providers,
     * credentials, submissions and agreements, and recording verification events.
     *
     * The ordinary working permission of a trust or identity lead. It creates and
     * amends records and it appends evidence; what it deliberately cannot do is
     * change a credential's standing or file to an authority, both of which are
     * split out below.
     */
    TRUST_EVIDENCE_WRITE: 'trust_evidence:write',
    /**
     * Sprint 62.5.0 — suspending, revoking or expiring a credential.
     *
     * Separate from `:write` because these transitions are the ones a relying
     * party acts on, and two of them are terminal by design (see
     * `TRUST_CREDENTIAL_TRANSITIONS`). Recording that a certificate was revoked
     * is not the same kind of act as correcting its contact details, and an
     * organisation that wants those in different hands can now put them there.
     *
     * As in 62.4.0, this does **not** claim separation of duties by default: the
     * roles that hold `:lifecycle` also hold `:write`. The split exists so a
     * tenant *can* separate them, and the audit record names the actor either way.
     */
    TRUST_EVIDENCE_LIFECYCLE: 'trust_evidence:lifecycle',
    /**
     * Sprint 62.5.0 — moving an electronic submission out of draft, and recording
     * the authority's receipt.
     *
     * The narrowest permission in the family and the only one whose effect is
     * visible outside the organisation. 62.2.4's `:send` precedent: filing to a
     * regulator is a different risk class from describing a filing, and the
     * permission boundary is where that difference is expressed.
     */
    TRUST_EVIDENCE_SUBMIT: 'trust_evidence:submit',
    /**
     * Sprint 62.6.0 — the regulatory artefact registry.
     *
     * Reading it means reading which official forms, lists and internal templates
     * an organisation holds and how each is classified. That is the auditor's
     * subject matter, so `:read` is deliberate — `seed-roles.ts` grants the
     * auditor role every permission whose value ends in `:read`.
     *
     * `:read` does NOT convey access to a restricted artefact. Discoverability is
     * decided per artefact by `resolveArtifactAccess`, and a `restricted` one is
     * absent from every result for a holder of `:read` alone.
     */
    REGULATORY_ARTIFACT_READ: 'regulatory_artifact:read',
    /**
     * Sprint 63.17.0 (Q6) — state why you need an artefact you cannot yet read
     * (`POST /regulatory-artifacts/:id/access-request`). It creates no grant, only
     * a request record, and was gated on `:read` because the requester is by
     * definition the person without custody. A request is still a stored record,
     * so it is its own narrow grant, held by the two T1 roles and the auditor.
     */
    REGULATORY_ARTIFACT_REQUEST_ACCESS: 'regulatory_artifact:request_access',
    /**
     * Sprint 62.6.0 — classifying an artefact: its kind, its tier, its provenance.
     *
     * Consequential because the classification decides both what badge an
     * artefact displays and who may reach it. Marking something `official`
     * asserts an authority published it; marking something `open` that should be
     * `restricted` is how six state-secret registers become ordinary forms.
     */
    REGULATORY_ARTIFACT_CLASSIFY: 'regulatory_artifact:classify',
    /**
     * Sprint 62.6.0 — the custodian permission: granting and revoking access to a
     * restricted artefact.
     *
     * The narrowest in the family and the only one that widens what another person
     * can see. Deliberately NOT held by the auditor role: an auditor who could
     * grant themselves access to the records they audit is not an auditor.
     *
     * Holding it is necessary and not sufficient — a custodian still needs their
     * own grant to *read* a restricted artefact, because "may issue access" and
     * "has access" are different facts and the audit trail must be able to tell
     * them apart.
     */
    REGULATORY_ARTIFACT_CUSTODY: 'regulatory_artifact:custody',
    // ── Sprint 62.7.0 — public data exchange ───────────────────────
    /**
     * Sprint 62.7.0 — the dataset catalogue, its dictionaries, the sharing
     * register and the release history.
     *
     * `:read` is deliberate. Which datasets an organisation stewards, who it
     * shares them with and what it has published is an inspector's and an
     * auditor's subject matter, and `seed-roles.ts` grants the auditor role every
     * permission ending in `:read`.
     */
    PUBLIC_DATA_READ: 'public_data:read',
    /**
     * Sprint 62.7.0 — cataloguing a dataset, authoring and confirming a
     * dictionary version, recording an arrangement, a transfer, a quality issue
     * or a correction.
     */
    PUBLIC_DATA_WRITE: 'public_data:write',
    /**
     * Sprint 62.7.0 — publishing a dataset openly, and taking a publication down.
     *
     * Separated from `:write` because it is the only act in this domain whose
     * effect is outside the organisation and, once taken, cannot be undone by
     * deleting a row: a dataset released openly has been read by people the
     * organisation will never identify. UC-VN-35 failure mode 8 requires that a
     * user without it sees no control and is refused server-side.
     *
     * The permission is necessary and not sufficient. A release also requires a
     * readiness record whose personal-data test was answered and signed off, and
     * no permission substitutes for that.
     */
    PUBLIC_DATA_RELEASE: 'public_data:release',
    // ── Sprint 62.7.0 — payments evidence ──────────────────────────
    /**
     * Sprint 62.7.0 — the payment service inventory, eKYC evidence, diligence
     * cross-links, reconciliations and exceptions.
     *
     * `:read` never conveys the content of a restricted protected reference. The
     * handle is redacted for every holder of `:read` alone; see `:unmask`.
     */
    PAYMENTS_EVIDENCE_READ: 'payments_evidence:read',
    /** Sprint 62.7.0 — recording payment services, verifications and links. */
    PAYMENTS_EVIDENCE_WRITE: 'payments_evidence:write',
    /**
     * Sprint 62.7.0 — signing a retention reconciliation, and resolving an
     * exception.
     *
     * Separate from `:write` because both acts are a person taking ownership of a
     * decision between two duties that genuinely conflict. As in 62.4.0 and
     * 62.5.0 this does not claim separation of duties by default — the roles
     * holding it also hold `:write`. The split exists so a tenant *can* separate
     * them, and the audit record names the actor either way.
     */
    PAYMENTS_EVIDENCE_RECONCILE: 'payments_evidence:reconcile',
    /**
     * Sprint 62.7.0 — reading a restricted protected reference unredacted.
     *
     * **Held by no system role, deliberately.** This is the first entry in
     * `CUSTOM_ONLY_PERMS` (`db/__tests__/permissions-contract.test.ts:39`, whose
     * hook has been empty since it was written), and the reason is UC-VN-34
     * failure mode 8: *"A user with payments permission but without explicit
     * biometric authorization attempts the same — positive authorization must be
     * required, not merely role membership."*
     *
     * A permission that arrives with a role IS role membership. So this one
     * arrives with none — not with `platform_admin`, which takes `allPermissions`
     * wholesale, and not with `compliance_owner`. A tenant that needs it must
     * create a role carrying it and assign that role to a named person, which is
     * a deliberate, auditable act by someone who had to think about it.
     *
     * The consequence is intended: out of the box, **nobody** in a new
     * organisation can see a restricted handle. The interface says so — a
     * redacted reference renders as withheld with the reason, never as an empty
     * field, because "we hold nothing" and "you may not see what we hold" are
     * different compliance positions and an operator must be able to tell them
     * apart.
     */
    PAYMENTS_EVIDENCE_UNMASK: 'payments_evidence:unmask',
    // ── Sprint 62.46.0 — the AML programme register ────────────────
    //
    // Six tokens, all `CUSTOM_ONLY_PERMISSIONS`: held by no seeded role
    // (`platform_admin` and `compliance_owner` included), absent from
    // `allPermissions`, and — for `aml_case:read` — excluded from the auditor's
    // suffix rule by `reachesAuditorBySuffix`. A tenant grants them by creating
    // a role and assigning it to a named person. The spec's decision: "none
    // granted to a role by default except the tenant admin's grant-management
    // right" — which is `role:*`, not these tokens.
    //
    // The reason is the case register's tipping-off property. A suspicious-
    // activity case is the one record in the product whose EXISTENCE must not
    // reach the wrong reader, so reading one must be a deliberate, named grant,
    // exactly as the 62.7.0 unmask precedent requires for a restricted handle.
    // The non-case surfaces (scoping, retention scope, indicator coverage,
    // beneficial owners, launch records) stay on `payments_evidence:read`/`:write`.
    /**
     * Sprint 62.46.0 — reading the suspicious-activity case register. Without it
     * the case list answers 403 and every per-case route answers 404, identical
     * for an existing and an unknown id; every read and every denial is audited.
     *
     * Ends in `:read` and must NOT reach the auditor: `reachesAuditorBySuffix`
     * excludes it by constant, beside `PDPL_EVIDENCE_PACK_READ`.
     */
    AML_CASE_READ: 'aml_case:read',
    /**
     * Sprint 62.46.0 — opening a case, editing its subject and triggers while
     * `open` or `determined`, recording the organisation's own filing reference,
     * and opening a CDD refresh on a linked diligence record.
     */
    AML_CASE_WRITE: 'aml_case:write',
    /**
     * Sprint 62.46.0 — recording the reasonable-grounds determination. The
     * platform never makes, defaults or suggests it; this token is who may
     * record a person's determination.
     */
    AML_CASE_DETERMINE: 'aml_case:determine',
    /**
     * Sprint 62.46.0 — approving a determination, closing a case, turning the
     * two-person policy on or off, and including case narratives in the
     * inspection pack. Under the two-person policy the server refuses an
     * approver who is the determiner, whatever grants they hold.
     */
    AML_CASE_APPROVE: 'aml_case:approve',
    /**
     * Sprint 62.46.0 — approving (freezing) a version of the indicator coverage
     * configuration. Separate from `payments_evidence:write`, which edits the
     * draft rows, so the person who drafts coverage and the person who approves
     * it are separable.
     */
    AML_INDICATOR_CONFIG_APPROVE: 'aml_indicator_config:approve',
    /**
     * Sprint 62.46.0 — the launch gate: recording whether a launch is in scope,
     * deciding a pre-launch assessment, and approving a launch record.
     */
    AML_LAUNCH_GATE_APPROVE: 'aml_launch_gate:approve',
    // ── Sprint 62.7.0 — platform evidence ──────────────────────────
    /**
     * Sprint 62.7.0 — the seller register, transaction and document evidence,
     * reconciliation runs and their exceptions.
     */
    PLATFORM_EVIDENCE_READ: 'platform_evidence:read',
    /** Sprint 62.7.0 — recording sellers, transactions, documents and runs. */
    PLATFORM_EVIDENCE_WRITE: 'platform_evidence:write',
    /**
     * Sprint 62.7.0 — assembling evidence for an authority's question and
     * recording the response.
     *
     * 62.2.4's `:send` and 62.5.0's `:submit` precedent: the only routes in the
     * domain whose effect is visible outside the organisation get their own
     * permission, so a tenant can separate "keeps the records" from "answers the
     * regulator" without separating everything else.
     */
    PLATFORM_EVIDENCE_RESPOND: 'platform_evidence:respond',
    // ── Sprint 62.8.0 — manufacturing operations ───────────────────
    //
    // Five sub-domains, four personas, and the grant boundaries follow the
    // personas rather than the code layout. A quality lead, an EPR lead, an EHS
    // steward and a plant OT lead are four different people in four different
    // reporting lines at every manufacturer this serves; one `manufacturing:read`
    // would have handed the chemical register to whoever keeps the batch records,
    // and 64.0.0's whole finding was permissions granted more broadly than the
    // thing they gate.
    //
    // Three of the seventeen are separated because their effect is a **decision or
    // a statement**, not record-keeping — the 62.2.4 `:send` / 62.5.0 `:submit` /
    // 62.7.0 `:respond` precedent, applied three more times.
    /** Sprint 62.8.0 — the product master, its batches, and its trace ledger. */
    MANUFACTURING_PRODUCT_READ: 'manufacturing_product:read',
    /** Sprint 62.8.0 — creating and amending products and batches. */
    MANUFACTURING_PRODUCT_WRITE: 'manufacturing_product:write',
    /**
     * Sprint 62.8.0 — appending to the trace ledger, including corrections.
     *
     * Named `:append` rather than `:write` because that is what the ledger
     * permits: there is no update and no delete, and a permission called `:write`
     * would imply a capability the module does not have. It is separate from
     * `manufacturing_product:write` because recording a shipment is a shop-floor
     * act performed many times a day, while amending the product master is a setup
     * act — the same person often should not do both.
     */
    MANUFACTURING_TRACE_APPEND: 'manufacturing_trace:append',
    /** Sprint 62.8.0 — conformity records, their evidence and their findings. */
    MANUFACTURING_CONFORMITY_READ: 'manufacturing_conformity:read',
    /** Sprint 62.8.0 — building a conformity file: route, evidence, findings. */
    MANUFACTURING_CONFORMITY_WRITE: 'manufacturing_conformity:write',
    /**
     * Sprint 62.8.0 — declaring that a product conforms.
     *
     * The one act in this domain that is a **legal statement by the organisation**
     * rather than a record about one. Whoever assembles the evidence file and
     * whoever signs the declaration are different roles at every manufacturer that
     * takes the duty seriously, and a tenant must be able to separate them without
     * separating everything else.
     */
    MANUFACTURING_CONFORMITY_DECLARE: 'manufacturing_conformity:declare',
    /** Sprint 62.8.0 — corrective actions and recalls. */
    MANUFACTURING_RECALL_READ: 'manufacturing_recall:read',
    /** Sprint 62.8.0 — raising and progressing a corrective action or recall. */
    MANUFACTURING_RECALL_WRITE: 'manufacturing_recall:write',
    /**
     * Sprint 62.8.0 — approving the closure of a corrective action or recall.
     *
     * AC4 requires closure approval. Approval by the person who raised the action
     * is self-attestation, and 62.2.4 recorded that making it *possible* is the
     * defect even when nobody does it. The service additionally refuses an
     * approver equal to the creator, so holding this permission is necessary and
     * not sufficient.
     */
    MANUFACTURING_RECALL_APPROVE_CLOSURE: 'manufacturing_recall:approve_closure',
    /** Sprint 62.8.0 — producer-responsibility scope, volumes, fulfilment, submissions. */
    MANUFACTURING_EPR_READ: 'manufacturing_epr:read',
    /** Sprint 62.8.0 — recording scope determinations, volumes and fulfilment. */
    MANUFACTURING_EPR_WRITE: 'manufacturing_epr:write',
    /**
     * Sprint 62.8.0 — recording a filing made with the authority.
     *
     * The platform files nothing itself. What this gates is recording that the
     * organisation filed, with the declared figure and its source — the point at
     * which a record starts being read as the organisation's own statement to a
     * regulator.
     */
    MANUFACTURING_EPR_SUBMIT: 'manufacturing_epr:submit',
    /** Sprint 62.8.0 — the substance register and product composition. */
    MANUFACTURING_SUBSTANCE_READ: 'manufacturing_substance:read',
    /** Sprint 62.8.0 — recording substances, limits and composition evidence. */
    MANUFACTURING_SUBSTANCE_WRITE: 'manufacturing_substance:write',
    /** Sprint 62.8.0 — OT asset profiles and remote-access grants. */
    MANUFACTURING_OT_READ: 'manufacturing_ot:read',
    /** Sprint 62.8.0 — recording OT profiles, integrations and grant requests. */
    MANUFACTURING_OT_WRITE: 'manufacturing_ot:write',
    /**
     * Sprint 62.8.0 — approving remote access to an operational-technology asset.
     *
     * Letting a vendor into a system that moves physical machinery is the highest
     * -consequence act in this module. It is separated from `:write` for the same
     * reason closure approval is, and the service refuses an approver equal to the
     * requester.
     */
    MANUFACTURING_OT_APPROVE_REMOTE_ACCESS: 'manufacturing_ot:approve_remote_access',
    // ── Sprint 62.9.0 — shipment, customs, parcel and postal authorisation ──
    //
    // Five registers, split by the reporting line that owns each rather than by
    // the module, on 62.8.0's ground: a single `logistics:read` would hand the
    // delivery-proof register — signature images and doorstep photographs of named
    // people at named addresses — to whoever keeps shipment paperwork.
    //
    // Three are separated because their effect is a **statement to somebody
    // outside the organisation, or a decision about a customer**, not
    // record-keeping. The 62.2.4 `:send` / 62.5.0 `:submit` / 62.7.0 `:respond`
    // precedent again.
    /** Sprint 62.9.0 — shipments, their parties, cargo lines and documents. */
    LOGISTICS_SHIPMENT_READ: 'logistics_shipment:read',
    /** Sprint 62.9.0 — creating and amending shipments and their cargo. */
    LOGISTICS_SHIPMENT_WRITE: 'logistics_shipment:write',
    /** Sprint 62.9.0 — customs artefact evidence and its clearance history. */
    LOGISTICS_CUSTOMS_READ: 'logistics_customs:read',
    /** Sprint 62.9.0 — preparing a customs evidence record. */
    LOGISTICS_CUSTOMS_WRITE: 'logistics_customs:write',
    /**
     * Sprint 62.9.0 — lodging a prepared artefact with an authority.
     *
     * Separate from `:write` because preparing a filing and sending one are
     * different acts with different consequences, and only one of them reaches a
     * customs authority. This is the same split `TRUST_EVIDENCE_SUBMIT` already
     * makes on the `ElectronicSubmission` this record points at.
     *
     * **Holding one without the other is a hole, so the three authority-facing
     * routes require both** — `requireAllPermissions(:write, :lodge)` on lodge,
     * amend and withdraw, with each half withheld separately in the suite.
     *
     * This sentence used to read *"so the **service** requires both"*, and that was
     * true of nothing: **no service in this module reads `session.permissions` at
     * all**, because permission enforcement in this codebase lives in route
     * middleware. Found by the stream that mounted the router, against a docblock
     * I wrote. A comment asserting where a control lives is a claim about the
     * code, and this one named a layer that has never enforced anything.
     */
    LOGISTICS_CUSTOMS_LODGE: 'logistics_customs:lodge',
    /**
     * Sprint 62.9.0 — appending to a customs record's clearance history.
     *
     * Named `:append` rather than `:write` because that is what the ledger
     * permits: no update, no delete. A permission called `:write` would imply a
     * capability the module does not have — the `manufacturing_trace:append`
     * reasoning, and the same reason it is separate from `logistics_customs:write`:
     * recording what the authority did is a daily operations act, preparing the
     * artefact is not.
     */
    LOGISTICS_CLEARANCE_APPEND: 'logistics_clearance:append',
    /** Sprint 62.9.0 — parcels and their append-only delivery timeline. */
    LOGISTICS_PARCEL_READ: 'logistics_parcel:read',
    /** Sprint 62.9.0 — registering parcels. */
    LOGISTICS_PARCEL_WRITE: 'logistics_parcel:write',
    /** Sprint 62.9.0 — appending to a parcel's timeline, including corrections. */
    LOGISTICS_PARCEL_EVENT_APPEND: 'logistics_parcel_event:append',
    /**
     * Sprint 62.9.0 — reading proof of delivery.
     *
     * **Its own permission, and the reason is the content.** A delivery proof
     * holds a signature image or a photograph of a person's doorstep: material
     * about an identified individual at an identified address, captured at a known
     * time. Every other record in this module is commercial paperwork. Folding
     * this into `logistics_parcel:read` would grant the most sensitive register in
     * the module to every dispatcher who needs to see where a parcel is — and the
     * courier persona's first pain point is precisely that this material
     * accumulates with nobody owning it.
     *
     * 64.0.0's `OWNER_TYPE_DOWNLOAD_POLICY` gates retrieval of the bytes on the
     * permission that gated creation, so this grant governs the attachment too.
     */
    LOGISTICS_DELIVERY_PROOF_READ: 'logistics_delivery_proof:read',
    /** Sprint 62.9.0 — capturing proof of delivery and setting its retention. */
    LOGISTICS_DELIVERY_PROOF_WRITE: 'logistics_delivery_proof:write',
    /** Sprint 62.9.0 — claims about a parcel and the evidence for them. */
    LOGISTICS_CLAIM_READ: 'logistics_claim:read',
    /** Sprint 62.9.0 — raising a claim and assembling its evidence. */
    LOGISTICS_CLAIM_WRITE: 'logistics_claim:write',
    /**
     * Sprint 62.9.0 — deciding a claim.
     *
     * Separate because a decision on a loss or damage claim is a position the
     * organisation takes toward a customer, with a liability behind it — not
     * record-keeping. The person who assembles the evidence and the person who
     * decides on it should be separable, which is the whole point of the courier
     * persona's *"outcomes are inconsistent and indefensible"*.
     */
    LOGISTICS_CLAIM_DECIDE: 'logistics_claim:decide',
    /** Sprint 62.9.0 — the organisation's own operating authorisations. */
    LOGISTICS_AUTHORISATION_READ: 'logistics_authorisation:read',
    /** Sprint 62.9.0 — preparing a licence or notification dossier. */
    LOGISTICS_AUTHORISATION_WRITE: 'logistics_authorisation:write',
    /** Sprint 62.9.0 — filing it with the authority. See `:lodge` above. */
    LOGISTICS_AUTHORISATION_SUBMIT: 'logistics_authorisation:submit',
    // ── Sprint 65.1.0 — the compliance work programme (Phase 28) ──
    /**
     * Sprint 65.1.0 — reading the obligation work programme: which duties are
     * running, from which domain fact, on which independently resolved statutory
     * clocks, under which separately recorded recommended cadence, for which
     * accountable party, with what evidence recorded and what still missing.
     *
     * Named for the activity, not an instrument or a jurisdiction: the register
     * holds whatever duties the organisation's installed content declares, and the
     * toolkit row a duty serves is a pack-declared code (R-46).
     *
     * **The `:read` suffix is load-bearing, and it was decided rather than
     * inherited.** `reachesAuditorBySuffix` hands the auditor role every `:read`
     * and `:export` by SHAPE, so choosing this name is choosing to give the
     * auditor the DPO work programme. That is the intended outcome, for three
     * reasons:
     *
     * 1. "Which duties were running, on whose clock, and what evidence exists" is
     *    not adjacent to an auditor's question — it *is* the question. A read-only
     *    auditor who could not see the work programme could not audit the
     *    programme.
     * 2. Every field here is derived from source-owned records. Coverage carries
     *    no manually editable "complete" flag (AC10) and an obligation has no
     *    settable completion — `completion_proof_id` points at a
     *    `ComplianceExecutionProof` that cites the domain record. So there is
     *    nothing on this surface an implementer could have curated for the
     *    auditor's benefit, which is what makes granting the read safe rather than
     *    merely convenient.
     * 3. It is not the `pdpl_evidence_pack:read` case. That token is the ONE
     *    `:read` excluded from the suffix rule (Sprint 64.35.0) because it gates
     *    retrieval of assembled regulator-facing filing packages. The work
     *    programme is an internal management view; it assembles no package
     *    addressed to an authority, and `:export` below produces a completeness
     *    artefact for the organisation's own use, not a filing.
     *
     * If a later slice mounts a genuinely regulator-facing package behind this
     * module, it takes its own token and its own exclusion — it does not widen
     * this one.
     */
    COMPLIANCE_OBLIGATION_READ: 'compliance_obligation:read',
    /**
     * Sprint 65.1.0 — the coordination layer's own writes: administering trigger
     * bindings (which fact starts which duty, against which selector, with which
     * prerequisites and which recommended cadence) and recording the accountable
     * party, the responsible actors and the DPO's oversight role on an obligation.
     *
     * Deliberately **not** the act of completing anything. There is no completion
     * this token could grant: the domain services stay authoritative for
     * completion (parent contract AC4), `closed` is reachable only with a recorded
     * `ComplianceExecutionProof`, and the coordinator mounts no domain action at
     * all in the foundation.
     *
     * Excluded from the auditor role by the suffix rule, and the exclusion is the
     * point: an auditor who could re-point a binding, change which clock a duty
     * resolves against, or reassign who is accountable for it would be authoring
     * the programme they audit — the `publication:release` and
     * `issued_record:issue` reasoning.
     */
    COMPLIANCE_OBLIGATION_MANAGE: 'compliance_obligation:manage',
    /**
     * Sprint 65.1.0 — producing the work-programme and completeness packages
     * through `UnifiedExportService`.
     *
     * `:export` reaches the auditor role by suffix, as every other `:export` does,
     * and for the same reason `:read` does above: the completeness package is the
     * evidence an internal audit of the programme is conducted from, and it is
     * computed from source-owned records rather than declared. Every export writes
     * `COMPLIANCE_OBLIGATION_EXPORTED` to the trail, so the auditor's own
     * retrieval is itself on the record.
     *
     * Like the 62.18.0 `issued_record:*` siblings and the 62.19.0
     * `sector_licence:*` ones, these three are declared here and seeded in
     * `allPermissions`, and appear in no `MODULE_TIER_PERMS` bucket. That is a
     * decision, not an omission: the work programme is reached through the content
     * an organisation installed, and a tier grant would hand it to organisations
     * whose installed content declares no duties for it to hold. A tier bucket is
     * what a later slice adds if the programme becomes part of a packaged tier.
     */
    COMPLIANCE_OBLIGATION_EXPORT: 'compliance_obligation:export',
    // ── Sprint 65.6.0 — DT-01 training and awareness ──
    /**
     * Reading the course catalogue, the assignment register and the completion
     * evidence recorded against each assignee.
     *
     * `:read` reaches the auditor role by suffix, and that is decided rather than
     * inherited. "Who was required to take this training, who completed it, and
     * what evidences each completion" is the auditor's own question — it is the
     * question the `training_awareness` readiness component answers, and before
     * this sprint that component was a random number in the 70–95 band. Every
     * field on the surface is derived: an assignment carries no manually settable
     * "done" flag, `completed` is unreachable without a persisted
     * `TrainingCompletionEvidence`, and an exemption carries its decider and its
     * reason. So there is nothing here an implementer could curate for the
     * auditor's benefit.
     */
    TRAINING_READ: 'training:read',
    /**
     * The consequential writes: defining a course, assigning it to named people,
     * recording a completion against the evidence that supports it, exempting a
     * person with an attributed reason, and cancelling an assignment.
     *
     * One `:manage` token rather than four, because every one of these acts is
     * "the person who runs the training programme changed the programme" and there
     * is no second authority in the product that would hold one and not the
     * others. Recording a completion is deliberately inside it and not a separate
     * self-service token: this sprint records that training happened and what
     * evidences it; it does not deliver training, and there is no trainee-facing
     * surface for a narrower grant to protect (`Out of scope`, sprint spec).
     *
     * Excluded from the auditor role by the suffix rule, and the exclusion is the
     * point — an auditor who could record a completion would be producing the
     * evidence they audit. Same argument as `publication:release` and
     * `compliance_obligation:manage`.
     */
    TRAINING_MANAGE: 'training:manage',
    // ── Sprint 65.6.0 (AC6) — DPO-01 appointment and PUB-01 public notice ──
    /**
     * Reading the appointment register: who is appointed, on whose decision, from
     * when, and whether the appointment has been made public.
     *
     * `:read` reaches the auditor role by suffix, and that is decided rather than
     * inherited. "Who was the data-protection officer on the day of the incident,
     * and where did you say so publicly" is the auditor's own question, and every
     * field that answers it is either operator-attested with its author recorded
     * (`appointing_decision`, `takes_effect`) or a transition this module wrote.
     * There is nothing on the surface an implementer could curate for the
     * auditor's benefit.
     */
    DPO_APPOINTMENT_READ: 'dpo_appointment:read',
    /**
     * The consequential writes: recording an appointment and the decision behind
     * it, bringing it into effect, publishing it, and ending or superseding it.
     *
     * One `:manage` token rather than four. Every one of those acts is "the
     * organisation changed who holds the post, or what it has said about that
     * publicly", and there is no second authority in this product that would hold
     * one and not the others. Excluded from the auditor role by the suffix rule,
     * and the exclusion is the point: an auditor who could record an appointment
     * would be producing the evidence they audit.
     */
    DPO_APPOINTMENT_MANAGE: 'dpo_appointment:manage',
    /**
     * Reading the notice register: which version is live, who approved it, where
     * it is published, and what was taken down when.
     *
     * Auditor-reachable by suffix for the same reason as the appointment register.
     * A public notice is the one compliance artefact whose subjects can read it
     * themselves, so the register is a statement about something already outside
     * the organisation — withholding it from an auditor would protect nothing.
     */
    PRIVACY_NOTICE_READ: 'privacy_notice:read',
    /**
     * Drafting a notice, approving it, publishing it to a stated target,
     * withdrawing it with a reason, and superseding it.
     *
     * Approval and publication are two acts on the record and one token here,
     * deliberately: they are separated by the **transition table**, which refuses
     * `draft → published` outright, rather than by two grants that an organisation
     * would in practice give to the same person. A separate `:approve` token
     * would look like a segregation of duties this product does not enforce.
     */
    PRIVACY_NOTICE_MANAGE: 'privacy_notice:manage',
    // Processing-service certificate register (Sprint 62.57.0 Slice E, work file D65)
    /**
     * Sprint 62.57.0 Slice E — reading the processing-service certificate
     * register an installed framework declares: the certificates with their
     * documents, revocations and returns, the applications with their parts,
     * receipts, governing text, periods, evaluation and decision, the authority's
     * communications and the history. `:read` reaches the auditor by suffix.
     * Named for the activity, never for an instrument (R-46).
     *
     * Like the `sector_licence:*` peers it sits in no `MODULE_TIER_PERMS` bucket
     * (D44): the register is reached through the framework that declares it and
     * the organisation's recorded scope, so a tier grant would hand it to
     * organisations whose installed content declares nothing. Seeded in
     * `allPermissions`, so the two T1 roles hold all three.
     */
    SERVICE_CERTIFICATE_READ: 'service_certificate:read',
    /**
     * Sprint 62.57.0 Slice E — recording on the register: a certificate held,
     * its documents, the authority's revocation, the return and a publication
     * seen; an application, its parts, condition links, filing, receipts,
     * communications, deficiency notices, supplements, evaluation, decision and
     * withdrawal. One grant: each is the organisation's record of what it or the
     * authority did, and nothing behind it files, decides or certifies anything
     * (UC-VN-118 has no approval step on them). Not `:read`-suffixed, so the
     * auditor never receives it by shape. The scope statement keeps
     * `platform:security:*` (the Organization profile).
     */
    SERVICE_CERTIFICATE_WRITE: 'service_certificate:write',
    /**
     * Sprint 62.57.0 Slice E (D66) — exporting the register and one
     * application's history through `UnifiedExportService` (R-33), PDF and XLSX.
     * Reaches the auditor by suffix.
     */
    SERVICE_CERTIFICATE_EXPORT: 'service_certificate:export',
};
/**
 * System role definitions — exactly the seven roles `seed-roles.ts` creates.
 *
 * Sprint 63.17.0 (owner question Q5, idea-catalog #84 item 1). This used to
 * declare `support`, `contributor` and `viewer`, which the seed never created,
 * and to omit `approver`, `partner_external_counsel` and `processor_liaison`,
 * which it always did: two registries disagreeing about what a system role is.
 * The three declared-only roles are now custom-role templates
 * (`src/backend/src/db/seed-data/custom-role-templates/`), and
 * `role-matrix-parity.test.ts` asserts the two registries are EQUAL.
 */
export const SystemRole = {
    PLATFORM_ADMIN: 'platform_admin',
    COMPLIANCE_OWNER: 'compliance_owner',
    CASE_MANAGER: 'case_manager',
    AUDITOR: 'auditor',
    APPROVER: 'approver',
    PARTNER_EXTERNAL_COUNSEL: 'partner_external_counsel',
    PROCESSOR_LIAISON: 'processor_liaison',
};
/** All data-mapping read permissions (shared across Viewer+) */
const DM_READ_PERMS = [
    Permission.DM_SYSTEM_READ,
    Permission.DM_CATEGORY_READ,
    Permission.DM_PURPOSE_READ,
    Permission.DM_RETENTION_READ,
    Permission.DM_FLOW_READ,
    Permission.DM_ACTIVITY_READ,
    Permission.DM_ROPA_READ,
    Permission.DM_EVIDENCE_READ,
    Permission.DM_ATTESTATION_READ,
    Permission.DM_OWNERSHIP_READ,
    Permission.DM_RISK_ASSESSMENT_READ,
    Permission.DM_REVIEW_READ,
    Permission.DM_INVENTORY_READ,
    Permission.DM_HISTORY_READ,
    // Sprint 45.4.0 — Data Registry read permission shared across Viewer+.
    Permission.DM_REGISTRY_READ,
];
/** Contributor-level write permissions (create/edit, no archive/delete/export) */
const DM_CONTRIBUTOR_PERMS = [
    ...DM_READ_PERMS,
    Permission.DM_SYSTEM_WRITE,
    Permission.DM_FLOW_WRITE,
    Permission.DM_ACTIVITY_WRITE,
    Permission.DM_EVIDENCE_WRITE,
    Permission.DM_EVIDENCE_LINK,
    // Sprint 45.4.0 — contributors (data_custodian-tier) may write + export
    // their org's Data Registry items. Archive + form import are manager-tier.
    Permission.DM_REGISTRY_WRITE,
    Permission.DM_REGISTRY_EXPORT,
];
/** Privacy Manager-level permissions (full CRUD + CSV export + archive) */
const DM_MANAGER_PERMS = [
    ...DM_CONTRIBUTOR_PERMS,
    Permission.DM_CATEGORY_WRITE,
    Permission.DM_PURPOSE_WRITE,
    Permission.DM_RETENTION_WRITE,
    // Sprint 63.31.0 (#410, OD-C) — held with retention write.
    Permission.ATTACHMENT_RETENTION_RELEASE,
    Permission.DM_SYSTEM_ARCHIVE,
    Permission.DM_FLOW_ARCHIVE,
    Permission.DM_ACTIVITY_ARCHIVE,
    Permission.DM_ROPA_GENERATE,
    Permission.DM_EVIDENCE_UNLINK,
    Permission.DM_EXPORT_CSV,
    Permission.DM_AUDIT_READ,
    Permission.DM_ATTESTATION_WRITE,
    Permission.DM_OWNERSHIP_WRITE,
    Permission.DM_RISK_ASSESSMENT_WRITE,
    Permission.DM_RISK_ASSESSMENT_APPROVE,
    Permission.DM_ATTESTATION_EXPORT,
    Permission.DM_REVIEW_WRITE,
    Permission.DM_CATEGORY_APPROVE,
    // Sprint 45.4.0 — manager-tier Registry ops (archive + form-driven import).
    Permission.DM_REGISTRY_ARCHIVE,
    Permission.DM_REGISTRY_IMPORT_FORM,
];
/** Owner/Admin-level permissions (everything including PDF export + defaults) */
const DM_ADMIN_PERMS = [
    ...DM_MANAGER_PERMS,
    Permission.DM_EVIDENCE_DELETE,
    Permission.DM_EXPORT_PDF,
    // Sprint 44.3.1 Epic C — DM_DEFAULTS_MANAGE removed (deleted perm).
];
/** All governance read permissions (shared across Viewer+) */
const GOV_READ_PERMS = [
    Permission.GOV_VENDOR_READ,
    Permission.GOV_SUBPROCESSOR_READ,
    Permission.GOV_TRANSFER_READ,
    Permission.GOV_CROSSBORDER_READ,
    Permission.GOV_DRIFT_READ,
    Permission.GOV_SCORE_READ,
    Permission.GOV_EVIDENCE_READ,
    Permission.GOV_ONBOARDING_READ,
    Permission.GOV_DPA_REVIEW_READ,
    Permission.GOV_PERIODIC_REVIEW_READ,
    Permission.GOV_OFFBOARDING_READ,
    // Sprint 41.5.0 G1 — Cross-Border Filing read goes to every read tier
    // (DPO, auditor, CEO) so the filings list is visible to all governance
    // viewers. Write/submit/archive land in MANAGER and ADMIN below.
    Permission.GOV_CROSS_BORDER_FILING_READ,
    // Sprint 41.5.0 G10 persona fix — PDF export is a read operation (auditors
    // need it for UC-VN-09 evidence bundle export). Moved from ADMIN to READ.
    Permission.GOV_EXPORT_PDF,
];
/** Contributor-level governance: read + execute assigned steps + upload evidence */
const GOV_CONTRIBUTOR_PERMS = [
    ...GOV_READ_PERMS,
    Permission.GOV_EXECUTION_STEP,
    Permission.GOV_EVIDENCE_WRITE,
    Permission.GOV_EVIDENCE_LINK,
];
/** Privacy Manager-level governance: full CRUD + workflows + CSV export */
const GOV_MANAGER_PERMS = [
    ...GOV_CONTRIBUTOR_PERMS,
    Permission.GOV_VENDOR_WRITE,
    Permission.GOV_VENDOR_OFFBOARD,
    Permission.GOV_SUBPROCESSOR_WRITE,
    Permission.GOV_TRANSFER_WRITE,
    Permission.GOV_DRIFT_MANAGE,
    Permission.GOV_DRIFT_ACCEPT_RISK,
    Permission.GOV_SCORE_WRITE,
    Permission.GOV_PLAYBOOK_READ,
    Permission.GOV_PLAYBOOK_WRITE,
    Permission.GOV_EXECUTION_READ,
    Permission.GOV_EXECUTION_INITIATE,
    Permission.GOV_APPROVAL_READ,
    Permission.GOV_AUDIT_READ,
    Permission.GOV_EXPORT_CSV,
    Permission.GOV_ONBOARDING_WRITE,
    Permission.GOV_DPA_REVIEW_WRITE,
    Permission.GOV_PERIODIC_REVIEW_WRITE,
    Permission.GOV_OFFBOARDING_WRITE,
    // Sprint 41.5.0 G1 — Cross-Border Filing manager-tier verbs
    Permission.GOV_CROSS_BORDER_FILING_WRITE,
    Permission.GOV_CROSS_BORDER_FILING_SUBMIT,
];
/** Owner/Admin governance: everything + approvals + PDF export + delete */
const GOV_ADMIN_PERMS = [
    ...GOV_MANAGER_PERMS,
    Permission.GOV_VENDOR_APPROVE,
    Permission.GOV_PLAYBOOK_DELETE,
    Permission.GOV_APPROVAL_DECIDE,
    Permission.GOV_EVIDENCE_DELETE,
    Permission.GOV_EXPORT_EXECUTE,
    // Sprint 41.5.0 G1 — Archive is admin-only (DPO + admin can archive
    // a filing once it's terminal-acknowledged)
    Permission.GOV_CROSS_BORDER_FILING_ARCHIVE,
];
/** Consent read permissions (shared across Auditor+) */
const CONSENT_READ_PERMS = [
    Permission.CONSENT_POLICY_READ,
    Permission.CONSENT_WORKFLOW_READ,
    Permission.CONSENT_CMP_SNAPSHOT_READ,
    Permission.CONSENT_PROPAGATION_READ,
    Permission.CONSENT_RECONCILIATION_READ,
    Permission.CONSENT_SYNC_READ,
];
/** Consent manager-level: read + write policies and workflows + export */
const CONSENT_MANAGER_PERMS = [
    ...CONSENT_READ_PERMS,
    Permission.CONSENT_POLICY_WRITE,
    Permission.CONSENT_WORKFLOW_WRITE,
    Permission.CONSENT_EXPORT_EXECUTE,
    Permission.CONSENT_PROPAGATION_WRITE,
    Permission.CONSENT_RECONCILIATION_WRITE,
    Permission.CONSENT_SYNC_WRITE,
];
/** Consent admin: everything including approve, delete, CMP snapshot write, and import */
const CONSENT_ADMIN_PERMS = [
    ...CONSENT_MANAGER_PERMS,
    Permission.CONSENT_POLICY_APPROVE,
    Permission.CONSENT_POLICY_DELETE,
    Permission.CONSENT_CMP_SNAPSHOT_WRITE,
    Permission.CONSENT_WORKFLOW_IMPORT,
];
/** Assessment read permissions (Sprint 1.5.0) */
const ASSESS_READ_PERMS = [
    Permission.DPIA_READ,
    Permission.TRANSFER_ASSESSMENT_READ,
];
/** Assessment manager: read + write + export */
const ASSESS_MANAGER_PERMS = [
    ...ASSESS_READ_PERMS,
    Permission.DPIA_WRITE,
    Permission.TRANSFER_ASSESSMENT_WRITE,
    // Sprint 44.3.1 Epic C — ASSESSMENT_EXPORT_EXECUTE removed (deleted perm).
];
/** Assessment admin: everything including approve + delete */
const ASSESS_ADMIN_PERMS = [
    ...ASSESS_MANAGER_PERMS,
    Permission.DPIA_APPROVE,
    Permission.DPIA_DELETE,
    Permission.TRANSFER_ASSESSMENT_APPROVE,
    Permission.TRANSFER_ASSESSMENT_DELETE,
    Permission.ASSESSMENT_TRIGGER_CONFIGURE,
    // fix-sprint-20260619-0053-followup-2 #10: PDPL evidence-pack transmission
    // is org_admin/DPO-scoped by default. Broader compliance staff can still
    // draft + edit + upload proof via the unchanged DPIA_WRITE surface.
    Permission.PDPL_EVIDENCE_PACK_SEND,
];
/** Classification read permissions (Sprint 1.6.0 + 20.8.0) */
const CLASS_READ_PERMS = [
    Permission.TAXONOMY_READ,
    Permission.CLASSIFICATION_READ,
    Permission.LIFECYCLE_READ,
    Permission.GAP_REPORT_READ,
    // Sprint 20.8.0: maturity read
    Permission.CLASSIFICATION_REVIEW_READ,
    Permission.CLASSIFICATION_EXCEPTION_READ,
    Permission.CLASSIFICATION_DISCOVERY_READ,
];
/** Classification manager: read + write + review + export */
const CLASS_MANAGER_PERMS = [
    ...CLASS_READ_PERMS,
    Permission.TAXONOMY_WRITE,
    Permission.CLASSIFICATION_WRITE,
    Permission.CLASSIFICATION_REVIEW,
    Permission.LIFECYCLE_WRITE,
    Permission.GAP_REPORT_MANAGE,
    Permission.CLASSIFICATION_EXPORT_EXECUTE,
    // Sprint 20.8.0: maturity write
    Permission.CLASSIFICATION_REVIEW_WRITE,
    Permission.CLASSIFICATION_EXCEPTION_WRITE,
    Permission.CLASSIFICATION_DISCOVERY_WRITE,
];
/** Classification admin: everything including taxonomy delete + lifecycle hold */
const CLASS_ADMIN_PERMS = [
    ...CLASS_MANAGER_PERMS,
    Permission.TAXONOMY_DELETE,
    Permission.LIFECYCLE_HOLD,
];
/** Monitoring read permissions (Sprint 1.7.0) */
const MON_READ_PERMS = [
    Permission.PROGRAM_READ,
    Permission.REVIEW_TASK_READ,
    Permission.TASK_TEMPLATE_READ,
    Permission.KPI_READ,
    Permission.RIC_READ,
    // Sprint 21.3.0: Monitoring Programs Maturity
    Permission.MONITORING_FRAMEWORK_LINK_READ,
    Permission.MONITORING_AUTHORITY_READINESS_READ,
    Permission.MONITORING_CHANGE_REVIEW_READ,
    // Sprint 27.7.1: Monitoring Operational Completeness
    Permission.MONITORING_RACI_READ,
];
/** Monitoring manager: read + write programs/tasks/templates + export */
const MON_MANAGER_PERMS = [
    ...MON_READ_PERMS,
    Permission.PROGRAM_WRITE,
    Permission.REVIEW_TASK_WRITE,
    Permission.REVIEW_TASK_ASSIGN,
    Permission.TASK_TEMPLATE_WRITE,
    Permission.KPI_WRITE,
    Permission.RIC_WRITE,
    // Sprint 44.3.1 Epic C — PROGRAM_EXPORT_EXECUTE removed (deleted perm;
    // superseded by monitoring_export:execute below).
    // Sprint 21.3.0: Monitoring Programs Maturity
    Permission.MONITORING_FRAMEWORK_LINK_WRITE,
    Permission.MONITORING_AUTHORITY_READINESS_WRITE,
    Permission.MONITORING_CHANGE_REVIEW_WRITE,
    // Sprint 27.7.1: Monitoring Operational Completeness
    Permission.MONITORING_EXPORT,
    Permission.MONITORING_RACI_WRITE,
];
/** Monitoring admin: everything including program manage */
const MON_ADMIN_PERMS = [...MON_MANAGER_PERMS, Permission.PROGRAM_MANAGE];
/** Incident read permissions (Sprint 1.8.0) */
const INC_READ_PERMS = [
    Permission.INCIDENT_READ,
    Permission.INCIDENT_TASK_READ,
];
/** Incident responder: read + write incidents/tasks */
const INC_RESPONDER_PERMS = [
    ...INC_READ_PERMS,
    Permission.INCIDENT_WRITE,
    Permission.INCIDENT_TASK_WRITE,
];
/** Incident manager: responder + triage + assess + assign + decisions + export */
const INC_MANAGER_PERMS = [
    ...INC_RESPONDER_PERMS,
    Permission.INCIDENT_TRIAGE,
    Permission.INCIDENT_ASSESS,
    Permission.INCIDENT_TASK_ASSIGN,
    Permission.INCIDENT_DECISION_WRITE,
    Permission.INCIDENT_EXPORT_EXECUTE,
];
/** Incident admin: everything including close + notification manage */
const INC_ADMIN_PERMS = [
    ...INC_MANAGER_PERMS,
    Permission.INCIDENT_CLOSE,
    Permission.INCIDENT_NOTIFICATION_MANAGE,
];
/** Connector read permissions (Sprint 2.0.0) */
const CONN_READ_PERMS = [
    Permission.CONNECTOR_READ,
    Permission.EVENT_BUS_READ,
];
/** Connector manager: read + test + credential read */
const CONN_MANAGER_PERMS = [
    ...CONN_READ_PERMS,
    Permission.CONNECTOR_TEST,
    Permission.CREDENTIAL_READ,
];
/** Connector admin: everything including write, manage, credential write, SSO, storage */
const CONN_ADMIN_PERMS = [
    ...CONN_MANAGER_PERMS,
    Permission.CONNECTOR_WRITE,
    Permission.CONNECTOR_MANAGE,
    Permission.CREDENTIAL_WRITE,
    Permission.EVENT_BUS_MANAGE,
    Permission.IDENTITY_SSO_CONFIGURE,
    Permission.STORAGE_CONNECTOR_CONFIGURE,
];
/** Vendor risk read permissions (Sprint 2.1.0) */
const VR_READ_PERMS = [
    Permission.VENDOR_RISK_CONFIG_READ,
    Permission.VENDOR_RISK_SCORE_READ,
    Permission.VENDOR_QUESTIONNAIRE_TEMPLATE_READ,
    Permission.VENDOR_QUESTIONNAIRE_READ,
    Permission.VENDOR_CONTRACT_READ,
    Permission.VENDOR_REVIEW_READ,
    Permission.VENDOR_RISK_SIGNAL_READ,
    Permission.VENDOR_RISK_REASSESSMENT_READ,
    Permission.VENDOR_RISK_ACCEPTANCE_READ,
    Permission.VENDOR_RISK_PORTFOLIO_READ,
    Permission.VENDOR_RISK_REVIEW_ROUTING_READ,
    Permission.VENDOR_ONBOARDING_CASE_READ,
    Permission.VENDOR_ONBOARDING_APPROVAL_READ,
];
/** Vendor risk manager: read + score + questionnaire write + contract write + review + export */
const VR_MANAGER_PERMS = [
    ...VR_READ_PERMS,
    Permission.VENDOR_RISK_SCORE_WRITE,
    Permission.VENDOR_QUESTIONNAIRE_WRITE,
    Permission.VENDOR_CONTRACT_WRITE,
    Permission.VENDOR_GATE_EVALUATE,
    Permission.VENDOR_REVIEW_WRITE,
    Permission.VENDOR_RISK_EXPORT_EXECUTE,
    Permission.VENDOR_RISK_SIGNAL_WRITE,
    Permission.VENDOR_RISK_REASSESSMENT_WRITE,
    Permission.VENDOR_RISK_ACCEPTANCE_WRITE,
    Permission.VENDOR_RISK_REVIEW_ROUTING_WRITE,
    Permission.VENDOR_ONBOARDING_CASE_WRITE,
    Permission.VENDOR_ONBOARDING_CASE_EXPORT,
    Permission.VENDOR_ONBOARDING_APPROVAL_WRITE,
];
/** Vendor risk admin: everything including config write, template write, questionnaire review, gate override */
const VR_ADMIN_PERMS = [
    ...VR_MANAGER_PERMS,
    Permission.VENDOR_RISK_CONFIG_WRITE,
    Permission.VENDOR_QUESTIONNAIRE_TEMPLATE_WRITE,
    Permission.VENDOR_QUESTIONNAIRE_REVIEW,
    Permission.VENDOR_GATE_OVERRIDE,
];
/**
 * Training read permissions (Sprint 65.6.0 — DT-01).
 *
 * Declared as tier arrays rather than only as tokens because DT-01 is a
 * customer-facing module with its own page and its own nav leaf, which is what
 * `compliance_obligation:*` is not (that one is reached through installed
 * content and deliberately has no tier bucket). The T1 roles take both tokens
 * through the derived matrix, the auditor takes `:read` through the suffix
 * filter, and `TRAIN_ADMIN_PERMS` is what a packaged tier grants.
 */
const TRAIN_READ_PERMS = [Permission.TRAINING_READ];
/** Training manager: read + the whole assignment lifecycle. */
const TRAIN_MANAGER_PERMS = [
    ...TRAIN_READ_PERMS,
    Permission.TRAINING_MANAGE,
];
/**
 * Training admin.
 *
 * Identical to the manager set today, and that is a measurement rather than an
 * oversight: the module declares two tokens, and inventing a third so the admin
 * bucket could differ would be a permission with no act behind it. If a later
 * slice adds a genuinely administrative act — retiring a course other people's
 * assignments point at, say — it takes its own token and lands here.
 */
const TRAIN_ADMIN_PERMS = [...TRAIN_MANAGER_PERMS];
/** Deletion orchestration read permissions (Sprint 2.2.0) */
const DELORCH_READ_PERMS = [
    Permission.DELETION_ORCHESTRATION_READ,
    Permission.DELETION_TEMPLATE_READ,
    Permission.DELETION_VENDOR_REQUEST_READ,
    Permission.DELETION_PROOF_READ,
];
/** Deletion orchestration manager: read + write + execute + escalate + report */
const DELORCH_MANAGER_PERMS = [
    ...DELORCH_READ_PERMS,
    Permission.DELETION_ORCHESTRATION_WRITE,
    Permission.DELETION_ORCHESTRATION_EXECUTE,
    Permission.DELETION_TEMPLATE_WRITE,
    Permission.DELETION_VENDOR_REQUEST_WRITE,
    Permission.DELETION_VENDOR_REQUEST_ESCALATE,
    Permission.DELETION_REPORT_GENERATE,
];
/** Deletion orchestration admin: everything including verify, cancel, proof configure */
const DELORCH_ADMIN_PERMS = [
    ...DELORCH_MANAGER_PERMS,
    Permission.DELETION_ORCHESTRATION_VERIFY,
    Permission.DELETION_ORCHESTRATION_CANCEL,
    Permission.DELETION_PROOF_CONFIGURE,
];
/** Discovery read permissions (Sprint 2.3.0) */
const DISC_READ_PERMS = [
    Permission.DISCOVERY_READ,
    Permission.CLASSIFICATION_SUGGESTION_READ,
    Permission.CLASSIFICATION_RULE_READ,
];
/** Discovery manager: read + write + promote + suggestion review + rule write + export */
const DISC_MANAGER_PERMS = [
    ...DISC_READ_PERMS,
    Permission.DISCOVERY_WRITE,
    Permission.DISCOVERY_PROMOTE,
    Permission.CLASSIFICATION_SUGGESTION_REVIEW,
    Permission.CLASSIFICATION_RULE_WRITE,
    Permission.DISCOVERY_EXPORT_EXECUTE,
];
/** Discovery admin: everything including configure */
const DISC_ADMIN_PERMS = [
    ...DISC_MANAGER_PERMS,
    Permission.DISCOVERY_CONFIGURE,
];
/** Incident ops read permissions (Sprint 2.4.0) */
const INCOPS_READ_PERMS = [
    Permission.INCIDENT_PLAYBOOK_READ,
    Permission.INCIDENT_SLA_READ,
    Permission.INCIDENT_REGULATOR_EXPORT_READ,
    Permission.INCIDENT_VENDOR_COORD_READ,
    Permission.INCIDENT_METRICS_READ,
];
/** Incident ops manager: read + write playbooks + activate + vendor coord + regulator export */
const INCOPS_MANAGER_PERMS = [
    ...INCOPS_READ_PERMS,
    Permission.INCIDENT_PLAYBOOK_WRITE,
    Permission.INCIDENT_PLAYBOOK_ACTIVATE,
    Permission.INCIDENT_VENDOR_COORD_WRITE,
    Permission.INCIDENT_REGULATOR_EXPORT_EXECUTE,
    Permission.INCIDENT_METRICS_EXPORT,
];
/** Incident ops admin: everything including approve, SLA write, regulator submit */
const INCOPS_ADMIN_PERMS = [
    ...INCOPS_MANAGER_PERMS,
    Permission.INCIDENT_PLAYBOOK_APPROVE,
    Permission.INCIDENT_SLA_WRITE,
    Permission.INCIDENT_REGULATOR_EXPORT_SUBMIT,
];
/** Incident response maturity read permissions (Sprint 21.1.0) */
export const incidentBreachNotificationReadTier = [
    Permission.INCIDENT_BREACH_NOTIFICATION_READ,
    Permission.INCIDENT_SUPPLEMENT_READ,
    Permission.INCIDENT_REMEDIATION_READ,
    Permission.INCIDENT_AUTHORITY_REQUEST_READ,
    Permission.INCIDENT_DISCLOSURE_READ,
];
/** Incident response maturity write permissions (Sprint 21.1.0) */
export const incidentBreachNotificationWriteTier = [
    Permission.INCIDENT_BREACH_NOTIFICATION_WRITE,
    Permission.INCIDENT_SUPPLEMENT_WRITE,
    Permission.INCIDENT_REMEDIATION_WRITE,
    Permission.INCIDENT_AUTHORITY_REQUEST_WRITE,
    Permission.INCIDENT_DISCLOSURE_WRITE,
];
/** Incident ops maturity read permissions (Sprint 21.2.0) */
export const incidentOpsMaturityReadTier = [
    Permission.INCIDENT_OPS_PLAYBOOK_LINK_READ,
    Permission.INCIDENT_OPS_REGULATORY_SLA_READ,
    Permission.INCIDENT_OPS_COORDINATION_READ,
    Permission.INCIDENT_OPS_EVIDENCE_CHAIN_READ,
    Permission.INCIDENT_OPS_POST_REVIEW_READ,
];
/** Incident ops maturity write permissions (Sprint 21.2.0) */
export const incidentOpsMaturityWriteTier = [
    Permission.INCIDENT_OPS_PLAYBOOK_LINK_WRITE,
    Permission.INCIDENT_OPS_REGULATORY_SLA_WRITE,
    Permission.INCIDENT_OPS_COORDINATION_WRITE,
    Permission.INCIDENT_OPS_EVIDENCE_CHAIN_WRITE,
    Permission.INCIDENT_OPS_POST_REVIEW_WRITE,
];
/** Program governance read permissions (Sprint 2.5.0; expanded by 41.5.0 G5) */
const PGOV_READ_PERMS = [
    Permission.MATURITY_MODEL_READ,
    Permission.MATURITY_ASSESSMENT_READ,
    Permission.TREND_DASHBOARD_READ,
    Permission.EXECUTIVE_REPORT_READ,
    Permission.AUDIT_READINESS_READ,
    // Sprint 41.5.0 G5 — new read permissions split out from MATURITY_MODEL_READ
    Permission.GOV_DOC_READ,
    Permission.OBLIGATION_MAPPING_READ,
    Permission.MATURITY_READ,
    Permission.ACKNOWLEDGMENT_READ,
    Permission.CHANGE_LOG_READ,
    Permission.REVIEW_CADENCE_READ,
];
/**
 * Fix sprint #447, owner ruling OR6 (2026-10-04) — the two reads a recipient
 * of a governance document needs: reading the document (`gov_doc:read`) and
 * acknowledging it on Acknowledgment Tracking (`acknowledgment:read`, the
 * permission `AcknowledgmentService.recipientCandidates` selects recipients
 * by). Granted to `case_manager`, `support` and `contributor` so any staff
 * member can be a recipient; `seed-roles.ts` grants the seeded `case_manager`
 * the same pair. Read only — no write, issue, archive or manage grant follows.
 */
export const STAFF_ACKNOWLEDGMENT_READ_PERMS = [
    Permission.GOV_DOC_READ,
    Permission.ACKNOWLEDGMENT_READ,
];
/** Program governance manager: read + write + configure + export */
const PGOV_MANAGER_PERMS = [
    ...PGOV_READ_PERMS,
    Permission.MATURITY_MODEL_WRITE,
    Permission.MATURITY_ASSESSMENT_WRITE,
    Permission.TREND_METRIC_WRITE,
    Permission.EXECUTIVE_REPORT_WRITE,
    Permission.AUDIT_READINESS_WRITE,
    Permission.GOVERNANCE_EXPORT_EXECUTE,
    // Sprint 41.5.0 G5 — manager-tier write permissions
    Permission.GOV_DOC_WRITE,
    Permission.OBLIGATION_MAPPING_WRITE,
    Permission.MATURITY_WRITE,
    Permission.ACKNOWLEDGMENT_MANAGE,
    Permission.REVIEW_CADENCE_MANAGE,
];
/** Program governance admin: everything including approve + publish + issue + archive */
const PGOV_ADMIN_PERMS = [
    ...PGOV_MANAGER_PERMS,
    Permission.MATURITY_ASSESSMENT_APPROVE,
    Permission.EXECUTIVE_REPORT_PUBLISH,
    // Sprint 41.5.0 G5 — admin-tier governance document lifecycle controls
    Permission.GOV_DOC_ISSUE,
    Permission.GOV_DOC_ARCHIVE,
    Permission.MATURITY_ASSESS,
];
/** Access accountability read permissions (Sprint 2.6.0) */
const ACCT_READ_PERMS = [
    Permission.ACCESS_LOG_READ,
    Permission.ACCESS_JUSTIFICATION_READ,
    Permission.ACCESS_INVESTIGATION_READ,
    Permission.ACCESS_CONFIG_READ,
    Permission.ACCESS_ANCHORING_VERIFY,
    Permission.ACCESS_SESSION_READ,
];
/** Access accountability manager: read + review + write + export + close */
const ACCT_MANAGER_PERMS = [
    ...ACCT_READ_PERMS,
    Permission.ACCESS_LOG_EXPORT,
    Permission.ACCESS_JUSTIFICATION_REVIEW,
    Permission.ACCESS_INVESTIGATION_WRITE,
    Permission.ACCESS_INVESTIGATION_EXPORT,
];
/** Access accountability admin: everything including close, config write, anchoring configure, session revoke */
const ACCT_ADMIN_PERMS = [
    ...ACCT_MANAGER_PERMS,
    // Admin tier only: the sweep is the module's one destructive verb.
    Permission.ACCESS_LOG_RETENTION_SWEEP,
    Permission.ACCESS_INVESTIGATION_CLOSE,
    Permission.ACCESS_CONFIG_WRITE,
    Permission.ACCESS_ANCHORING_CONFIGURE,
    Permission.ACCESS_LOG_WRITE,
    // Sprint 63.29.0 (#483) — the evidence-package sign-off, split off `:log:export`.
    Permission.ACCESS_EVIDENCE_PACKAGE_SIGN_OFF,
    // Same tier as ACCESS_LOG_WRITE, for the same reason: both write into the
    // append-only accountability record.
    Permission.ACCESS_JUSTIFICATION_WRITE,
    Permission.ACCESS_SESSION_WRITE,
    Permission.ACCESS_SESSION_REVOKE,
];
/** Consent property read permissions (Sprint 2.7.0) */
const CPROP_READ_PERMS = [
    Permission.CONSENT_PROPERTY_READ,
    Permission.CONSENT_BINDING_READ,
    Permission.CONSENT_COMPARISON_READ,
    Permission.CONSENT_CHANGE_REQUEST_READ,
    Permission.CONSENT_SNAPSHOT_SCHEDULE_READ,
    Permission.CONSENT_GOLDEN_TEMPLATE_READ,
    Permission.CONSENT_PROPERTY_ANALYTICS_READ,
];
/** Consent property manager: read + write + execute comparisons + export */
const CPROP_MANAGER_PERMS = [
    ...CPROP_READ_PERMS,
    Permission.CONSENT_PROPERTY_WRITE,
    Permission.CONSENT_BINDING_WRITE,
    Permission.CONSENT_COMPARISON_EXECUTE,
    Permission.CONSENT_CHANGE_REQUEST_WRITE,
    Permission.CONSENT_CROSS_PROPERTY_EXPORT,
];
/** Consent property admin: everything including decommission, approve, deploy, schedule write, golden template write */
const CPROP_ADMIN_PERMS = [
    ...CPROP_MANAGER_PERMS,
    Permission.CONSENT_PROPERTY_DECOMMISSION,
    Permission.CONSENT_CHANGE_REQUEST_APPROVE,
    Permission.CONSENT_CHANGE_REQUEST_DEPLOY,
    Permission.CONSENT_SNAPSHOT_SCHEDULE_WRITE,
    Permission.CONSENT_GOLDEN_TEMPLATE_WRITE,
    Permission.CONSENT_SENSITIVE_DATA_REVIEW,
];
/** Localization read permissions (Sprint 2.8.0) */
const L10N_READ_PERMS = [
    Permission.MARKET_PACK_READ,
    Permission.LANGUAGE_PACK_READ,
    Permission.REGULATORY_FRAMEWORK_READ,
    Permission.PACK_SUBMISSION_READ,
];
/** Localization manager: read + install packs + write language packs + write frameworks + write mappings + submit + export */
const L10N_MANAGER_PERMS = [
    ...L10N_READ_PERMS,
    Permission.MARKET_PACK_WRITE,
    Permission.LANGUAGE_PACK_WRITE,
    Permission.REGULATORY_FRAMEWORK_WRITE,
    Permission.REGULATORY_MAPPING_WRITE,
    Permission.PACK_SUBMISSION_WRITE,
    Permission.LOCALIZATION_EXPORT_EXECUTE,
];
/** Localization admin: everything including manage packs, manage language packs, review submissions */
const L10N_ADMIN_PERMS = [
    ...L10N_MANAGER_PERMS,
    Permission.MARKET_PACK_MANAGE,
    Permission.LANGUAGE_PACK_MANAGE,
    Permission.HELP_CONTENT_PACK_MANAGE,
    Permission.PACK_SUBMISSION_REVIEW,
];
/** Partner marketplace read permissions (Sprint 2.9.0) */
const PM_READ_PERMS = [
    Permission.PARTNER_ORG_READ,
    Permission.PARTNER_TENANT_READ,
    Permission.SERVICE_TIER_READ,
    Permission.DELIVERABLE_READ,
    Permission.PARTNER_REPORT_READ,
    Permission.PARTNER_BRAND_READ,
    Permission.PARTNER_TICKET_READ,
    Permission.PARTNER_WORKFLOW_READ,
];
/** Partner marketplace manager: read + provision + write deliverables/tickets/workflows + generate reports + escalate */
const PM_MANAGER_PERMS = [
    ...PM_READ_PERMS,
    Permission.PARTNER_TENANT_PROVISION,
    Permission.DELIVERABLE_WRITE,
    Permission.PARTNER_REPORT_GENERATE,
    Permission.PARTNER_TICKET_WRITE,
    Permission.PARTNER_TICKET_ESCALATE,
    Permission.PARTNER_WORKFLOW_WRITE,
];
/** Partner marketplace admin: everything including org write, lifecycle, tier write, template write, brand write, audit, export */
const PM_ADMIN_PERMS = [
    ...PM_MANAGER_PERMS,
    Permission.PARTNER_ORG_WRITE,
    Permission.PARTNER_TENANT_LIFECYCLE,
    Permission.SERVICE_TIER_WRITE,
    Permission.DELIVERABLE_TEMPLATE_WRITE,
    Permission.PARTNER_BRAND_WRITE,
    Permission.PARTNER_WORKFLOW_TEMPLATE_WRITE,
    Permission.PARTNER_AUDIT_READ,
    Permission.PARTNER_EXPORT_EXECUTE,
];
/** Privacy scanner read permissions (Sprint 4.1.0) */
export const privacyScannerReadPermissions = [
    Permission.PRIVACY_SCANNER_READ,
];
/** Privacy scanner T2: read + export */
export const privacyScannerT2Permissions = [
    ...privacyScannerReadPermissions,
    Permission.PRIVACY_SCANNER_EXPORT,
];
/** Privacy scanner T1: full access */
export const privacyScannerT1Permissions = [
    ...privacyScannerT2Permissions,
    Permission.PRIVACY_SCANNER_WRITE,
];
/** Form Engine read permissions (Sprint 7.6.0) */
export const FORM_READ_PERMS = [
    Permission.FORMS_TEMPLATE_READ,
    Permission.FORMS_INSTANCE_READ,
];
/** Form Engine manager: read + write + export */
export const FORM_MANAGER_PERMS = [
    ...FORM_READ_PERMS,
    Permission.FORMS_INSTANCE_WRITE,
    Permission.FORMS_INSTANCE_EXPORT,
];
/** Form Engine admin: full access */
export const FORM_ADMIN_PERMS = [
    ...FORM_MANAGER_PERMS,
    Permission.FORMS_TEMPLATE_WRITE,
    Permission.FORMS_INSTANCE_APPROVE,
    Permission.FORMS_AUDIT_READ,
];
/** Group reader permissions (Sprint 11.5.0) */
export const GROUP_READ_PERMS = [
    Permission.GROUP_READ,
    Permission.GROUP_SHARED_DATA_READ,
    Permission.GROUP_AUDIT_READ,
    Permission.GROUP_DASHBOARD_READ,
    Permission.GROUP_REPORTING_READ,
    Permission.GROUP_ORG_SWITCH,
];
/** Group admin: full group access (Sprint 11.5.0) */
export const GROUP_ADMIN_PERMS = [
    ...GROUP_READ_PERMS,
    Permission.GROUP_MANAGE,
    Permission.GROUP_REPORTING_WRITE,
    Permission.GROUP_SHARED_DATA_MANAGE,
    Permission.GROUP_USERS_MANAGE,
    Permission.GROUP_SETTINGS_MANAGE,
];
/** Org management read permissions (Phase 9 extension) */
export const ORG_MANAGE_READ_PERMS = [
    Permission.ORG_MANAGE_READ,
    Permission.INTAKE_CONFIG_READ,
];
/** Org management admin: full org CRUD + intake config (Phase 9 extension) */
export const ORG_MANAGE_ADMIN_PERMS = [
    ...ORG_MANAGE_READ_PERMS,
    Permission.ORG_MANAGE_WRITE,
    Permission.ORG_MANAGE_DEACTIVATE,
    Permission.INTAKE_CONFIG_WRITE,
];
/** Onboarding read permissions (Sprint 1.9.0) */
const OBD_READ_PERMS = [
    Permission.ONBOARDING_WIZARD_READ,
    Permission.ONBOARDING_TOUR_READ,
    Permission.SAMPLE_DATA_READ,
    Permission.HELP_ARTICLE_READ,
];
/**
 * Onboarding manager: read + write wizard/tours + load sample data.
 *
 * Sprint 64.145.0 removed `HELP_ARTICLE_WRITE` from this tier and
 * `HELP_ARTICLE_DELETE` / `HELP_FAQ_EDIT` from the admin tier below.
 * `grc_help_articles` carries no tenant column and a unique index on `slug`
 * alone, so those three grants let an onboarding manager in one organisation
 * rewrite or remove the help article every other organisation reads. They are
 * `PLATFORM_ONLY_PERMISSIONS` now — held by no role, seeded or custom.
 */
const OBD_MANAGER_PERMS = [
    ...OBD_READ_PERMS,
    Permission.ONBOARDING_WIZARD_WRITE,
    Permission.ONBOARDING_TOUR_WRITE,
    Permission.SAMPLE_DATA_LOAD,
];
/** Onboarding admin: everything including clear sample data */
const OBD_ADMIN_PERMS = [...OBD_MANAGER_PERMS, Permission.SAMPLE_DATA_CLEAR];
/** Task Management read permissions (Sprint 17.0.0) */
const TM_READ_PERMS = [
    Permission.TM_TASK_READ,
    Permission.TM_COMMENT_READ,
    Permission.TM_CALENDAR_READ,
];
/** Task Management analyst/reviewer: read + update/complete own + comments + checklist */
const TM_ANALYST_PERMS = [
    ...TM_READ_PERMS,
    Permission.TM_TASK_UPDATE,
    Permission.TM_TASK_COMPLETE,
    Permission.TM_COMMENT_WRITE,
    Permission.TM_CHECKLIST_UPDATE,
];
/** Task Management manager: analyst + create + assign (no delete, no escalate) */
const TM_MANAGER_PERMS = [
    ...TM_ANALYST_PERMS,
    Permission.TM_TASK_CREATE,
    Permission.TM_TASK_ASSIGN,
    Permission.TM_EXPORT,
];
/** Task Management admin/DPO: all tm:* permissions */
const TM_ADMIN_PERMS = [
    ...TM_MANAGER_PERMS,
    Permission.TM_TASK_DELETE,
    Permission.TM_TASK_ESCALATE,
];
/** Task Management auditor: read-only + export */
const TM_AUDITOR_PERMS = [...TM_READ_PERMS, Permission.TM_EXPORT];
/** Workflow Engine read permissions (Sprint 17.1.0) */
const WF_READ_PERMS = [
    Permission.WF_DEFINITION_READ,
    Permission.WF_INSTANCE_READ,
    Permission.WF_TEMPLATE_READ,
];
/** Workflow Engine manager: read + transition */
const WF_MANAGER_PERMS = [
    ...WF_READ_PERMS,
    Permission.WF_INSTANCE_TRANSITION,
];
/** Workflow Engine admin: all wf:* permissions */
const WF_ADMIN_PERMS = [
    ...WF_MANAGER_PERMS,
    Permission.WF_DEFINITION_WRITE,
    Permission.WF_DEFINITION_DELETE,
    Permission.WF_DEFINITION_ACTIVATE,
    Permission.WF_TEMPLATE_WRITE,
];
/** Notification read permissions (Sprint 17.3.0) */
const NOTIF_READ_PERMS = [Permission.PLATFORM_NOTIFICATIONS_READ];
/** Notification analyst/reviewer: read + manage (own) + preferences */
const NOTIF_ANALYST_PERMS = [
    ...NOTIF_READ_PERMS,
    Permission.PLATFORM_NOTIFICATIONS_MANAGE,
    Permission.PLATFORM_NOTIFICATIONS_PREFERENCES,
];
/** Notification admin/DPO: all notification permissions */
const NOTIF_ADMIN_PERMS = [
    ...NOTIF_ANALYST_PERMS,
    Permission.PLATFORM_NOTIFICATIONS_ADMIN,
];
/** UI Assist permissions (Sprint 17.8.0) — system_admin only */
const UI_ASSIST_ADMIN_PERMS = [
    Permission.UI_ASSIST_VIEW,
    Permission.UI_ASSIST_EXECUTE,
];
/** Agent Lobby permissions — system_admin only (UI Assist extension) */
const AGENT_LOBBY_ADMIN_PERMS = [
    Permission.AGENT_LOBBY_VIEW,
    Permission.AGENT_LOBBY_MANAGE,
    Permission.AGENT_LOBBY_DECIDE,
];
// Sprint 18.0.0: License & Package Management permission groups
const LIC_READ_PERMS = [Permission.LICENSE_READ, Permission.PACKAGE_READ];
const LIC_ADMIN_PERMS = [
    ...LIC_READ_PERMS,
    Permission.LICENSE_ACTIVATE,
    Permission.LICENSE_DEACTIVATE,
    Permission.PACKAGE_UPGRADE,
    Permission.PACKAGE_DOWNGRADE,
];
// ── Sprint 47.5.0 — Forseti permission helper arrays ─────────────────
// All Forseti permissions are available on all tiers (Light/Standard/
// Enterprise) per family plan §9.1 Q2. Helper arrays group by role
// archetype rather than by tier.
/** Read-only Forseti permissions for VIEWER + AUDITOR roles. */
export const FORSETI_READ_PERMS = [
    Permission.AI_ADVISOR_CHAT, // Read implicit — chat permission also covers reading own chats
    Permission.AI_PROJECTS_READ,
    Permission.AI_WORKFLOWS_READ,
    Permission.AI_WORKFLOWS_SYSTEM_PACK_READ,
    Permission.AI_TABULAR_REVIEWS_READ,
    Permission.AI_PROVIDER_VIEW_USAGE,
    Permission.AI_PROVIDER_VIEW_AUDIT,
    // Sprint 48.0.0c — memory browse is part of the read baseline so all
    // Forseti users can see what context the org has accumulated.
    Permission.AI_ADVISOR_MEMORY_READ,
];
/** Standard write permissions for CONTRIBUTOR role (no admin/delete/configure). */
export const FORSETI_WRITE_PERMS = [
    ...FORSETI_READ_PERMS,
    Permission.AI_ADVISOR_CHAT_SHARE,
    Permission.AI_ADVISOR_CHAT_EXPORT,
    // Sprint 48.0.0c — contributors can edit / delete their own memories +
    // those auto-inferred from their chats. Promote-to-curated stays admin.
    Permission.AI_ADVISOR_MEMORY_WRITE,
    Permission.AI_PROJECTS_WRITE,
    Permission.AI_PROJECTS_SHARE,
    Permission.AI_WORKFLOWS_WRITE,
    Permission.AI_WORKFLOWS_RUN,
    Permission.AI_WORKFLOWS_SHARE,
    Permission.AI_WORKFLOWS_SYSTEM_PACK_HIDE,
    Permission.AI_TABULAR_REVIEWS_WRITE,
    Permission.AI_TABULAR_REVIEWS_RUN,
    Permission.AI_TABULAR_REVIEWS_SHARE,
    Permission.AI_DOCUMENT_REVIEW_PROPOSE_EDIT,
    Permission.AI_DOCUMENT_REVIEW_ACCEPT_EDIT,
    Permission.AI_DOCUMENT_REVIEW_REJECT_EDIT,
    Permission.AI_DOCUMENT_REVIEW_VERSION_PROMOTE,
];
/** Manager-level Forseti permissions (CASE_MANAGER + COMPLIANCE_OWNER). */
export const FORSETI_MANAGER_PERMS = [
    ...FORSETI_WRITE_PERMS,
    Permission.AI_PROJECTS_DELETE,
    Permission.AI_WORKFLOWS_DELETE,
    Permission.AI_TABULAR_REVIEWS_DELETE,
];
/** Full Forseti permissions including provider configuration + quota
 *  override (PLATFORM_ADMIN only). */
export const FORSETI_ADMIN_PERMS = [
    ...FORSETI_MANAGER_PERMS,
    Permission.AI_ADVISOR_ADMIN,
    // Sprint 48.0.0c — promote-to-curated is admin-only (curating locks
    // a memory against retention GC).
    Permission.AI_ADVISOR_MEMORY_CURATE,
    Permission.AI_PROJECTS_ADMIN,
    Permission.AI_WORKFLOWS_ADMIN,
    Permission.AI_TABULAR_REVIEWS_ADMIN,
    Permission.AI_DOCUMENT_REVIEW_ADMIN,
    Permission.AI_PROVIDER_CONFIGURE,
    Permission.AI_PROVIDER_QUOTA_OVERRIDE,
    // Sprint 48.5.x — self-service billing (buy + redeem token bundles) is
    // admin-only, mirroring quota override: it touches the org's paid capacity.
    Permission.AI_PROVIDER_BILLING,
    // Sprint 47.9.7 — MCP key management is admin-only, mirroring the
    // BYOK provider-config permission. The :list permission is also
    // admin-only because the inventory reveals key labels + creation
    // timestamps that aid lateral-movement reconnaissance.
    Permission.AI_PROVIDER_MCP_CREATE,
    Permission.AI_PROVIDER_MCP_REVOKE,
    Permission.AI_PROVIDER_MCP_LIST,
    // Sprint 64.172.0 — the pending-write queue's two grants sit beside the key
    // permissions they were split out of, so this array keeps describing the
    // Forseti admin tier accurately.
    //
    // Declarative only, and said plainly because the alternative is this
    // sprint's own defect in miniature: `FORSETI_ADMIN_PERMS` has NO runtime
    // consumer — `grep` finds only this declaration — as the note above
    // `ROLE_PERMISSIONS` already records for the whole family of tier arrays.
    // Adding a permission here grants nobody anything. The migration decision is
    // executable in `seed-roles.ts`'s `allPermissions` and in the boot-wired
    // custom-role migration; it is NOT executable here, and a comment claiming
    // otherwise would be a declaration asserting a mechanism that does not run.
    Permission.AI_PROVIDER_MCP_PENDING_LIST,
    Permission.AI_PROVIDER_MCP_PENDING_DECIDE,
    // Sprint 63.23.0 — OAuth clients and grants. Declarative, as above; the
    // executable grant is `seed-roles.ts`'s `allPermissions`.
    Permission.AI_PROVIDER_MCP_OAUTH_CLIENT_LIST,
    Permission.AI_PROVIDER_MCP_OAUTH_CLIENT_CREATE,
    Permission.AI_PROVIDER_MCP_OAUTH_CLIENT_REVOKE,
    Permission.AI_PROVIDER_MCP_GRANT_LIST,
    Permission.AI_PROVIDER_MCP_GRANT_REVOKE,
];
/**
 * Sprint 64.0.0 — module tier-grouping vocabulary.
 *
 * These per-module admin/auditor tier arrays used to feed the hand-authored
 * `ROLE_PERMISSIONS` entries for platform_admin / compliance_owner /
 * case_manager / auditor. Those entries are now DERIVED from the seed's
 * selection logic (see the reconciliation note above `ROLE_PERMISSIONS`),
 * so nothing at runtime consumes the admin tiers any more — but they remain
 * the declared tier model the 30 per-module permission-matrix documents
 * describe, so they are kept importable here rather than deleted piecemeal.
 * Their fate (delete, or become the source for custom-role templates) is
 * Sprint 64.31.0's registry decision (idea-catalog #84).
 */
export const MODULE_TIER_PERMS = {
    DM_ADMIN_PERMS,
    GOV_ADMIN_PERMS,
    CONSENT_ADMIN_PERMS,
    ASSESS_ADMIN_PERMS,
    CLASS_ADMIN_PERMS,
    MON_ADMIN_PERMS,
    INC_ADMIN_PERMS,
    CONN_ADMIN_PERMS,
    VR_ADMIN_PERMS,
    DELORCH_ADMIN_PERMS,
    DISC_ADMIN_PERMS,
    INCOPS_ADMIN_PERMS,
    PGOV_ADMIN_PERMS,
    ACCT_ADMIN_PERMS,
    CPROP_ADMIN_PERMS,
    L10N_ADMIN_PERMS,
    PM_ADMIN_PERMS,
    OBD_ADMIN_PERMS,
    TM_ADMIN_PERMS,
    TM_AUDITOR_PERMS,
    WF_ADMIN_PERMS,
    NOTIF_ADMIN_PERMS,
    UI_ASSIST_ADMIN_PERMS,
    AGENT_LOBBY_ADMIN_PERMS,
    LIC_ADMIN_PERMS,
    // Sprint 65.6.0 — DT-01 training.
    TRAIN_ADMIN_PERMS,
};
/**
 * Default permissions per role.
 * See: docs/product/phases/phase-1/sprint-1.2.0.md - RBAC
 */
/**
 * Permissions no system role may hold, however a role is derived.
 *
 * Sprint 62.7.0. `payments_evidence:unmask` reads a restricted handle back in
 * full. UC-VN-34 failure mode 8: "positive authorization must be required, not
 * merely role membership" — so it arrives with no role at all, including the
 * two that otherwise hold everything. A tenant that needs it creates a role
 * carrying it and assigns that role to a named person, which is a deliberate,
 * auditable act by somebody who had to think about it.
 *
 * The consequence is intended: out of the box **nobody** in a new organisation
 * can see a restricted handle.
 */
/**
 * Sprint 64.145.0 — permissions NO tenant principal may hold. Idea-catalog #198.
 *
 * ## What this is, and how it differs from `CUSTOM_ONLY_PERMISSIONS`
 *
 * The two sets look alike and are opposites:
 *
 * | | `CUSTOM_ONLY_PERMISSIONS` | `PLATFORM_ONLY_PERMISSIONS` |
 * |---|---|---|
 * | Seeded system role may hold it | no | no |
 * | Custom role may hold it | **yes — that is the point** | **no — that is the point** |
 * | Why | positive authorization must be a deliberate, named act | the write changes state every OTHER tenant reads |
 *
 * `payments_evidence:unmask` arrives with no role so that a tenant who needs it
 * must create one on purpose. These four arrive with no role because **there is
 * no tenant answer**: the row being written is not the tenant's.
 *
 * ## The measurement, and why "platform-admin only" was not expressible
 *
 * The ruling taken for this sprint reads *"platform-admin only; tenant roles
 * lose write"*. On this codebase those are the same clause, and the reason has
 * to be stated here or the next reader will try to write the narrower version:
 *
 * **`platform_admin` is itself a per-tenant role.** `seedRoles(db, orgId, …)`
 * upserts `grc_roles` on `{ org_id, name }`, so every organisation gets its
 * own. There is no principal in this system that is not a tenant principal —
 * `pack-install-engine.ts` already says so in passing, in a comment about a
 * delete that cleared another tenant's form catalogue: *"an L10N_ADMIN tenant
 * role, not a platform superadmin"*.
 *
 * So gating these on `platform_admin` would narrow the population from two T1
 * roles to one and fix nothing: tenant A's platform_admin would still rewrite
 * the help article tenant B reads. The only reading of the ruling that changes
 * the fact it was taken about is *no tenant role holds it*.
 *
 * ## What still writes these collections
 *
 * The platform's own paths, which carry no session at all: pack install and
 * uninstall (`pack-installer.ts`, `help-content-pack-service.ts`,
 * `pack-install-engine.ts`) and the boot seed. Those are ruled separately in
 * `scripts/shared-state-write-dispositions.ts`; two of them are
 * `deliberately-shared` behind an "is any other org still using this?" fence.
 *
 * ## Enforcement — four places, because three of them are not enough
 *
 * 1. Excluded from `allPermissions` in `seed-roles.ts` — no seeded role takes it.
 * 2. Excluded from `ROLE_PERMISSIONS` below — the declared matrix agrees.
 * 3. **Refused by `RoleService.create`/`update`** — this is the half
 *    `CUSTOM_ONLY_PERMISSIONS` deliberately does not have. Without it a tenant
 *    admin re-grants the permission to a custom role in one API call and every
 *    other layer here is decoration.
 * 4. Stripped from existing custom roles by a boot-wired migration, because a
 *    role that already holds one keeps holding it otherwise.
 *
 * Asserted by `permissions-contract.test.ts`. Like `CUSTOM_ONLY_PERMISSIONS`,
 * it must stay deliberately hard to add to.
 */
export const PLATFORM_ONLY_PERMISSIONS = new Set([
    // `grc_help_articles` — no tenant column, unique index on `slug` alone, so
    // two tenants cannot even hold the same article. `HelpService` writes its
    // audit event with the literal actor scope `'global'`: the author knew the
    // collection was shared and recorded it, and no reader asked what that
    // implied about who may write. Content arrives by help-content pack.
    Permission.HELP_ARTICLE_WRITE,
    Permission.HELP_ARTICLE_DELETE,
    // The FAQ editor is the same collection's subdocuments and the same audience.
    Permission.HELP_FAQ_EDIT,
    // `grc_connector_definitions` — `config_schema` and `capabilities` are the
    // shape every tenant's connector instances are built against.
    Permission.CONNECTOR_DEFINITION_WRITE,
]);
export const CUSTOM_ONLY_PERMISSIONS = new Set([
    Permission.PAYMENTS_EVIDENCE_UNMASK,
    // Sprint 62.24.0 (gate E12.5 pass 1, F1) — the same act on a consent-evidence
    // entry's custody reference. Same reasoning, same consequence: out of the
    // box nobody in a new organisation can read the handle in clear.
    Permission.CONSENT_EVIDENCE_UNMASK,
    // Sprint 62.28.0 — the same act on a marketing consent entry's custody
    // reference. Third repeat of the 62.7.0 precedent, unvaried: out of the box
    // nobody in a new organisation can read the handle in clear.
    Permission.MARKETING_CONSENT_UNMASK,
    // Sprint 62.46.0 — the AML programme register's six tokens. The first entries
    // that are not an unmask: the case register's EXISTENCE must not reach the
    // wrong reader (tipping-off), so reading, writing, determining and approving
    // a case — and the two gates beside it — arrive with no role. A tenant that
    // runs the programme creates a role carrying them and assigns it to a named
    // person. Out of the box nobody in a new organisation can see a case.
    Permission.AML_CASE_READ,
    Permission.AML_CASE_WRITE,
    Permission.AML_CASE_DETERMINE,
    Permission.AML_CASE_APPROVE,
    Permission.AML_INDICATOR_CONFIG_APPROVE,
    Permission.AML_LAUNCH_GATE_APPROVE,
    // Sprint 62.52.0 (owner answer OQ-2 (a)) — reading every cybersecurity
    // assurance index period whether or not the holder is named on it. The
    // results are confidential (the Decision makes them so); a tenant names the
    // people who may read them all by a custom role. Out of the box a period is
    // read only by its members.
    Permission.CYBERSECURITY_ASSURANCE_INDEX_RESTRICTED_READ,
]);
/**
 * Sprint 63.29.0, owner ruling R6 (2026-10-18, gate E12.5 pass 2) — two grants
 * that never travel by delegation, beside the platform-only and custom-only
 * sets. Each lets its holder hand out OTHER people's access: the SSO
 * configuration names the role every federated newcomer receives, and the
 * group-user grant assigns group roles across organisations. Lent, either turns
 * a bounded window into a permanent grant for somebody else.
 *
 * Fix sprint #610 (Finding 6) — one list, here. The server
 * (`delegations/delegated-grants.ts`) and the delegation form
 * (`utils/grantable-permissions.ts`) each held their own copy, and the
 * frontend's was tested for one of its two members.
 */
export const DELEGATION_EXCLUDED_PERMISSIONS = new Set([
    Permission.PLATFORM_SSO_WRITE,
    Permission.GROUP_USERS_MANAGE,
    // Sprint 62.49.0 (security decision, coordinator) — the food traceability
    // second-person approval and the food grant. `:approve` applies a correction
    // to the trace ledger and puts a food grant in force (a delegation of
    // `:read` / `:write` from the approver, follow-up 2026-10-28); `:grant`
    // requests and removes those grants. The tenant's segregation rule pairs the two (AC24):
    // lent, either lets a delegatee stand as the "second person" of a two-person
    // rule, or hand food access to somebody else, beyond the bounded window.
    Permission.FOOD_TRACEABILITY_APPROVE,
    Permission.FOOD_TRACEABILITY_GRANT,
]);
/**
 * Owner ruling 2026-10-22 (OR-9 confirmed narrowly, fix batch B10) — the
 * licence, package and module-configuration WRITE grants, which only the
 * seeded `platform_admin` role holds.
 *
 * The licence, the package tier and the module configuration are
 * installation-global (OR-9, 2026-10-21: every route addresses
 * `PLATFORM_ORG_ID`). Until this ruling both T1 roles took them through
 * `allPermissions`. The owner's answer to "platform administrators" was
 * `platform_admin` alone, so the seeded `compliance_owner` no longer holds
 * them: `seed-roles.ts` filters them out of that role and the declared
 * `ROLE_PERMISSIONS` below agrees (`role-matrix-parity.test.ts`).
 *
 * Unlike {@link PLATFORM_ONLY_PERMISSIONS} these ARE held by a seeded role
 * (`platform_admin`). Existing organisations lose the grants on
 * `compliance_owner` through the boot-wired migration
 * `fix-or9-compliance-owner-platform-writes.ts`; `syncSystemPermissions` is a
 * union and never removes.
 *
 * fix-sprint-20261023-2118 — "platform_admin alone" is held on the two other
 * paths a grant travels by:
 *
 * - **Delegation.** {@link isNonDelegablePermission} names these, so a
 *   platform administrator cannot lend one (refused at creation, stripped on
 *   every read of a record written before the rule) — the shape owner ruling R6
 *   closed for `platform:sso:write`.
 * - **Custom roles.** `RoleService` refuses one ADDED to any role (400
 *   `PLATFORM_ADMIN_ROLE_ONLY_PERMISSION`), and the role editor does not offer
 *   them. A custom role that carried one before the rule keeps it (nothing is
 *   stripped from stored roles) and can still be edited without it being
 *   judged, as `rejectNotHeld` judges only additions.
 */
export const PLATFORM_ADMIN_ROLE_ONLY_PERMISSIONS = new Set([
    Permission.LICENSE_ACTIVATE,
    Permission.LICENSE_DEACTIVATE,
    Permission.PACKAGE_UPGRADE,
    Permission.PACKAGE_DOWNGRADE,
    Permission.PLATFORM_MODULE_CONFIG_WRITE,
]);
/**
 * Whether no delegation can carry `permission`: platform-only, custom-only, one
 * of {@link DELEGATION_EXCLUDED_PERMISSIONS}, or one of
 * {@link PLATFORM_ADMIN_ROLE_ONLY_PERMISSIONS} (owner ruling OR-9: installation-
 * global writes are platform_admin's alone, so they are not lent either;
 * fix-sprint-20261023-2118). The server strips these at creation and again on
 * every read; the form never offers them.
 */
export function isNonDelegablePermission(permission) {
    return (PLATFORM_ONLY_PERMISSIONS.has(permission) ||
        CUSTOM_ONLY_PERMISSIONS.has(permission) ||
        DELEGATION_EXCLUDED_PERMISSIONS.has(permission) ||
        PLATFORM_ADMIN_ROLE_ONLY_PERMISSIONS.has(permission));
}
/**
 * Sprint 64.35.0 — the auditor's suffix rule, in ONE place.
 *
 * This rule had **three** independent spellings, and all three had drifted:
 *
 *   1. `seed-roles.ts`'s `reachesAuditorBySuffix` — what production actually
 *      seeds.
 *   2. `permissions-contract.test.ts`'s `AUDITOR_SUFFIX_PERMS` — which omitted
 *      `audit:dashboard:read`, so the invariant it asserted was subtly not the
 *      one the platform implements.
 *   3. The inline filter that used to sit right here, building the DECLARED
 *      auditor matrix.
 *
 * Two of them agreeing is not a contract; it is a coincidence that survives
 * until someone edits one. Sprint 64.35.0 made that concrete: adding
 * `pdpl_evidence_pack:read` — a permission that ends in `:read` and must NOT
 * reach the auditor, because it gates retrieval of the regulator-facing PDPL
 * filing packs — has to be excluded in every copy or the copies disagree about
 * who can download a filing pack.
 *
 * So the rule lives here, in `shared`, which the backend can import and the
 * declared matrix can use directly. One rule, three callers.
 *
 * The exclusion is expressed against the CONSTANT rather than the string, so a
 * rename cannot silently re-open it.
 */
export function reachesAuditorBySuffix(p) {
    // Sprint 64.35.0 — the one `:read` the auditor must not reach. Named `:read`
    // to match its `:send` sibling; excluded here because the suffix rule would
    // otherwise hand the auditor exactly what the sprint exists to withhold.
    if (p === Permission.PDPL_EVIDENCE_PACK_READ)
        return false;
    // Sprint 62.46.0 — the second. `aml_case:read` reads the suspicious-activity
    // case register, whose existence must not reach a reader nobody named
    // (tipping-off). It is custom-only; the suffix rule would otherwise hand it to
    // the auditor role through `ROLE_PERMISSIONS[AUDITOR]`, which filters
    // `Object.values(Permission)` rather than `allPermissions`.
    if (p === Permission.AML_CASE_READ)
        return false;
    return (p.endsWith(':read') ||
        p.endsWith(':search') ||
        p.endsWith(':export') ||
        // Sprint 44.3.1 Epic C — the legacy *_export:execute perms, reached by a
        // suffix-drift workaround chosen over breaking renames.
        p.endsWith('_export:execute') ||
        p === Permission.DM_EXPORT_CSV ||
        p === Permission.DM_EXPORT_PDF ||
        p === Permission.AUDIT_DASHBOARD_READ);
}
export const ROLE_PERMISSIONS = {
    // ─────────────────────────────────────────────────────────────────
    // Sprint 64.0.0 — declared-vs-seeded reconciliation (idea-catalog #46).
    //
    // Until this sprint the four entries below were hand-authored lists that
    // had drifted from what `src/backend/src/db/seed-roles.ts` actually
    // grants (494 diverging permission rows at kickoff, measured). The seed
    // is the enforced source of truth: ROLE_PERMISSIONS has no runtime
    // consumer, and the FROZEN 44.3.0 permissions-final-spec points auditors
    // at the seed-filter code. These entries are now DERIVED with the same
    // selection logic the seed uses, and
    // `src/backend/src/db/__tests__/role-matrix-parity.test.ts` asserts
    // exact per-role set equality — edit either side alone and it fails
    // naming the role and the permissions.
    //
    // approver / partner_external_counsel / processor_liaison below are
    // hand-authored mirrors of the seed (Sprint 63.17.0): the three roles the
    // seed always created and this registry omitted. The three roles this
    // registry declared and the seed never created (support, contributor,
    // viewer) are custom-role templates now.
    // ─────────────────────────────────────────────────────────────────
    // T1 roles hold every defined permission, by definition ("Full access") —
    // with exactly one exception, and the exception is the point.
    //
    // Sprint 62.7.0 × 64.0.0 interaction, found at the merge. 64.0.0 replaced the
    // hand-authored T1 lists with `Object.values(Permission)`, which is right for
    // every permission that existed when it was written. 62.7.0 then added
    // `payments_evidence:unmask`, whose entire design is that it is held by **no
    // system role**: UC-VN-34 failure mode 8 requires "positive authorization,
    // not merely role membership", and a permission that arrives with a role IS
    // role membership. `Object.values` would have handed it to `platform_admin`
    // and `compliance_owner` silently — neither sprint wrong alone, the pair
    // wrong together.
    //
    // Excluded here rather than by dropping the derivation, because the
    // derivation is the better design: it cannot go stale as permissions are
    // added. `CUSTOM_ONLY_PERMISSIONS` is the one list a T1 role does not take,
    // it is asserted by `permissions-contract.test.ts`, and it must stay
    // deliberately hard to add to.
    //
    // Sprint 64.145.0 adds the second exclusion, for the same reason and by the
    // same mechanism. `PLATFORM_ONLY_PERMISSIONS` is held by no role at all —
    // including these two, and including `platform_admin`, which is itself a
    // per-tenant role (see that set's docblock). Excluding it here keeps the
    // DECLARED matrix agreeing with what `seed-roles.ts` actually seeds; the two
    // disagreeing is the drift Sprint 64.0.0 removed and must not reintroduce.
    [SystemRole.PLATFORM_ADMIN]: Object.values(Permission).filter((p) => !CUSTOM_ONLY_PERMISSIONS.has(p) && !PLATFORM_ONLY_PERMISSIONS.has(p)),
    // Owner ruling 2026-10-22 (OR-9) — the third exclusion, on this role alone:
    // the licence, package and module-configuration writes are `platform_admin`'s.
    [SystemRole.COMPLIANCE_OWNER]: Object.values(Permission).filter((p) => !CUSTOM_ONLY_PERMISSIONS.has(p) &&
        !PLATFORM_ONLY_PERMISSIONS.has(p) &&
        !PLATFORM_ADMIN_ROLE_ONLY_PERMISSIONS.has(p)),
    // T2 — daily case operations and DSR processing. Mirrors the seed's
    // prefix filter: case/task/tm/template/dsr/sla/data_subject namespaces
    // (minus the DPO-governed case:template_admin) plus the audit reads and
    // the five explicit platform-files / export / scanner grants.
    [SystemRole.CASE_MANAGER]: [
        ...Object.values(Permission).filter((p) => p !== Permission.CASE_TEMPLATE_ADMIN &&
            (p.startsWith('case:') ||
                p.startsWith('task:') ||
                p.startsWith('tm:') ||
                p.startsWith('template:') ||
                p.startsWith('dsr:') ||
                p.startsWith('sla:') ||
                p.startsWith('data_subject:') ||
                p === Permission.AUDIT_EVENTS_READ ||
                p === Permission.AUDIT_DASHBOARD_READ)),
        Permission.PLATFORM_FILES_BROWSE,
        Permission.PLATFORM_FILES_DETAIL_READ,
        Permission.PLATFORM_FILES_METRICS_READ,
        Permission.EXPORT_BULK,
        Permission.PRIVACY_SCANNER_READ,
        // Sprint 63.14.0 (OD12) — every internal role may resolve a user id to a name.
        Permission.PLATFORM_USERS_LOOKUP,
        // Fix sprint #447 (owner ruling OR6) — any staff member can be asked to
        // acknowledge a governance document, so every internal role reads the
        // documents and acknowledges them. Read only; mirrored in seed-roles.ts.
        ...STAFF_ACKNOWLEDGMENT_READ_PERMS,
    ],
    // T2 — read-only audit access. Mirrors the seed's suffix filter
    // (:read / :search / :export / _export:execute + the two dm export
    // formats) plus the explicit grants, including the Sprint 64.0.0
    // additions (attachments:download / attachments:upload /
    // case:attach:download / ai_provider views / anchoring verify /
    // deletion_report:generate / gov:export:pdf) — each decided
    // individually in sprint-64.0.0-workfile.md → AC6.
    [SystemRole.AUDITOR]: [
        ...Object.values(Permission).filter(reachesAuditorBySuffix),
        Permission.PLATFORM_FILES_BROWSE,
        Permission.ATTACHMENTS_DOWNLOAD,
        // Sprint 64.32.0 — `ATTACHMENTS_UPLOAD` was here from Sprint 43.5.7, which
        // added it to fix the Governance Audit Trail export 403'ing on the
        // client-csv-export pass-through. Sprint 64.0.0's gate E12.5 recorded what
        // it cost (compliance F3): that permission is also the key to
        // `POST /attachments`, which accepts any owner type in the union. The
        // capability the auditor was granted for is persistence, and it now has a
        // permission of its own.
        Permission.ATTACHMENTS_CLIENT_EXPORT_PERSIST,
        Permission.CASE_ATTACH_DOWNLOAD,
        Permission.AI_PROVIDER_VIEW_AUDIT,
        Permission.AI_PROVIDER_VIEW_USAGE,
        Permission.ACCESS_ANCHORING_VERIFY,
        Permission.DELETION_REPORT_GENERATE,
        Permission.GOV_EXPORT_PDF,
        // Sprint 63.14.0 (OD12) — every internal role may resolve a user id to a name.
        Permission.PLATFORM_USERS_LOOKUP,
        // Sprint 63.17.0 (Q6) — the three actions that were gated on a `:read` grant;
        // mirrored in seed-roles.ts.
        Permission.DASHBOARD_PREFERENCES_WRITE,
        Permission.IMMUTABLE_AUDIT_TRAILS_VERIFY,
        Permission.REGULATORY_ARTIFACT_REQUEST_ACCESS,
        // Sprint 63.25.0 (§3.6) — the internal auditor verifies audit evidence
        // packs it did not assemble; mirrored in seed-roles.ts.
        Permission.AUDIT_EVIDENCE_PACKS_REVIEW,
        // Sprint 63.29.0 (#483, owner ruling 2026-10-18) — the auditor signs off an
        // access evidence package's stage (the `internal_audit` stage is its own);
        // closing one stays with `:log:write`. Mirrored in seed-roles.ts.
        Permission.ACCESS_EVIDENCE_PACKAGE_SIGN_OFF,
        // Owner decision O7 (2026-10-26) — the internal auditor (P-VN-10) may approve
        // a customs dossier, determination or examination conclusion it did not
        // record; the service refuses the recorder. Mirrored in seed-roles.ts.
        Permission.CUSTOMS_TAX_ADMINISTRATION_REVIEW,
    ],
    // T3 — approval workflow gates. Mirrors seed-roles.ts exactly.
    [SystemRole.APPROVER]: [
        Permission.CASE_READ,
        Permission.TASK_READ,
        Permission.AUDIT_EVENTS_READ,
        Permission.AUDIT_DASHBOARD_READ,
        Permission.GOV_APPROVAL_READ,
        Permission.GOV_APPROVAL_DECIDE,
        Permission.CASE_CLOSURE_APPROVE,
        // Sprint 63.14.0 (OD12) — every internal role may resolve a user id to a name.
        Permission.PLATFORM_USERS_LOOKUP,
    ],
    // T3 — case-scoped read and comment for an external adviser. Deliberately NO
    // `platform:users:lookup` (Sprint 63.14.0, OD12c); mirrors seed-roles.ts.
    [SystemRole.PARTNER_EXTERNAL_COUNSEL]: [
        Permission.CASE_READ,
        Permission.CASE_COMMENT,
        Permission.CASE_ATTACH,
        Permission.CASE_ATTACH_DOWNLOAD,
    ],
    // T3 — DSR data collection and deletion fulfilment for a processor's liaison.
    // Deliberately NO `platform:users:lookup` (OD12c); mirrors seed-roles.ts.
    [SystemRole.PROCESSOR_LIAISON]: [
        Permission.DSR_DATA_COLLECTION_REQUEST,
        Permission.DSR_DELETION_REQUEST,
        Permission.DSR_DELETION_CONFIRM,
        Permission.CASE_READ,
        Permission.TASK_READ,
        Permission.TASK_UPDATE,
        Permission.TASK_COMPLETE,
        Permission.PRIVACY_SCANNER_READ,
    ],
};
//# sourceMappingURL=permissions.js.map